// KYT — wallet screening, read into the Books.
//
// The screening itself lives in the onboarding worker (Arkham attribution +
// monitoring, GL Vision scoring, OFAC lists, the Telegram ops-group commands).
// This screen is the Books' window onto it: what came in and whether anything
// is wrong with it, what the wallets hold, and whether the screening itself is
// working — and, beside it, the wallets the Books themselves register, so a
// treasury wallet that nobody is watching is visible as exactly that.
//
// READ-ONLY BY CONTRACT (onboarding-agent/docs/kyt-api-contract.md): adding
// or removing a watched wallet stays a Telegram ops-group command behind the
// operator-id allowlist. The one paid action here is reading a wallet's
// transfers from Arkham — two credits for every transfer returned, cached ten
// minutes by the BFF — so it is a button that says its price, never a load.
//
// 5 Oct 2026 — THREE VIEWS AND A TRACE (the operator: "build a proper screens
// for effective KYT Tracing, not too complicated, not overwhelming"). Until
// then one page asked everything at once, top to bottom, and a payment's
// particulars unfolded inside its table row. Now:
//   Payments  what came in, each payment's level and sanctions result, and the
//             answer before a deposit to an exchange — the view the screen
//             opens on;
//   Wallets   this month's desk wallets — their own score, what an exchange
//             sees behind a deposit from them, what they received by level —
//             and the Books' register by class;
//   System    is the screening working: the four tiles, who is answering, the
//             rules in force, flagged screenings.
// A red line — a payment alert, the brain down, nothing checked last night —
// stands above the views, whichever is open, and a view's trouble is said on
// its tab. TRACE opens beside the list (the whole screen on a phone) and
// answers one question, where did this money come from: the verdict, the
// sanctions result, the funds by kind of source in dollars of that payment,
// who paid, and what was checked. It is free — it shows what the check stored
// with the payment (GET /api/kyt/incoming, the worker's `trace`).
//
// 22 Oct 2026 — THE DESIGN-SYSTEM SWEEP (docs/DESIGN-SYSTEM.md; the render review of 135 states, at phone and laptop):
//   · A read that failed is ONE red line — what failed, why, the way again (readFailure, StateLine, Slab) — and never the look
//     of an empty screen; both reads down is one banner. A section that could not be said says "Not read".
//   · A wallet whose payments are not being read (paymentsUnread) is never "fully checked", never "no alerts", never green,
//     never answered "No payments" (drawnAnswer) — whatever the worker's answer for it says; the Wallets tab counts it.
//   · The answers before a deposit are for the window the RESPONSE is of: while another window is read (the query keeps the
//     last response on screen) a CALM conclusion of the old days ("No payments", the green answer) waits — "Reading the last
//     7 days…" stands in its place; an adverse answer and every not-fully-checked count stay, labelled "last 24 h".
//   · A count of what was not fully checked is never behind a tap: each answer row is its blocks — the wallet, the lead, a
//     reader's state (one concise line: red and announced, role=alert, or amber and quiet), and "Not fully checked: 5 payments · 2 watches or alerts" — whatever the
//     answer is, a wallet not being read included; behind Details the reader's raw sentences, what the second count means, the rest.
//   · "No alerts" and an empty month are decided from the worker's TALLIES, not from the rows: an alert or a payment that is
//     counted and has no row is said as counted — "1 alert counted · $7,000 — list not read." — with a Refresh.
//   · A zero, a "none" or an empty list is a fact only where everything was read: a wallet's periods and the month ring say
//     "Not known", a pressed level "None read at this level.", payments counted and not listed "N payments counted — list not
//     read.", the flags "Nothing flagged in what was checked." unless the night AND the sender check looked at all (22 Oct 2026).
//   · Words are a budget: a line is ≤ 10 words and its detail sits behind a tap (StateLine's Details, a native <details>:
//     the rules, the provider details, "N more", the funds bars) — nothing is deleted, and the page's own HTML keeps it.
//   · The Payments table gives each column its seat: the hash stands from 1400 px, the score from xl (both in the row's
//     details and its trace); Main risk always has room and wraps rather than cutting an adverse clause.
//
// THE PHONE (19 Sept 2026, the phone-layout pass): below lg every table on
// this screen is a list of cards on the Ledger's card seats — line 1 the
// pill and the words, line 2 the time, the chain and the row's state with
// Trace at the line's end. The tables stand at lg+; the two forms are
// switched by the breakpoint alone.
import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { ExternalLink, Copy, Check, Search, X, ChevronDown, ChevronUp } from 'lucide-react';
import {
  api, type KytBand, type KytBooksWallet, type KytOverview, type KytProviderState, type KytSweep, type KytTransferRow, type KytWatchedWallet,
  type KytDepositAnswer, type KytExposure, type KytIncoming, type KytIncomingWallet, type KytKind, type KytLevel, type KytPayment, type KytTally,
  type KytTrace, type KytTraceGroup, type KytTransfers, type KytDeep, type TreasuryBalancesResponse, type TreasuryOutside, type TreasuryRailValue,
} from '../lib/api';
import { ScreenHeader } from '../components/ScreenHeader';
import { ApiError, errorWords } from '../lib/errors';
import { Pending } from '../components/Pending';
import { Pill, type PillTone } from '../components/Pill';
import { Card, EmptyState, Label, StateLine } from '../components/kit';
import { LEDGER } from '../components/Ledger';
import { KeyValue, KeyValueRow } from '../components/KeyValue';
import { fmtRelative, fmtStamp, fmtAmount, fmtMoney, fmtDay, fmtMonth, fmtTime, smartCase, displayAsset, dubaiDate } from '../lib/format';
import { DUST_DEFAULT, dustFor, partyName, sameAsset, foldAsset } from '../lib/naming';

// ── Portals ─────────────────────────────────────────────────────────────────
const ARKHAM_PORTAL   = 'https://arkm.com';
const GL_VISION_PORTAL = 'https://vision.glprotocol.com';

const arkhamUrl = (address: string) => `${ARKHAM_PORTAL}/explorer/address/${encodeURIComponent(address)}`;

// Public explorers, keyed by the chain names the two systems use. A chain
// this table does not know simply gets no explorer link — never a wrong one.
const EXPLORER: Record<string, { address: (a: string) => string; tx: (h: string) => string }> = {
  TRC20:    { address: (a) => `https://tronscan.org/#/address/${a}`,      tx: (h) => `https://tronscan.org/#/transaction/${h}` },
  TRON:     { address: (a) => `https://tronscan.org/#/address/${a}`,      tx: (h) => `https://tronscan.org/#/transaction/${h}` },
  ERC20:    { address: (a) => `https://etherscan.io/address/${a}`,        tx: (h) => `https://etherscan.io/tx/${h}` },
  ETH:      { address: (a) => `https://etherscan.io/address/${a}`,        tx: (h) => `https://etherscan.io/tx/${h}` },
  ETHEREUM: { address: (a) => `https://etherscan.io/address/${a}`,        tx: (h) => `https://etherscan.io/tx/${h}` },
  BEP20:    { address: (a) => `https://bscscan.com/address/${a}`,         tx: (h) => `https://bscscan.com/tx/${h}` },
  // Base (19 Sept 2026, Hot wallet · Base): the same EVM key on chain 8453.
  BASE:     { address: (a) => `https://basescan.org/address/${a}`,        tx: (h) => `https://basescan.org/tx/${h}` },
  BSC:      { address: (a) => `https://bscscan.com/address/${a}`,         tx: (h) => `https://bscscan.com/tx/${h}` },
  BTC:      { address: (a) => `https://mempool.space/address/${a}`,       tx: (h) => `https://mempool.space/tx/${h}` },
  BITCOIN:  { address: (a) => `https://mempool.space/address/${a}`,       tx: (h) => `https://mempool.space/tx/${h}` },
  SOL:      { address: (a) => `https://solscan.io/account/${a}`,          tx: (h) => `https://solscan.io/tx/${h}` },
  SOLANA:   { address: (a) => `https://solscan.io/account/${a}`,          tx: (h) => `https://solscan.io/tx/${h}` },
};
export const explorer = (chain: string | null | undefined) => EXPLORER[(chain ?? '').trim().toUpperCase()];

/** Whether the screener has an answer on record for this wallet. */
export const looked = (w: { lastChecked?: string | null; last_checked?: string | null }): boolean => !!(w.lastChecked ?? w.last_checked);

/** The provider in the operator's words — a slug is not a screen word. */
export const providerName = (p: string): string => (p === 'global_ledger' || p === 'gl' ? 'GL Vision' : p === 'arkham' ? 'Arkham' : smartCase(p));

export const mask = (a: string): string => (a.length <= 12 ? a : `${a.slice(0, 6)}…${a.slice(-4)}`);

export const BAND_TONE: Record<KytBand, PillTone> = { low: 'good', medium: 'warn', high: 'bad', severe: 'bad' };

/** No band is one of two different facts, and they get different words:
 *  nobody looked (unscreened), or a provider looked and gave no score — Arkham
 *  on the pay-as-you-go plan names an address, it does not score it. */
export function BandPill({ band, looked = false }: { band: KytBand | string | null | undefined; looked?: boolean }) {
  if (!band) return <Pill>{looked ? 'no score' : 'unscreened'}</Pill>;
  const tone = BAND_TONE[band as KytBand] ?? 'neutral';
  return <Pill tone={tone}>{band}</Pill>;
}

/** Masked address with the full one on hover, a copy button, and the two
 *  intel doors as icons (§5.29 alignment — the words made every table wider
 *  than a phone). The full address never renders as text. */
/** A door's hit area: the icon is 12 px, the area to press is 24 — beside its neighbour's, never over it. */
const DOOR = 'inline-flex items-center justify-center p-1.5 -m-1.5';

/** `poisoning` (3 Nov 2026, the worker's look-alike rule): the address is an address-poisoning attempt — red, the warning for its
 *  title, the explorer door only. No Copy: a history list with a copy button is what the attack relies on. */
function Address({ address, chain, quiet = false, poisoning = null }: { address: string; chain?: string | null; quiet?: boolean; poisoning?: string | null }) {
  const [copied, setCopied] = useState(false);
  const ex = explorer(chain);
  const doors = (
    <>
      {!poisoning && (<>
      <button
        type="button"
        className={`${DOOR} tap-44 text-tg-hint hover:text-tg-text`}
        title="Copy full address"
        aria-label="Copy full address"
        onClick={() => {
          void navigator.clipboard?.writeText(address).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          });
        }}
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </button>
      <a href={arkhamUrl(address)} target="_blank" rel="noreferrer" className={`${DOOR} tap-44 text-info hover:text-tg-text`} title="Open in Arkham" aria-label="Open in Arkham">
        <Search size={12} />
      </a>
      </>)}
      {ex && (
        <a href={ex.address(address)} target="_blank" rel="noreferrer" className={`${DOOR} text-info hover:text-tg-text`} title="Open in the chain explorer" aria-label="Open in the chain explorer">
          <ExternalLink size={12} />
        </a>
      )}
    </>
  );
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap" data-address={poisoning ? 'lookalike' : 'plain'}>
      <span className={`font-mono tabular text-meta${poisoning ? ' text-neg' : ''}`} title={poisoning ? `Address poisoning — look-alike of ${poisoning}. Never a payee; never copy.` : address}>{mask(address)}</span>
      {/* `quiet` (a list row at lg+): the three doors show when the row is pointed at or one is focused — still there, still tabbable. */}
      <span className={`inline-flex items-center gap-3 ${quiet ? 'lg:opacity-0 lg:group-hover:opacity-100 lg:focus-within:opacity-100 transition-opacity' : ''}`}>{doors}</span>
    </span>
  );
}

/** Half-height tile, one figure each (7 Sept 2026). */
function Stat({ label, value, tone }: { label: string; value: React.ReactNode; tone?: 'warn' | 'bad' | 'good' }) {
  const color = tone === 'bad' ? 'text-neg' : tone === 'warn' ? 'text-warn' : tone === 'good' ? 'text-pos' : '';
  return (
    <div className="card px-2.5 py-1.5 min-w-0">
      <div className="text-label uppercase tracking-wide text-tg-hint truncate">{label}</div>
      <div className={`text-body font-semibold tabular truncate ${color}`}>{value}</div>
    </div>
  );
}

/** The phone card's seats, shared by the four lists below (the Ledger's
 *  card: line 1 pill + words, line 2 time · state · action). A section is a
 *  `card p-0`, so each card carries the section's 12px side gutter; the
 *  last card in a list drops its rule so it does not double the card's edge. */
const CARD = `${LEDGER.card} px-3 last:border-b-0`;
/** Line 2 may wrap: a wallet's chain, address state and risk beside a Trace
 *  button do not always share one 375px line, and a wrapped line beats a
 *  clipped one. The action sits at the line's end (ml-auto). */
const CARD_LINE2 = `${LEDGER.cardLine2} flex-wrap gap-y-1`;
/** The chain's code on a card — mono, muted, a step down. */
const CHAIN = 'font-mono tabular text-meta text-tg-hint';
/** A section's header line: title at the left, a muted note at the right;
 *  the note wraps under the title on a phone rather than squeezing it. */
const SECTION_HEAD = 'flex flex-wrap items-center justify-between gap-x-3 px-3 py-2 border-b border-stroke-soft';

// ── The screener's own account of itself (1 Oct 2026) ───────────────────────
// Every figure below is the last time something REAL happened — a provider
// that answered, a night that looked at wallets — never the last time a job
// ran. The hint line this replaces showed the clock of the job, and read
// healthy for 25 nights while Arkham answered nothing.
export const PROVIDER_WORD: Record<KytProviderState, { word: string; tone: PillTone }> = {
  ok:          { word: 'Working',       tone: 'good' },
  dark:        { word: 'Not answering', tone: 'bad' },
  unknown:     { word: 'Not asked yet', tone: 'neutral' },
  not_enabled: { word: 'Off',           tone: 'neutral' },
};

/** `word` replaces the provider wording where the item is not a provider (the
 *  nightly check ran or did not; it does not "answer"), and `tone` its colour
 *  where a job is neither working nor failed — amber, part done. */
export interface StripItem {
  key: string; name: string; state: KytProviderState;
  /** The note as one line — today's strip's. The rebuilt System table draws the fields under it instead (1 Nov 2026). */
  note: string;
  /** the whole note, behind "Provider details" */
  detail?: string; word?: string; tone?: PillTone;
  /** The credits: "1,182 credits left", "412 of 1,500 credits today". */
  credits?: string | null;
  /** When it last answered, ran or was refreshed — "last score 2h ago", "since 2h ago", "10h ago". */
  when?: string | null;
  /** What else the note says ("12 lists", a sender check's counts) — nothing when the two fields hold it all. */
  rest?: string | null;
}

/** The nightly check runs once a day, so its newest record is under a day old
 *  — plus slack for a late run. Older than this, "last night" is not last night. */
const NIGHT_STALE_MS = 26 * 3600_000;
function nightIsStale(sw: KytSweep, nowMs: number): boolean {
  const at = sw.at ? Date.parse(sw.at) : NaN;
  return !Number.isFinite(at) || nowMs - at > NIGHT_STALE_MS;
}

/** A COUNT, or a percentage on a rule line — whole: payments, wallets, credits, sources. Never an amount: an amount goes
 *  through fmtAmount with its asset, so the shared rule in lib/format.ts decides its places (N-084, his "screens too",
 *  30 Oct 2026 — the amounts rule covers the screens; this screen keeps no figure rule of its own). */
export const whole = (n: number) => fmtAmount(n, undefined, 0);
export const joined = (parts: Array<string | null | false | undefined>) => parts.filter(Boolean).join(' · ');

// ── The screen's own small pieces (the design system's rules, docs/DESIGN-SYSTEM.md §3 and §5) ──────────
// A line is ONE line — what happened and what to do — and its detail sits behind a tap. A failed read is
// never drawn like an empty one; a long server sentence shows its first sentence and folds the rest.

/** A server's sentence in two parts: its first sentence (shown) and the rest (behind a tap). */
export function firstSentence(s: string | null | undefined): { first: string; rest: string } {
  const t = (s ?? '').trim();
  const m = /^(.+?[.!?])\s+(\S[\s\S]*)$/.exec(t);
  return m ? { first: m[1]!.replace(/\.$/, ''), rest: m[2]! } : { first: t.replace(/\.$/, ''), rest: '' };
}

/** What a failed read of the screener says: the cause in a few words, and the detail for behind a tap.
 *  The BFF's own reason ("the screening worker answered 502 …") is the cause — the generic "the books did not
 *  answer, the desk Mac may be asleep" is for a gateway error only, and the raw text of a database error is
 *  never put on the screen. */
export function readFailure(e: unknown): { why: string; more: string } {
  if (e instanceof ApiError) {
    const reason = typeof e.body.reason === 'string' ? e.body.reason.trim() : '';
    const said = reason ? reason.charAt(0).toUpperCase() + reason.slice(1) : errorWords(e);
    if (e.code === 'kyt_not_wired') return { why: 'the screener is not connected', more: said };
    if (e.code === 'kyt_unreachable') return { why: 'the screener is not answering', more: said };
    // Any other refusal says its own first sentence ("the books did not answer", "your sign-in has expired"): the Books' words.
    const first = firstSentence(errorWords(e)).first;
    return { why: first.charAt(0).toLowerCase() + first.slice(1), more: errorWords(e) };
  }
  if (e instanceof TypeError) return { why: 'no connection', more: errorWords(e) };
  return { why: 'no answer', more: errorWords(e) };
}

/** A line that stands above the views: a red slab (failed, alarmed) or an amber one (warned) — ONE line, its
 *  detail behind a tap (the kit's StateLine), and `below` for the particulars that must stay in view. */
function Slab({ tone, children, more, action, below }: {
  tone: 'bad' | 'warn'; children: React.ReactNode; more?: React.ReactNode; action?: React.ReactNode; below?: React.ReactNode;
}) {
  return (
    <div className={`rounded-lg p-3 text-body ${tone === 'bad' ? 'bg-neg-bg' : 'bg-warn-bg'}`} data-slab={tone}>
      <StateLine tone={tone} more={more} action={action}>{children}</StateLine>
      {below}
    </div>
  );
}

/** A fold behind a tap that stays in the page's own HTML (a native <details>): a keyboard reaches it, and what is
 *  folded is never lost to a script. */
function Disclosure({ label, children, className = '' }: { label: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <details className={`group ${className}`}>
      <summary className="inline-flex cursor-pointer list-none items-center gap-1 text-meta text-tg-hint hover:text-tg-text [&::-webkit-details-marker]:hidden">
        <span aria-hidden="true" className="group-open:hidden">▸</span>
        <span aria-hidden="true" className="hidden group-open:inline">▾</span>
        {label}
      </summary>
      <div className="mt-1.5 space-y-1 border-l border-stroke pl-3 text-meta text-tg-hint">{children}</div>
    </details>
  );
}

/** A section of this screen that could not be said at all — in the screen's own section shape (a header row, a body), rimmed by
 *  the severity: a failed read is red, a gap amber. */
function SectionNote({ title, tone, children }: { title: string; tone: 'warn' | 'bad'; children: React.ReactNode }) {
  return (
    <section className={`card p-0 overflow-hidden ${tone === 'bad' ? 'border-neg/40' : 'border-warn/40'}`}>
      <header className={SECTION_HEAD}><h2 className="font-semibold text-body">{title}</h2></header>
      <div className="p-3">{children}</div>
    </section>
  );
}

/** "Try again" — the one action of a failed read. */
function Retry({ onClick }: { onClick: () => void }) {
  return <button type="button" className="btn-link text-meta" onClick={onClick}>Try again</button>;
}

/** "Refresh" — the one action of a list that was counted and did not come. */
function Refresh({ onClick }: { onClick: () => void }) {
  return <button type="button" className="btn-link text-meta" onClick={onClick}>Refresh</button>;
}

/** One item per provider, each with its OWN state. They are never merged: a
 *  working explorer beside a failed sanctions oracle is one green and one red,
 *  not one green (review, 1 Oct 2026 — the first cut let the explorer speak for
 *  both, which is the false-healthy this strip exists to remove). */
export function providerStrip(d: KytOverview, nowMs: number): StripItem[] | null {
  const p = d.providers;
  if (!p) return null;
  const s = d.spend;
  const since = (x: { state: KytProviderState; lastOk: string | null; lastFail: string | null }, word: string) =>
    x.state === 'dark'
      ? x.lastFail && `since ${fmtRelative(x.lastFail)}`
      : x.lastOk && `${word} ${fmtRelative(x.lastOk)}`;
  const items: StripItem[] = [];
  if (p.gl) {
    const glWhen = since(p.gl, 'last score');
    items.push({ key: 'gl', name: 'GL Vision', state: p.gl.state,
      note: joined([s.glRemaining != null && `${whole(s.glRemaining)} credits left`, glWhen]),
      credits: s.glRemaining != null ? `${whole(s.glRemaining)} credits left` : null, when: glWhen || null, rest: null,
      detail: joined([s.glRemaining != null && `${whole(s.glRemaining)} credits left`, s.glActiveTo && `to ${fmtDay(s.glActiveTo, true)}`, glWhen]) });
  }
  if (p.arkham) {
    const arkWhen = since(p.arkham, 'last answer');
    items.push({ key: 'arkham', name: 'Arkham', state: p.arkham.state,
      note: joined([`${whole(s.arkhamToday)} of ${whole(s.arkhamBudget)} credits today`, arkWhen]),
      credits: `${whole(s.arkhamToday)} of ${whole(s.arkhamBudget)} credits today`, when: arkWhen || null, rest: null,
      detail: joined([
        s.arkhamRisk ? 'names and risk' : 'names only, no risk score on this plan',
        `${whole(s.arkhamToday)} of ${whole(s.arkhamBudget)} credits today`,
        s.arkhamRemaining != null && `${whole(s.arkhamRemaining)} left`,
        arkWhen,
      ]) });
  }
  if (p.ofac) {
    // The lists are refreshed once a day BY A JOB. A stamp older than that is a
    // job that has stopped, with no failure to show for it — the nightly
    // check's shape, judged the same way: WHEN before WHAT. (The providers
    // below are asked only when a wallet is checked, so their last answer may
    // honestly be days old.)
    const at = p.ofac.lastOk ? Date.parse(p.ofac.lastOk) : NaN;
    const staleLists = p.ofac.state === 'ok' && (!Number.isFinite(at) || nowMs - at > NIGHT_STALE_MS);
    const lists = p.ofac.detail?.match(/^\d+ lists?/)?.[0] ?? null;
    const ofacWhen = (staleLists ? p.ofac.lastOk && `last refreshed ${fmtRelative(p.ofac.lastOk)}` : since(p.ofac, 'refreshed')) || null;
    items.push({
      key: 'ofac', name: 'Sanctions lists', state: staleLists ? 'dark' : p.ofac.state, ...(staleLists ? { word: 'Not refreshed' } : {}),
      note: joined([lists, ofacWhen]),
      credits: null, when: ofacWhen, rest: lists,
    });
  }
  // A provider not asked yet is said by its pill ("Not asked yet"): the note does not say it again.
  const asked = (x: { state: KytProviderState; lastOk: string | null; lastFail: string | null }) =>
    x.state === 'unknown' ? '' : joined([since(x, 'last answer')]);
  if (p.oracle) items.push({ key: 'oracle', name: 'Sanctions oracle', state: p.oracle.state, note: asked(p.oracle), credits: null, when: asked(p.oracle) || null, rest: null });
  if (p.explorer) items.push({ key: 'explorer', name: 'Chain explorers', state: p.explorer.state, note: asked(p.explorer), credits: null, when: asked(p.explorer) || null, rest: null });
  // The job itself, with its real time AND its result — the same verdict as the
  // tile above, in the strip's words. It used to be a green "Ran" for any
  // recent run, beside a tile reading 0 of 99 in red; and with no record at
  // all it was not on the strip (final review, 1 Oct 2026).
  const night = nightTile(d, nowMs);
  if (night.word === 'Off') {
    items.push({ key: 'night', name: 'Nightly check', state: 'not_enabled', note: 'Arkham is off', credits: null, when: null, rest: 'Arkham is off' });
  } else if (night.word) {
    items.push({
      key: 'night', name: 'Nightly check', state: 'unknown', word: night.word, tone: night.tone ?? 'neutral',
      note: d.sweep?.at ? fmtRelative(d.sweep.at) : 'no record of a run',
      credits: null, when: d.sweep?.at ? fmtRelative(d.sweep.at) : 'no record of a run', rest: null,
    });
  }
  items.push(senderItem(d, nowMs));
  return items;
}

/** How many items on the strip are failed — a provider not answering, a sanctions list not refreshed, a night or a sender
 *  check that failed — the count the System tab wears whichever view is open (today's chip and the v2 tab say the same). */
export const notWorking = (strip: StripItem[] | null): number =>
  (strip ?? []).filter((it) => (it.tone ?? PROVIDER_WORD[it.state].tone) === 'bad').length;

/** The sender ring — who paid the watched wallets, checked nightly. It is on
 *  the strip ALWAYS: a failed ring, a stale one, one that never ran and one
 *  never switched on must not look alike, and the first cuts of this strip
 *  showed some of them as nothing at all (final review, 1 Oct 2026).
 *
 *  Decided in this order: what is switched on NOW (the setting's to say, not
 *  the last record's) · whether it ran lately · what it COUNTED. Green is
 *  earned by the counts, never taken from the state word — and a worker from
 *  before the counters (no `senders`) cannot prove an ok, so its ok is shown
 *  without a colour and without its figures. */
export function senderItem(d: KytOverview, nowMs: number): StripItem {
  const base = { key: 'senders', name: 'Sender check' };
  const cp = d.counterparties ?? null;
  const recordSaysOff = cp?.state === 'disabled' || cp?.state === 'not_enabled';
  if (d.providers?.arkham?.state === 'not_enabled') return { ...base, state: 'not_enabled', note: 'Arkham is off', credits: null, when: null, rest: 'Arkham is off' };
  // A worker that does not say which rings are on leaves the last record to say it.
  const rings = d.spend.arkhamRings;
  const on = rings ? rings.includes('counterparty') : !recordSaysOff;
  if (!on) return { ...base, state: 'not_enabled', note: 'not switched on', credits: null, when: null, rest: 'not switched on' };
  if (!cp) return { ...base, state: 'dark', word: 'Not running', note: 'no record of a run', credits: null, when: 'no record of a run', rest: null };
  const when = cp.at ? fmtRelative(cp.at) : 'no record of a run';
  const at = cp.at ? Date.parse(cp.at) : NaN;
  if (!Number.isFinite(at) || nowMs - at > NIGHT_STALE_MS) return { ...base, state: 'dark', word: 'Not running', note: when, credits: null, when, rest: null };
  // Switched on after the last run: that record is of a ring that was off.
  if (recordSaysOff) return { ...base, state: 'unknown', word: 'Not run yet', note: 'switched on', credits: null, when: null, rest: 'switched on' };
  if (cp.state === 'idle') return { ...base, state: 'unknown', word: 'No wallets', note: when, credits: null, when, rest: null };
  const counted = cp.senders != null;
  // On the line: what fell short, the senders' own count, what was flagged, when. A full read of every wallet is no news.
  const note = !counted ? when : joined([
    cp.inflowsRead != null && cp.inflowsRead < cp.wallets && `${whole(cp.inflowsRead)} of ${whole(cp.wallets)} wallets read`,
    (cp.fullPages ?? 0) > 0 && `${whole(cp.fullPages ?? 0)} with more than one page`,
    `${whole(cp.screened)} of ${whole(cp.senders ?? 0)} senders checked`,
    cp.riskScoring === true && (cp.noBand ?? 0) > 0 && `${whole(cp.noBand ?? 0)} without a risk score`,
    cp.flagged > 0 && `${whole(cp.flagged)} flagged`,
    when,
  ]);
  // …and the whole run, every count, behind "Provider details".
  const detail = !counted ? when : joined([
    cp.inflowsRead != null && `${whole(cp.inflowsRead)} of ${whole(cp.wallets)} wallets read`,
    (cp.fullPages ?? 0) > 0 && `${whole(cp.fullPages ?? 0)} with more than one page`,
    `${whole(cp.screened)} of ${whole(cp.senders ?? 0)} senders checked`,
    cp.riskScoring === true && (cp.noBand ?? 0) > 0 && `${whole(cp.noBand ?? 0)} without a risk score`,
    cp.flagged > 0 && `${whole(cp.flagged)} flagged`,
    when,
  ]);
  // The same line as fields (1 Nov 2026): the counts in one cell, when it ran in another.
  const counts = !counted ? null : joined([
    cp.inflowsRead != null && cp.inflowsRead < cp.wallets && `${whole(cp.inflowsRead)} of ${whole(cp.wallets)} wallets read`,
    (cp.fullPages ?? 0) > 0 && `${whole(cp.fullPages ?? 0)} with more than one page`,
    `${whole(cp.screened)} of ${whole(cp.senders ?? 0)} senders checked`,
    cp.riskScoring === true && (cp.noBand ?? 0) > 0 && `${whole(cp.noBand ?? 0)} without a risk score`,
    cp.flagged > 0 && `${whole(cp.flagged)} flagged`,
  ]) || null;
  const fields = { credits: null, when, rest: counts };
  if (cp.state === 'dark') return { ...base, state: 'dark', word: 'Failed', note, detail, ...fields };
  if (cp.state === 'budget') return { ...base, state: 'dark', word: 'No credits', note, detail, ...fields };
  const short =
    (cp.inflowsRead != null && cp.inflowsRead < cp.wallets) ||
    (cp.senders != null && cp.screened < cp.senders) ||
    (cp.errors ?? 0) > 0 ||
    (cp.fullPages ?? 0) > 0 ||
    (cp.riskScoring === true && (cp.noBand ?? 0) > 0) ||
    cp.stoppedOnBudget === true;
  if (cp.state === 'partial' || short) return { ...base, state: 'ok', tone: 'warn', word: 'Part done', note, detail, ...fields };
  // Green needs the state AND the counters behind it.
  return cp.state === 'ok' && counted ? { ...base, state: 'ok', word: 'Ran', note, detail, ...fields } : { ...base, state: 'unknown', word: 'Ran', note, detail, ...fields };
}

/** Whether the sender check — when it is on — screened every sender it meant to: a part, a failure, a stopped job, a record
 *  that is missing or one with no counters to prove it are not. Off, or with no wallet to read, there is nothing it missed. */
export function senderCheckWhole(it: StripItem): boolean {
  if (it.state === 'not_enabled' || it.word === 'No wallets') return true;
  return it.state === 'ok' && it.word === 'Ran' && !it.tone;
}

/** The night's verdict: the tile's figure and colour, the red line's reason,
 *  and the same verdict in the strip's word ('' = nothing to say). */
export interface Night { value: string; tone?: 'good' | 'warn' | 'bad'; why: string | null; short?: string; word: string }

/** Last night, in one figure: wallets that got an answer, of those it meant
 *  to check — and the tone comes from THAT, not from the state word alone. A
 *  night that answered 12 of 99 and then ran out of credits is not a good
 *  night whatever it calls itself, and a night that answered none gets the red
 *  line with the true reason: Arkham silent, or the day's credits already gone
 *  (review, 1 Oct 2026). A record from before 1 Oct carries no state. `nowMs`
 *  is the BFF's own read time, so the viewer's clock cannot age a record. */
export function nightTile(d: KytOverview | undefined, nowMs: number): Night {
  if (!d) return { value: '—', why: null, word: '' };
  const sw = d.sweep;
  // WHAT IS ON NOW is the provider's to say, not the last record's. With no key
  // today the check is Off whatever last night found; with a key today, a
  // record of a night without one is not "Off" — it is a check that has not
  // run since. A worker from before `providers` leaves the record to say it
  // (final review, 1 Oct 2026 — the sender item already worked this way).
  const keyNow = d.providers?.arkham ? d.providers.arkham.state !== 'not_enabled' : null;
  if (keyNow === false) return { value: 'Off', why: null, word: 'Off' };
  if (!sw) {
    // No record at all. A worker from before `providers` could say no more
    // than a dash. Since then a missing record is a job that has not run: with
    // Arkham on and wallets on the watchlist that is a failure, not a dash —
    // a provider answering a call does not show the nightly check ever ran
    // (final review, 1 Oct 2026).
    if (!d.providers) return { value: '—', why: null, word: '' };
    if (d.wallets.length === 0) return { value: 'Nothing to check', why: null, word: 'No wallets' };
    return {
      value: 'Not run',
      tone: 'bad',
      why: 'There is no record of a nightly check — nothing has looked at the watched wallets.',
      short: 'no record of a nightly check',
      word: 'Not running',
    };
  }
  if (sw.state === 'not_enabled' && keyNow === null) return { value: 'Off', why: null, word: 'Off' };
  // WHEN before WHAT: a good record from a job that has since stopped is not
  // last night's result, and must not sit there green (review, 1 Oct 2026).
  if (nightIsStale(sw, nowMs)) {
    return {
      value: 'Not run',
      tone: 'bad',
      why: `The nightly check has not run since ${sw.at ? fmtRelative(sw.at) : 'its last record'} — nothing has looked at the watched wallets since then.`,
      short: `the nightly check last ran ${sw.at ? fmtRelative(sw.at) : 'long ago'}`,
      word: 'Not running',
    };
  }
  // The key arrived after the last run: that record is of a night without one.
  if (sw.state === 'not_enabled') return { value: 'Not run yet', why: null, word: 'Not run yet' };
  if (sw.state === 'idle' || sw.targets === 0) return { value: 'Nothing to check', why: null, word: 'No wallets' };
  // "noBand" is a name with no risk band. On the pay-as-you-go plan that is the whole answer; on a plan that scores
  // (sw.riskScoring) it is a wallet NOT scored, counted as a shortfall — as the sender ring already counts it (audit
  // F3, 21 Oct 2026: 0 scored of 2 read "2 of 2", green).
  const unscored = sw.riskScoring ? (sw.noBand ?? 0) : 0;
  const answered = sw.checked + (sw.noBand ?? 0) + (sw.cached ?? 0) - unscored;
  const none = answered === 0;
  const short = answered < sw.targets;
  const outOfCredits = sw.state === 'budget' || sw.stoppedOnBudget === true;
  const tone = none || sw.state === 'dark' ? 'bad' : short || sw.state === 'partial' || outOfCredits ? 'warn' : 'good';
  // The credits are the reason only when they were gone before a single call.
  // A call that was made and failed is Arkham's silence, whatever ran out after
  // it — the worker calls that night dark, and so does this line.
  const neverAsked = outOfCredits && !((sw.attempted ?? 0) > 0);
  const why = !none
    ? null
    : unscored > 0 && (sw.attempted ?? 0) > 0
      ? `Arkham named ${whole(unscored)} wallet${unscored === 1 ? '' : 's'} but scored none — on a plan that scores, that is no check.`
      : neverAsked
        ? 'Arkham credits for the day had run out before any watched wallet was looked at.'
        : `Arkham did not answer${sw.lastError ? ` (${sw.lastError})` : ''} — the watched wallets were not looked at.`;
  const word = tone === 'bad' ? (none && neverAsked ? 'No credits' : 'Failed') : tone === 'warn' ? 'Part done' : 'Ran';
  // The same reason in a few words, for the red line above the views; `why` is the whole sentence, behind its Details.
  const reason = !none
    ? undefined
    : unscored > 0 && (sw.attempted ?? 0) > 0
      ? `Arkham named ${whole(unscored)} wallet${unscored === 1 ? '' : 's'} but scored none`
      : neverAsked
        ? 'Arkham credits ran out first'
        : `Arkham did not answer${sw.lastError ? ` (${sw.lastError})` : ''}`;
  return { value: `${whole(answered)} of ${whole(sw.targets)}`, tone, why, short: reason, word };
}

/** What the "Read the transfers" button says and whether it may be pressed —
 *  the one paid action on this screen, inside a wallet's trace. The BFF does
 *  the arithmetic (two Arkham credits for every transfer returned, sized to
 *  what today's cap can still pay); these are only the words. It stands down
 *  when a press could only be refused (the budget, sweep item 38, 14 Sept 2026;
 *  Arkham off, 1 Oct 2026). A failed last call does NOT stand it down: a press
 *  is how a recovery is found, and a call that fails costs nothing. */
export interface TraceInfo { off: boolean; label: string; again: string; title: string }
export const TRACE_UNREAD: TraceInfo = { off: true, label: 'Read the transfers', again: 'Read again', title: '' };

export function traceInfo(d: KytOverview, budgetOut: boolean): TraceInfo {
  const t = d.trace;
  if (!t) {
    return {
      off: budgetOut,
      label: 'Read the transfers',
      again: 'Read again',
      title: budgetOut
        ? 'Arkham credits for today are spent — reading transfers is paused until 04:00 Dubai time'
        : 'The last 7 days in and out, with who the other side is — paid in Arkham credits, cached 10 minutes',
    };
  }
  if (t.off) {
    return {
      off: true,
      label: 'Read the transfers',
      again: 'Read again',
      title: t.off === 'budget'
        ? 'Arkham credits left for today cannot pay for this read — it resumes at 04:00 Dubai time'
        : 'Arkham is off on the screener',
    };
  }
  const caution = t.unsure ? 'Arkham did not answer its last call — this read may fail, and a failed call costs nothing. ' : '';
  return {
    off: false,
    label: `Read the transfers · up to ${whole(t.credits)} credits`,
    again: `Read again · up to ${whole(t.credits)} credits`,
    title: `${caution}The last 7 days in and out, with who the other side is — two Arkham credits for every transfer returned, at most ${whole(t.rows)} now; cached 10 minutes`,
  };
}

type View = 'payments' | 'wallets' | 'system';
/** One question to a view (5 Oct 2026, the operator: "build a proper screens
 *  for effective KYT Tracing, not too complicated, not overwhelming"). Until then
 *  one screen asked everything at once, top to bottom. The treasury is what the
 *  first two track; onboarding had a view of its own until 7 Oct 2026, when it moved to the Onboarding page — its New leads and All chats tabs (the operator: "KYT doesn't need to show leads/customers kyt data") (the operator, 6 Oct 2026:
 *  "we need just treasury wallets into KYT screen … not all onboarding wallets we
 *  have checked … can be clubbed inside the KYT screen but not on the main
 *  screen. Treasury wallets are the important ones to track"). */
const VIEWS: Array<{ key: View; label: string }> = [
  { key: 'payments', label: 'Payments' },
  { key: 'wallets', label: 'Wallets' },
  { key: 'system', label: 'System' },
];

/** What is open beside the list: one payment's trace, or one wallet's. */
export type Tracing = { kind: 'payment'; id: string } | { kind: 'wallet'; chain: string; address: string } | null;

const sameAddress = (a: string, b: string): boolean => a === b || (a.startsWith('0x') && a.toLowerCase() === b.toLowerCase());
/** The network a chain code names — the two systems write the same one several ways. */
const NETWORK: Record<string, string> = {
  TRC20: 'TRON', TRON: 'TRON', TRX: 'TRON', ERC20: 'ETH', ETH: 'ETH', ETHEREUM: 'ETH', BEP20: 'BSC', BSC: 'BSC', BNB: 'BSC',
  BASE: 'BASE', BTC: 'BTC', BITCOIN: 'BTC', SOL: 'SOL', SOLANA: 'SOL',
  // The Books' slug tokens (`hot.trc`, `cold.ledger` has none) and the Investment coins' networks (TERMINOLOGY §2, 30 Oct 2026).
  TRC: 'TRON', ERC: 'ETH', BEP: 'BSC', APT: 'APTOS', APTOS: 'APTOS', TON: 'TON', GRAM: 'TON',
};
export const network = (chain: string | null | undefined): string => {
  const c = (chain ?? '').trim().toUpperCase();
  return NETWORK[c] ?? c;
};
/** The network as the bible spells it (docs/TERMINOLOGY.md §2: TRC20 · ERC20 · BEP20 · Base · SOL · BTC — never TRON or ETH
 *  for the network): the word a person reads. `network()` above keeps the keys the screen compares by, until N-091's one
 *  wallet name lands. */
const NETWORK_WORD: Record<string, string> = { TRON: 'TRC20', ETH: 'ERC20', BSC: 'BEP20', BASE: 'Base', SOL: 'SOL', BTC: 'BTC', APTOS: 'Aptos', TON: 'TON' };
/** Whether a token names a network at all (`trc` yes, `ledger` no). */
export const isNetwork = (token: string | null | undefined): boolean => (token ?? '').trim().toUpperCase() in NETWORK;
export const networkWord = (chain: string | null | undefined): string => NETWORK_WORD[network(chain)] ?? network(chain);
/** The exception's mark on a register row (the legacy list): "freeze watch only" when the freeze watch holds a desk wallet the
 *  desk list does not (3 Nov 2026, his Hot · Base), else "not watched"; the words behind each are its title. */
export function WatchMark({ w }: { w: { chain: string; address: string; label?: string | null; freeze_watched?: boolean; swept_at?: string | null } }) {
  if (w.freeze_watched) {
    const net = networkWord(w.chain);
    return <span className="text-meta text-warn whitespace-nowrap" title={`Frozen coins watched${w.swept_at ? `, last ${fmtRelative(w.swept_at)}` : ''}; payments on ${net} are not read here and GL gives no ${net} score`}>freeze watch only</span>;
  }
  return <span className="text-meta text-warn whitespace-nowrap" title={`/watch ${w.chain} ${w.address}${w.label ? ` ${w.label}` : ''}`}>not watched</span>;
}
/** One wallet: the same address on the same network. The Books' Base wallet is the September
 *  ERC20 key — by address alone, its trace showed the other network's party and GL score. */
export const sameWallet = (a: { chain: string; address: string }, b: { chain: string; address: string }): boolean =>
  network(a.chain) === network(b.chain) && sameAddress(a.address, b.address);

/** `initialTrace`: the trace the screen opens with — none, as the router renders
 *  it. It is the seam the screen's tests open a trace through (a server render
 *  cannot press a button), and where a link to one payment would land.
 *  `initialHours`: the window chosen — 24 h as the router renders it; the seam a test picks another through. */
export function Kyt({ initialTrace = null, initialHours = 24 }: { initialTrace?: Tracing; initialHours?: Hours } = {}) {
  const qc = useQueryClient();
  const [search, setSearch] = useSearchParams();
  const view: View = VIEWS.find((v) => v.key === search.get('view'))?.key ?? 'payments';
  const q = useQuery({ queryKey: ['kyt', 'overview'], queryFn: () => api.kytOverview(), refetchInterval: 60_000 });
  const [tracing, setTracing] = useState<Tracing>(initialTrace);
  // Incoming payments are a read of their own: the window is the operator's.
  const [hours, setHours] = useState<Hours>(initialHours);
  // The last window's answer stays on screen while another is read: the section does not blank on a chip.
  const incoming = useQuery({ queryKey: ['kyt', 'incoming', hours], queryFn: () => api.kytIncoming(hours), refetchInterval: 60_000, placeholderData: (prev) => prev });
  // What the treasury holds — the Books' own figures, the Treasury tab's read (its cache is shared): read while Wallets is open.
  const treasury = useQuery({ queryKey: ['treasury', 'balances'], queryFn: () => api.treasuryBalances(), enabled: view === 'wallets' });
  const alerts = paymentAlerts(incoming.data);
  // With one or two alerts the red line carries each one's particulars — what came in, from whom, why — and a way in:
  // the first screen answers "what is it", not "go and find it below" (Astra, 6 Oct 2026). Named only when the names ARE the count.
  const alertsNamed = namedAlerts(incoming.data, alerts);

  const d = q.data;
  const budgetOut = d ? d.spend.arkhamToday >= d.spend.arkhamBudget : false;
  // The BFF's clock at the moment it read the screener, not the viewer's.
  const readAt = d ? Date.parse(d.fetched_at) : NaN;
  const nowMs = Number.isFinite(readAt) ? readAt : Date.now();
  const strip = d ? providerStrip(d, nowMs) : null;
  const night = nightTile(d, nowMs);
  const trace = d ? traceInfo(d, budgetOut) : TRACE_UNREAD;
  // A view that is not on screen must not hide its trouble: its tab says it.
  const broken = notWorking(strip);
  // The Wallets tab: desk wallets whose payments are not being read, and treasury wallets nobody screens (the register's own count).
  const notRead = (incoming.data?.wallets ?? []).filter(paymentsUnread).length;
  const notWatched = d?.coverage ? Math.max(0, d.coverage.treasury - d.coverage.watched) : 0;
  const tracedPayment = tracing?.kind === 'payment' ? incoming.data?.payments.find((p) => p.id === tracing.id) : undefined;
  const openWallet = (chain: string, address: string) => setTracing({ kind: 'wallet', chain, address });
  // Reads that failed. Both down is ONE banner (the design system: all reads down = one banner); one down is its own line.
  const bothDown = q.isError && incoming.isError && !d && !incoming.data;
  const alarm = d?.meta.alarm ? firstSentence(d.meta.alarm) : null;
  const ovFail = q.isError ? readFailure(q.error) : null;
  const incFail = incoming.isError ? readFailure(incoming.error) : null;

  return (
    <div className="space-y-4">
      <ScreenHeader
        title="KYT"
        subtitle={
          d
            ? <>Read <span className="font-mono tabular">{fmtRelative(d.fetched_at)}</span></>
            : q.isError ? 'Screener not read' : 'Reading…'
        }
        actions={
          <>
            {/* The portals sit here at desktop width; on a phone they are in
                About this screen, so the title row stays one row. */}
            <a href={ARKHAM_PORTAL} target="_blank" rel="noreferrer" className="btn-secondary text-body py-1.5 hidden lg:inline-flex items-center gap-1">
              Arkham <ExternalLink size={12} />
            </a>
            <a href={GL_VISION_PORTAL} target="_blank" rel="noreferrer" className="btn-secondary text-body py-1.5 hidden lg:inline-flex items-center gap-1">
              GL Vision <ExternalLink size={12} />
            </a>
            <button type="button" className="btn-secondary text-body py-1.5 disabled:opacity-50" disabled={q.isFetching}
              onClick={() => qc.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'kyt' && query.queryKey[1] !== 'transfers' })}>
              {q.isFetching ? 'Reading…' : 'Refresh'}
            </button>
          </>
        }
        help={
          <>
            <p>
              Three views. <strong>Payments</strong>: every payment into this month&apos;s desk wallets, each checked
              against the sanctions lists and GL Vision and told in the KYT group. <strong>Wallets</strong>: the
              treasury — what each pot holds (the Books&apos; own figures, as on Wallets; money on chain not booked
              yet is said beside them, never added in), the desk wallets themselves and the treasury wallets the
              Books register.{' '}
              <strong>System</strong>: whether the screening is working, the
              rules in force and the flagged wallet checks.
            </p>
            <p>
              <strong>Trace</strong> opens beside the list and answers one question: where did this money come from.
              For a payment it shows its funds by kind of source — sanctions, criminal, risky, unknown, known — from
              GL&apos;s report on the payment&apos;s own transaction (its sender&apos;s, for a payment checked before
              that was switched on), in dollars of that payment, who paid, and what was checked. It costs nothing: it reads what
              the check already stored.
            </p>
            <p>
              Alert means keep the funds apart; Watch is reported with its reason and holds nothing. Not fully
              checked is a payment with no result yet — or one that reads clean where the lists, the oracle or
              GL&apos;s sources could not be read: it is never counted as clean.
              &ldquo;Before a deposit&rdquo; answers one question for the window you pick: does anything
              received appear on sanctions. An exchange screens deposits with its own tool and can see more.
            </p>
            <p>
              The payments are listed newest first; the order menu puts the highest risk first, or the largest. A row
              opens to say why, what was not checked, and its Trace. Every payment keeps its own level.
            </p>
            <p>
              Flagged wallet checks (System) are the newest twelve of the treasury&apos;s own screenings with a high or severe
              band, or a sanctions, scam or hack label — the senders of incoming payments and the desk wallets. An
              applicant&apos;s own check is with the lead, under Onboarding.
            </p>
            <p>
              Read-only: the screening itself lives in the onboarding worker, and watchlist changes stay
              in the ops group — <span className="font-mono">/watch &lt;chain&gt; &lt;address&gt; [label]</span>.
              A wallet in the Books that is not watched is screened by nobody.
            </p>
            <p>
              The one paid action is inside a wallet&apos;s trace: reading its transfers of the last 7 days from
              Arkham — two credits for every transfer returned, cached ten minutes. The button says the most it
              can cost.
            </p>
            <p>
              <a href={ARKHAM_PORTAL} target="_blank" rel="noreferrer" className="text-info hover:underline">Arkham portal</a>
              {' · '}
              <a href={GL_VISION_PORTAL} target="_blank" rel="noreferrer" className="text-info hover:underline">GL Vision portal</a>
            </p>
          </>
        }
      />

      {/* An alert on incoming funds comes before everything else on this screen — whichever view is open. */}
      {alerts && (
        <Slab tone="bad"
          action={view !== 'payments' ? <button type="button" className="btn-link text-meta" onClick={() => setSearch({}, { replace: true })}>See Payments</button> : undefined}
          more={alerts.listed > 0 ? `${count(alerts.listed, 'sender is', 'senders are')} on a sanctions list.` : undefined}
          below={alertsNamed.length > 0 ? (
            <ul className="mt-2 space-y-1.5 text-body text-tg-text">
              {alertsNamed.map((p) => (
                <li key={p.id} className="leading-snug">
                  <span className="amt">{fmtAmount(p.amount, p.asset)}</span> {p.asset} · {p.at ? fmtStamp(p.at) : '—'} · <span className="font-mono text-meta">{mask(p.from)}</span>{p.walletLabel ? ` · into ${p.walletLabel}` : ''}
                  {reasonLine(p) && <> — {firstSentence(reasonLine(p)).first}</>}{' '}
                  <button type="button" className="font-medium text-neg underline underline-offset-2 hover:text-tg-text" onClick={() => setTracing({ kind: 'payment', id: p.id })}>Trace it</button>
                </li>
              ))}
            </ul>
          ) : undefined}>
          <strong>{count(alerts.n, 'payment alert', 'payment alerts')}</strong>{alerts.usd > 0 && <> · <span className="amt">{dollars(alerts.usd)}</span></>} · last {windowChip(alerts.hours)} — {alerts.stop}
        </Slab>
      )}

      {/* Reads that failed: one line, red — never drawn like an empty screen. Both down is one banner. */}
      {bothDown && ovFail && incFail && (
        <Slab tone="bad" action={<Retry onClick={() => { void q.refetch(); void incoming.refetch(); }} />}
          more={<><p>Payments: {incFail.more}</p><p>System status: {ovFail.more}</p></>}>
          Nothing is read — {ovFail.why}.
        </Slab>
      )}
      {!bothDown && ovFail && (
        <Slab tone="bad" action={<Retry onClick={() => void q.refetch()} />} more={ovFail.more}>
          System status not read — {ovFail.why}.
        </Slab>
      )}
      {/* One "Reading…" is enough: the Payments view says its own while its read is in flight. */}
      {q.isLoading && !(view === 'payments' && incoming.isLoading) && <Pending what="Reading the screener…" />}

      {d && (
        <>
          {/* The one banner that matters: the onboarding brain is down. */}
          {alarm && (
            <Slab tone="bad" more={alarm.rest || undefined}><strong>Screening brain is DOWN</strong> — {alarm.first}</Slab>
          )}
          {night.why && !d.meta.alarm && (
            <Slab tone="bad" more={night.why}><strong>Nothing was checked last night</strong> — {night.short}</Slab>
          )}
          {budgetOut && !d.meta.alarm && (
            <Slab tone="warn" more="The nightly check and the transfers read both wait for the day's credits.">Arkham credits spent — checks pause until 04:00 Dubai.</Slab>
          )}
        </>
      )}

      {/* The views. The chip row is the house's tab row; a view's trouble is said on its chip. */}
      <nav className="flex flex-wrap items-center gap-1.5" aria-label="KYT views">
        {VIEWS.map((v) => (
          <button key={v.key} type="button" className={`chip ${view === v.key ? 'chip-on' : ''}`} aria-pressed={view === v.key}
            onClick={() => setSearch(v.key === 'payments' ? {} : { view: v.key }, { replace: true })}>
            {v.label}
            {v.key === 'payments' && (incoming.isError
              ? <span className="text-neg"> · not read</span>
              : alerts && <span className="text-neg"> · {count(alerts.n, 'alert', 'alerts')}</span>)}
            {v.key === 'wallets' && notRead > 0 && <span className="text-warn"> · {whole(notRead)} not read</span>}
            {v.key === 'wallets' && notWatched > 0 && <span className="text-warn"> · {whole(notWatched)} not watched</span>}
            {v.key === 'wallets' && d?.books_error && <span className="text-warn"> · register not read</span>}
            {v.key === 'system' && broken > 0 && <span className="text-neg"> · {whole(broken)} not working</span>}
          </button>
        ))}
      </nav>

      {view === 'payments' && !bothDown && <PaymentsView q={incoming} hours={hours} onHours={setHours} onTrace={(id) => setTracing({ kind: 'payment', id })} />}
      {view === 'wallets' && !bothDown && <WalletsView d={d} ov={q} q={incoming} t={treasury} onTrace={openWallet} />}
      {view === 'system' && !bothDown && (d
        ? <SystemView d={d} strip={strip} night={night} senderWhole={senderCheckWhole(senderItem(d, nowMs))} inc={incoming.data} incFailed={incoming.isError} />
        : q.isError && <SectionNote tone="bad" title="System"><StateLine tone="bad" action={<Retry onClick={() => void q.refetch()} />}>Not read.</StateLine></SectionNote>)}

      {tracedPayment && <PaymentTrace p={tracedPayment} gl={glSenderNames(incoming.data?.payments ?? [])(tracedPayment)} onClose={() => setTracing(null)} />}
      {tracing?.kind === 'wallet' && (
        <WalletTrace
          chain={tracing.chain}
          address={tracing.address}
          desk={incoming.data?.wallets.find((w) => sameWallet(w, tracing))}
          watched={d?.wallets.find((w) => sameWallet(w, tracing))}
          books={d?.books_wallets?.find((w) => sameWallet(w, tracing))}
          trace={trace}
          onClose={() => setTracing(null)}
        />
      )}
    </div>
  );
}

// ── The System view: is the screening itself working ────────────────────────
function SystemView({ d, strip, night, senderWhole, inc, incFailed }: { d: KytOverview; strip: StripItem[] | null; night: Night; senderWhole: boolean; inc: KytIncoming | undefined; incFailed: boolean }) {
  const stray = d.unwatched_screened ?? [];
  // "Flags 0" and "Nothing flagged." are claims over what was screened — the desk's wallets by the night, and the senders by
  // the sender check: over a night that looked at a part of the wallets, at none, or that is off, or a sender check that is on
  // and did not screen every sender, the zero is amber and the list says what it covers (22 Oct 2026, two rounds). With no
  // wallet to check there is nothing to look at, and the plain words stand.
  const lookedAtAll = (night.tone === 'good' || night.value === 'Nothing to check') && senderWhole;
  return (
    <>
      {/* The Books stopped watching these; the screener did not hear of it (the operator, 6 Oct 2026:
          archive = stop watching). Said until it is fixed: /unwatch in the ops group. */}
      {stray.length > 0 && (
        <Slab tone="warn" more="The Books stopped watching it, and the screener did not hear. Send /unwatch with the address in the ops group."
          below={(
            <div className="mt-1.5 space-y-1">
              {stray.map((u) => (
                <div key={`${u.chain}:${u.address}`} className="flex flex-wrap items-baseline gap-x-2 text-tg-text">
                  <span className={CHAIN}>{u.chain}</span>
                  <Address address={u.address} chain={u.chain} />
                  {u.label && <span className="text-tg-hint">{u.label}</span>}
                </div>
              ))}
            </div>
          )}>
          {count(stray.length, 'address', 'addresses')} still screened — send <span className="font-mono">/unwatch</span>.
        </Slab>
      )}
      {/* Three tiles — the clients awaiting approval are under Onboarding → New leads (7 Oct 2026). */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
        <Stat label="Checked last night" value={night.value} tone={night.tone} />
        {/* No flags is not green: nothing is flagged when nothing is checked, too. */}
        <Stat label="Flags" value={d.flags.length} tone={d.flags.length || !lookedAtAll ? 'warn' : undefined} />
        {/* The register unread: how many are watched is known, of how many is not — said, and amber, never a bare count. */}
        <Stat label="Wallets watched" value={d.coverage ? `${whole(d.coverage.watched)} of ${whole(d.coverage.treasury)}` : `${whole(d.wallets.length)} of ?`}
          tone={!d.coverage || d.coverage.watched < d.coverage.treasury ? 'warn' : undefined} />
      </div>
      {/* Who is answering — each provider's last REAL answer, never the
          last time a job ran. A BFF deployed before this screen sends no
          `providers`; the old hint line stands in for that minute. */}
      {strip ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-1.5 text-meta leading-snug">
            {strip.map((it) => (
              <div key={it.key} className="flex items-baseline gap-x-2 min-w-0">
                {/* A fixed seat for the state, so the names stand in a column — wide enough for "Not answering". */}
                <span className="w-28 shrink-0"><Pill tone={it.tone ?? PROVIDER_WORD[it.state].tone}>{it.word ?? PROVIDER_WORD[it.state].word}</Pill></span>
                <span className="min-w-0">
                  <span className="font-medium">{it.name}</span>
                  {it.note && <span className="text-tg-hint"> · {it.note}</span>}
                </span>
              </div>
            ))}
          </div>
          {strip.some((it) => it.detail && it.detail !== it.note) && (
            <Disclosure label="Provider details">
              {strip.filter((it) => it.detail && it.detail !== it.note).map((it) => <p key={it.key}><span className="text-tg-text">{it.name}</span> · {it.detail}</p>)}
            </Disclosure>
          )}
        </>
      ) : (
        <>
          <p className="text-meta text-tg-hint">
            OFAC lists {d.meta.ofac ?? '—'}
            {' · '}Nightly sweep {d.meta.sweep ? fmtRelative(d.meta.sweep) : '—'}
            {' · '}GL Vision {d.spend.gl}{d.spend.glBudget != null && ` of ${d.spend.glBudget}`}
          </p>
          <Disclosure label="Provider details"><p>Counterparties checked {d.meta.cpSweep ? fmtRelative(d.meta.cpSweep) : '—'}.</p></Disclosure>
        </>
      )}

      {inc
        ? <RulesInForce inc={inc} />
        : incFailed && <SectionNote tone="warn" title="The rules in force"><StateLine tone="warn">Rules not read — the payments read failed.</StateLine></SectionNote>}

      {/* ── Flags ────────────────────────────────────────────────────── */}
      <section className="card p-0 overflow-hidden">
        <header className={SECTION_HEAD}>
          <h2 className="font-semibold text-body">Flagged wallet checks</h2>
          {/* The treasury's checks only — the screener's own (the worker's deskFlags): an applicant's wallet check is the lead's,
              under Onboarding (the operator, 7 Oct 2026: "KYT doesn't need to show leads/customers kyt data"). What is flagged
              is said in About this screen. */}
          <span className="text-meta text-tg-hint">newest 12</span>
        </header>
        {d.flags.length === 0 ? (
          <EmptyState>{lookedAtAll ? 'Nothing flagged.' : 'Nothing flagged in what was checked.'}</EmptyState>
        ) : (
          <>
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-body">
                <thead className="text-label uppercase tracking-wide text-tg-hint">
                  <tr>
                    <th className="text-left px-3 py-1.5">When</th>
                    <th className="text-left px-3 py-1.5">Chain</th>
                    <th className="text-left px-3 py-1.5">Address</th>
                    <th className="text-left px-3 py-1.5">Source</th>
                    <th className="text-left px-3 py-1.5">Band</th>
                    <th className="text-right px-3 py-1.5">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {d.flags.map((f, i) => (
                    <tr key={`${f.address}:${f.created_at}:${i}`} className="border-t border-stroke-soft">
                      <td className="px-3 py-1.5 whitespace-nowrap text-tg-hint" title={fmtStamp(f.created_at)}>{fmtRelative(f.created_at)}</td>
                      <td className="px-3 py-1.5 font-mono tabular text-meta">{f.chain}</td>
                      <td className="px-3 py-1.5"><Address address={f.address} chain={f.chain} /></td>
                      <td className="px-3 py-1.5">{providerName(f.provider)}</td>
                      <td className="px-3 py-1.5"><BandPill band={f.risk_band} looked /></td>
                      <td className="px-3 py-1.5 text-right font-mono tabular">{f.risk_score ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* The phone: band · address, the score at the right; then
                when · chain · source. The chain sits on line 2 as it does
                on the watched card: with it on line 1 the widest case
                (an unscreened pill, ETHEREUM, three icons, a 100) met the
                375px edge and the nowrap address ran into the score. */}
            <div className="lg:hidden">
              {d.flags.map((f, i) => (
                <div key={`${f.address}:${f.created_at}:${i}`} className={CARD}>
                  <div className={LEDGER.cardLine1}>
                    <BandPill band={f.risk_band} looked />
                    <span className="flex-1 min-w-0"><Address address={f.address} chain={f.chain} /></span>
                    <span className="amt shrink-0">{f.risk_score ?? '—'}</span>
                  </div>
                  <div className={CARD_LINE2}>
                    <span className={LEDGER.whenText} title={fmtStamp(f.created_at)}>{fmtRelative(f.created_at)}</span>
                    <span className={CHAIN}>{f.chain}</span>
                    <span className="text-tg-hint">{providerName(f.provider)}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

    </>
  );
}

// ── The Books' register, as a table at lg+ and cards below ──────────────────
/** `kind`: the Kind column stands only where more than one class is listed together.
 *  `tail`: a second block under a header line of its own — in the SAME table at lg+, so its columns stand under the first's. */
function RegisterRows({ rows, kind, onTrace, tail, folded = [] }: {
  rows: KytBooksWallet[]; kind: boolean; onTrace: (chain: string, address: string) => void;
  tail?: { head: React.ReactNode; rows: KytBooksWallet[] };
  folded?: FoldedPot[];
}) {
  const all = [...rows, ...(tail?.rows ?? [])];
  // The Label column stands only where some wallet has a label that is more than its name (6 Oct 2026: it was a column of dashes).
  const labels = all.some((w) => !!w.label && w.label !== partyName({ display_name: w.display_name, slug: w.slug }));
  // Watched and Recorded risk stand only when they tell one row from another (7 Oct 2026, "minimise the clutter"):
  // a column of "watched" under "3 of 3 watched", or of "no score", says nothing the header does not.
  const watchCol = all.some((w) => !w.watched);
  const riskCol = all.some((w) => !!w.band || w.risk_score != null);
  const span = (kind ? 8 : 7) - (labels ? 0 : 1) - (watchCol ? 0 : 1) - (riskCol ? 0 : 1);
  const body = (list: KytBooksWallet[]) => list.map((w) => <BooksRow key={`${w.chain}:${w.address}`} w={w} kind={kind} labels={labels} watchCol={watchCol} riskCol={riskCol} onTrace={() => onTrace(w.chain, w.address)} />);
  return (
    <>
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full text-body">
          <thead className="text-label uppercase tracking-wide text-tg-hint">
            <tr>
              <th className="text-left px-3 py-1.5">Party</th>
              {kind && <th className="text-left px-3 py-1.5">Kind</th>}
              <th className="text-left px-3 py-1.5">Chain</th>
              <th className="text-left px-3 py-1.5">Address</th>
              {labels && <th className="text-left px-3 py-1.5">Label</th>}
              {watchCol && <th className="text-left px-3 py-1.5">Watched</th>}
              {riskCol && <th className="text-left px-3 py-1.5">Recorded risk</th>}
              <th className="text-right px-3 py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {body(rows)}
            {folded.map((f) => (
              <tr key={`pot:${f.key}`} className="border-t border-stroke-soft">
                <td className="px-3 py-1.5 font-medium">{f.name}</td>
                <td className="px-3 py-1.5 font-mono tabular text-meta">{f.chains.join(' · ')}</td>
                <td colSpan={span - 3} className="px-3 py-1.5 text-meta">
                  {whole(f.n)} addresses · <span className={f.watched < f.n ? 'text-warn' : 'text-tg-hint'}>{f.watched === 0 ? 'none watched' : `${whole(f.watched)} of ${whole(f.n)} watched`}</span>
                </td>
                <td className="px-3 py-1.5 text-right"><button type="button" className="btn-link" onClick={f.onOpen}>Show them</button></td>
              </tr>
            ))}
            {tail && (
              <>
                <tr><td colSpan={span} className="px-3 pt-3 pb-1 border-t border-stroke-soft">{tail.head}</td></tr>
                {body(tail.rows)}
              </>
            )}
          </tbody>
        </table>
      </div>
      <div className="lg:hidden">
        {rows.map((w) => <BooksCard key={`${w.chain}:${w.address}`} w={w} kind={kind} onTrace={() => onTrace(w.chain, w.address)} />)}
        {folded.map((f) => (
          // Two lines: the name is what tells one pot from the next — beside the count it was cut to "Hot wallet…".
          <div key={`pot:${f.key}`} className={`${CARD} min-w-0 text-body leading-snug`}>
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="min-w-0 break-words font-medium">{f.name}</span>
              <button type="button" className="btn-link ml-auto shrink-0" onClick={f.onOpen}>Show them</button>
            </div>
            <div className="text-meta text-tg-hint">
              {f.chains.join(' · ')} · {whole(f.n)} addresses · <span className={f.watched < f.n ? 'text-warn' : undefined}>{f.watched === 0 ? 'none watched' : `${whole(f.watched)} of ${whole(f.n)} watched`}</span>
            </div>
          </div>
        ))}
        {tail && <div className="px-3 pt-3 pb-1 border-t border-stroke-soft">{tail.head}</div>}
        {tail?.rows.map((w) => <BooksCard key={`${w.chain}:${w.address}`} w={w} kind={kind} onTrace={() => onTrace(w.chain, w.address)} />)}
      </div>
    </>
  );
}

/** How many retired addresses show before "Show all": the ones nobody watches come first, so these are the exceptions. */
export const RETIRED_SHOWN = 8;

/** The treasury's register: this month's wallets, then every retired address — the register ran to forty rows of
 *  addresses nobody watches, under three wallets that matter. The retired follow under one header that counts
 *  them (how many are watched), the unwatched first, and only a few at a time: the exceptions are on the page,
 *  the rest a press away (6 Oct 2026). */
/** A pot with more addresses than this is one line in the register until opened: the live page listed a Bitcoin
 *  pot's sixteen addresses and My Ledger's eighteen, row under row, each "not watched" (7 Oct 2026). */
export const POT_ROWS_SHOWN = 3;

/** One pot folded to a line: its name, networks, how many addresses and how many are watched. */
export interface FoldedPot { key: string; name: string; chains: string[]; n: number; watched: number; onOpen: () => void }

/** The register's layout: this month's wallets — a pot with more than POT_ROWS_SHOWN addresses folded to one line until
 *  opened — then the retired addresses, the unwatched first and each group newest-retired first, RETIRED_SHOWN at a time. */
export function registerLayout(rows: KytBooksWallet[], opened: string[], all: boolean): {
  active: KytBooksWallet[]; folded: Array<Omit<FoldedPot, 'onOpen'>>; retired: KytBooksWallet[]; shown: KytBooksWallet[]; watchedRetired: number;
} {
  const potKey = (w: KytBooksWallet): string => w.treasury_slug ?? w.slug;
  const byPot = new Map<string, KytBooksWallet[]>();
  for (const w of rows.filter((x) => x.status !== 'archived')) byPot.set(potKey(w), [...(byPot.get(potKey(w)) ?? []), w]);
  const folded = [...byPot.entries()]
    .filter(([k, ws]) => ws.length > POT_ROWS_SHOWN && !opened.includes(k))
    .map(([k, ws]) => ({
      key: k, name: partyName({ display_name: ws[0]!.display_name, slug: ws[0]!.slug }), chains: [...new Set(ws.map((w) => w.chain))],
      n: ws.length, watched: ws.filter((w) => w.watched).length,
    }));
  const active = rows.filter((w) => w.status !== 'archived' && !folded.some((f) => f.key === potKey(w)));
  const retired = rows.filter((w) => w.status === 'archived')
    .sort((a, b) => Number(!!a.watched) - Number(!!b.watched) || (b.retired_at ?? '').localeCompare(a.retired_at ?? ''));
  return { active, folded, retired, shown: all ? retired : retired.slice(0, RETIRED_SHOWN), watchedRetired: retired.filter((w) => w.watched).length };
}

function TreasuryRegister({ rows, onTrace }: { rows: KytBooksWallet[]; onTrace: (chain: string, address: string) => void }) {
  const [all, setAll] = useState(false);
  const [opened, setOpened] = useState<string[]>([]);
  const layout = registerLayout(rows, opened, all);
  const folded: FoldedPot[] = layout.folded.map((f) => ({ ...f, onOpen: () => setOpened((o) => [...o, f.key]) }));
  const { active, retired, shown, watchedRetired: watched } = layout;
  const head = (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-label uppercase tracking-wide">
      <span className="font-medium text-tg-hint">Retired addresses · {whole(retired.length)}</span>
      <span className={`normal-case tracking-normal text-meta ${watched < retired.length ? 'text-warn' : 'text-tg-hint'}`}>{whole(watched)} of {whole(retired.length)} watched</span>
    </div>
  );
  return (
    <>
      <RegisterRows rows={active} kind={false} onTrace={onTrace} folded={folded} tail={retired.length > 0 ? { head, rows: shown } : undefined} />
      {retired.length > RETIRED_SHOWN && (
        <div className="px-3 py-2 border-t border-stroke-soft">
          <button type="button" className="btn-link" onClick={() => setAll(!all)}>{all ? 'Show fewer' : `Show all ${whole(retired.length)}`}</button>
        </div>
      )}
    </>
  );
}

// ── Holdings: what each treasury pot holds (the operator, 7 Oct 2026) ──

/** The treasury read, as the Wallets view needs it. */
export type TreasuryRead = { data?: TreasuryBalancesResponse; isLoading: boolean; isError: boolean; error: unknown };

/** One treasury pot as the register knows it: its balance line in the Books, its name, its networks. */
export interface Pot { slug: string; name: string; chains: string[] }

/** The register's treasury wallets as pots — one per balance line (`treasury_slug`), however many addresses it has. */
export function potsOf(rows: KytBooksWallet[]): Pot[] {
  const pots = new Map<string, Pot>();
  for (const w of rows) {
    if (w.party_type !== 'own_account' || !w.treasury_slug) continue;
    const p = pots.get(w.treasury_slug) ?? { slug: w.treasury_slug, name: partyName({ slug: w.slug, display_name: w.display_name }), chains: [] };
    if (!p.chains.includes(w.chain)) p.chains.push(w.chain);
    pots.set(w.treasury_slug, p);
  }
  return [...pots.values()];
}

/** What one pot holds by the Books' lines, and its roll-up when the rate book could price it. */
export interface PotHolding {
  /** `networks`: where THIS asset sits — a coin of one network by the coin, else by the Books' line it is held on (its slug's
   *  network token), in TERMINOLOGY's spellings; empty when neither says. The pot's own networks are its addresses', which is
   *  not the same thing (a Ledger pot registered by its BTC addresses holds ETH, TRX, APT and GRAM too — 3 Nov 2026). */
  assets: Array<{ asset: string; balance: number; outside: TreasuryOutside | null; networks: string[] }>;
  value: TreasuryRailValue | null;
}

/** Where a coin can live, its home network first: ETH on Ethereum or Base, the others on one network each. A stablecoin
 *  lives on any network, so its line says. */
const COIN_NETWORKS: Readonly<Record<string, readonly string[]>> = { BTC: ['BTC'], ETH: ['ERC20', 'Base'], BNB: ['BEP20'], TRX: ['TRC20'], APT: ['Aptos'], GRAM: ['TON'], SOL: ['SOL'] };
/** The network of a holding: the line's own network (`hot.trc` → TRC20, `hot.base` → Base) when the coin can live there —
 *  ETH on `hot.base` is on Base (Astra, 3 Nov 2026) — else the coin's home network (ETH on `ledger.btc` is on Ethereum,
 *  not BTC); null when neither says (USDT on `cold.ledger`). */
export function holdingNetwork(asset: string, slug: string | null | undefined): string | null {
  const token = (slug ?? '').split('.').slice(1).find(isNetwork);
  const line = token ? networkWord(token) : null;
  const can = COIN_NETWORKS[foldAsset(asset).toUpperCase()];
  if (!can) return line;
  return line && can.includes(line) ? line : can[0]!;
}

/**
 * The treasury read, per pot. The QUANTITIES come from the balance lines — always in the answer, whichever way a
 * group is laid out (a rail's `balances`, or an asset line's `sources`). The VALUES come from the per-rail roll-up,
 * which the BFF builds only when the rate book reads: taken from the roll-up alone, a failed rate book would show
 * every pot as holding nothing.
 */
export function holdingsBySlug(resp: TreasuryBalancesResponse): Map<string, PotHolding> {
  const out = new Map<string, PotHolding>();
  const pot = (slug: string): PotHolding => {
    const h = out.get(slug) ?? { assets: [], value: null };
    out.set(slug, h);
    return h;
  };
  const add = (slug: string, asset: string, balance: number, outside: TreasuryOutside | null | undefined) => {
    const h = pot(slug);
    const net = holdingNetwork(asset, slug);
    // One coin, however the Books spell it (USDT, USDT-TRC20 …): the asset contract's fold.
    const a = h.assets.find((x) => sameAsset(x.asset, asset));
    if (!a) {
      h.assets.push({ asset, balance, outside: outside ?? null, networks: net ? [net] : [] });
      return;
    }
    a.balance += balance;
    if (net && !a.networks.includes(net)) a.networks.push(net);
    if (outside) a.outside = a.outside ? { ...a.outside, amount: a.outside.amount + outside.amount } : outside;
  };
  for (const g of resp.groups ?? []) {
    for (const l of g.lines) {
      if (l.slug) for (const x of l.balances ?? []) add(l.slug, x.asset, x.balance, x.outside);
      if (l.asset) for (const x of l.sources ?? []) add(x.slug, l.asset, x.balance, x.outside);
    }
    for (const r of g.rails ?? []) pot(r.slug).value = r;
  }
  return out;
}

/**
 * What each treasury pot holds — the Books' own figures, the Treasury tab's (the operator, 7 Oct 2026: "show holding
 * amounts in each treasury balances"). Per POT, not per address: a pot's addresses (a month's rotation, a Bitcoin
 * pot's sixteen) are ONE balance in the Books, so a figure on each address would say it sixteen times. Booked
 * amounts; money on chain that is not booked yet is said beside them and never added in, as on Wallets. A read that
 * failed says so — never a row of zeros — and with the rate book unread there are quantities, no values.
 */
/** The holdings as drawn, from the treasury read: each pot with what it holds (value-ordered), whether the read is usable
 *  and priced, the total — only when every pot holding something is priced in full (a part that reads as a whole
 *  under-reports) — and the stated-price caveat, dated by the oldest stated price, said once. */
export function holdingsOf(pots: Pot[], t: TreasuryRead): { readable: boolean; priced: boolean; rows: Array<{ p: Pot; h: PotHolding | null }>; total: number | null; statedAt: string[]; oldestStated: string | null } {
  const readable = !!t.data?.groups;
  const held = readable ? holdingsBySlug(t.data!) : new Map<string, PotHolding>();
  const priced = readable && t.data!.valuation?.available === true;
  const rows = pots
    .map((p) => ({ p, h: held.get(p.slug) ?? null }))
    .sort((a, b) => (b.h?.value?.value_usdt_priced ?? -1) - (a.h?.value?.value_usdt_priced ?? -1) || a.p.name.localeCompare(b.p.name));
  const holding = rows.filter(({ h }) => heldOf(h).length > 0);
  const statedAt = priced ? holding.flatMap(({ h }) => (h!.value?.stated_sources.length ? [h!.value.stated_as_of ?? ''] : [])) : [];
  const oldestStated = statedAt.filter(Boolean).sort()[0] ?? null;
  const total = priced && holding.length > 0 && holding.every(({ h }) => h!.value?.value_usdt != null)
    ? holding.reduce((n, { h }) => n + (h!.value!.value_usdt ?? 0), 0) : null;
  return { readable, priced, rows, total, statedAt, oldestStated };
}

export function Holdings({ pots, t, registerRead = true }: { pots: Pot[]; t: TreasuryRead; /** The Books' register was read: no pot is then "none registered", it is unknown. */ registerRead?: boolean }) {
  const { readable, priced, rows, total, statedAt, oldestStated } = holdingsOf(pots, t);
  return (
    <section className="card p-0 overflow-hidden">
      <header className={SECTION_HEAD}>
        <h2 className="font-semibold text-body">Holdings</h2>
        {total != null && (
          <span className="text-meta text-tg-hint">
            <span className="font-mono tabular text-tg-text">≈ {fmtAmount(total, 'USDT', 0)} USDT</span> in all
            {statedAt.length > 0 && <> · partly at stated prices{oldestStated ? ` (${fmtDay(oldestStated)})` : ''} — an estimate</>}
          </span>
        )}
      </header>
      {t.isError && (() => {
        const said = firstSentence(errorWords(t.error));
        return <div className="p-3"><StateLine tone="warn" more={said.rest || undefined}>Couldn&apos;t read the treasury&apos;s holdings — {said.first}</StateLine></div>;
      })()}
      {t.isLoading && !t.data && <div className="p-3"><Pending inline what="Reading the treasury…" /></div>}
      {t.data && !readable && <div className="p-3"><StateLine tone="warn">Holdings unreadable here — they are on Wallets.</StateLine></div>}
      {readable && !priced && <p className="px-3 pt-2 text-meta text-warn">The rate book could not be read — quantities only, no values.</p>}
      {/* No pots because the register was not read is not "none registered": the pots are unknown. */}
      {readable && pots.length === 0 && !registerRead && <div className="p-3"><StateLine tone="warn">Register not read — holdings unknown.</StateLine></div>}
      {readable && pots.length === 0 && registerRead && <EmptyState>No wallet registered in the Books.</EmptyState>}
      {readable && pots.length > 0 && (
        <div className="divide-y divide-stroke-soft">
          {rows.filter(({ h }) => !emptyPot(h)).map(({ p, h }) => <HoldingRow key={p.slug} p={p} h={h} priced={priced} />)}
          {/* The pots holding nothing: one line, not a row each (7 Oct 2026, "minimise the clutter"). */}
          {rows.some(({ h }) => emptyPot(h)) && (
            <p className="px-3 py-2 text-meta text-tg-hint">Nothing held: {rows.filter(({ h }) => emptyPot(h)).map(({ p }) => p.name).join(', ')}</p>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Dust: below a coin's own floor (0.5 USDT, 0.0001 BTC — the console's one table), or worth under half a dollar
 * by the rate book. The default floor of one whole coin is NOT applied: it would hide half a BNB.
 */
function isDust(asset: string, amount: number, valueUsdt: number | null | undefined): boolean {
  const floor = dustFor(asset);
  if (floor < DUST_DEFAULT && Math.abs(amount) < floor) return true;
  return valueUsdt != null && Math.abs(valueUsdt) < 0.5;
}
/** What a pot holds that is more than dust, and what waits outside the books that is more than dust. */
export const heldOf = (h: PotHolding | null) => (h?.assets ?? []).filter((a) => a.balance !== 0 && !isDust(a.asset, a.balance, h?.value?.assets.find((x) => sameAsset(x.asset, a.asset))?.value_usdt));
export const outsideOf = (h: PotHolding | null) => (h?.assets ?? []).filter((a) => a.outside && a.outside.amount !== 0 && !isDust(a.asset, a.outside.amount, null));
/** A pot that holds nothing but dust on the books and has nothing waiting outside them. */
export const emptyPot = (h: PotHolding | null): boolean => heldOf(h).length === 0 && outsideOf(h).length === 0;

/** One pot: its name and networks, its value at the right; what it holds; then what waits on chain, unbooked. */
function HoldingRow({ p, h, priced }: { p: Pot; h: PotHolding | null; priced: boolean }) {
  const held = heldOf(h);
  const outside = outsideOf(h);
  const v = priced && held.length > 0 ? h?.value ?? null : null;
  return (
    <div className="px-3 py-2 text-body leading-snug">
      <div className="flex items-baseline gap-2 min-w-0">
        <span className="min-w-0 break-words font-medium">{p.name}</span>
        <span className="text-meta text-tg-hint shrink-0">{p.chains.join(' · ')}</span>
        {v && (v.value_usdt != null
          ? <span className="ml-auto font-mono tabular shrink-0">≈ {fmtAmount(v.value_usdt, 'USDT', 0)} USDT</span>
          : <span className="ml-auto font-mono tabular shrink-0 text-tg-hint">at least {fmtAmount(v.value_usdt_priced, 'USDT', 0)} USDT</span>)}
      </div>
      <div className="mt-0.5 font-mono tabular text-meta break-words">
        {held.length > 0
          ? held.map((a) => `${fmtAmount(a.balance, a.asset)} ${displayAsset(a.asset)}`).join(' · ')
          : <span className="font-sans text-tg-hint">Nothing on the books</span>}
      </div>
      {outside.map((a) => (
        <div key={a.asset} className="text-meta text-warn">+ {fmtAmount(a.outside!.amount, a.asset)} {displayAsset(a.asset)} {a.outside!.words}</div>
      ))}
      {v && v.unpriced.length > 0 && <div className="text-meta text-tg-hint">No price for {v.unpriced.map((a) => displayAsset(a)).join(', ')}</div>}
    </div>
  );
}

// ── The Wallets view: the treasury — what it holds, and what an exchange would see ──

function WalletsView({ d, ov, q, t, onTrace }: {
  d: KytOverview | undefined;
  /** The overview's read — the register and the watchlist come from it. */
  ov: { isError: boolean; refetch: () => unknown };
  q: { data?: KytIncoming; isLoading: boolean; isError: boolean; error: unknown; refetch: () => unknown };
  t: TreasuryRead;
  onTrace: (chain: string, address: string) => void;
}) {
  const inc = q.data;
  const books = d?.books_wallets ?? null;
  // The treasury alone: the KYT page is the treasury's (the operator, 7 Oct 2026) — the clients are under
  // Onboarding → New leads, and no client's or merchant's wallet is on this page.
  const rows = (books ?? []).filter((w) => w.party_type === 'own_account');
  const others = inc && d ? d.wallets.filter((w) => !inc.wallets.some((x) => sameWallet(x, w))) : [];
  // A watched wallet that is not one of this month's desk wallets is somewhere: a treasury wallet in the register
  // below; a client's or merchant's is not the KYT page's (7 Oct 2026). What stays HERE is the watched wallet the
  // Books do not know at all — nobody registered it (6 Oct 2026: "also watched" listed the same wallets again, and
  // the clients' with them). With the Books unread nothing can be told apart, so every one stays, as before.
  const strays = books ? others.filter((w) => !books.some((b) => sameWallet(b, w))) : others;
  const failed = q.isError ? readFailure(q.error) : null;
  // The overview unread: its cards are said as not read — never left out as though there were nothing to show.
  const ovLine = (title: string) => (
    <SectionNote tone="bad" title={title}><StateLine tone="bad" action={<Retry onClick={() => void ov.refetch()} />}>Not read.</StateLine></SectionNote>
  );
  return (
    <>
      {d ? <Holdings pots={potsOf(rows)} t={t} registerRead={books !== null} /> : ov.isError && ovLine('Holdings')}

      {/* ── This month's desk wallets ────────────────────────────────── */}
      <section className="card p-0 overflow-hidden">
        <header className={SECTION_HEAD}>
          <h2 className="font-semibold text-body">Desk wallets</h2>
          {inc ? <Coverage inc={inc} /> : q.isError ? <span className="text-meta text-warn">not read</span> : null}
        </header>
        {failed && <div className="p-3"><StateLine tone="bad" more={failed.more} action={<Retry onClick={() => void q.refetch()} />}>Desk wallets not read — {failed.why}.</StateLine></div>}
        {q.isLoading && <div className="p-3"><Pending inline what="Reading the desk wallets…" /></div>}
        {inc && inc.wallets.length === 0 && <div className="p-3"><NoneWatched /></div>}
        {inc && inc.wallets.length > 0 && (
          <div className="divide-y divide-stroke-soft">
            {inc.wallets.map((w) => <DeskWallet key={`${w.chain}:${w.address}`} w={w} hours={inc.hours} small={smallSaid(inc)} onTrace={() => onTrace(w.chain, w.address)} />)}
          </div>
        )}
        {/* A watched wallet that is not one of this month's desk wallets is still watched: it stays on the screen. */}
        {inc && strays.length > 0 && (
          <div className="border-t border-stroke-soft">
            <div className="px-3 pt-2 text-label uppercase tracking-wide text-tg-hint">
              {books ? 'Watched, not in the Books · nobody registered them' : 'Also watched · payments into them not read'}
            </div>
            {strays.map((w) => <WatchedCard key={`${w.chain}:${w.address}`} w={w} onTrace={() => onTrace(w.chain, w.address)} />)}
          </div>
        )}
        {/* Without the payments read, the watchlist still stands — as the overview has it. */}
        {!inc && d && (d.wallets.length === 0 ? (
          <div className="p-3"><NoneWatched /></div>
        ) : (
          <div>
            {d.wallets.map((w) => <WatchedCard key={`${w.chain}:${w.address}`} w={w} onTrace={() => onTrace(w.chain, w.address)} />)}
          </div>
        ))}
      </section>

      {/* ── The wallets the Books register ──────────────────────── */}
      {d ? (
        <section className="card p-0 overflow-hidden">
          <header className={SECTION_HEAD}>
            <h2 className="font-semibold text-body">Wallets in the Books</h2>
            <span className="flex flex-wrap items-baseline gap-x-3 text-meta text-tg-hint">
              {d.coverage && <span className={d.coverage.watched < d.coverage.treasury ? 'text-warn' : undefined}>{whole(d.coverage.watched)} of {whole(d.coverage.treasury)} watched</span>}
              {/* The legend only when its words are on the rows below. */}
              {(rows.some((w) => !w.watched) || !!d.coverage?.retired) && (
                <span>{joined([rows.some((w) => !w.watched && !w.freeze_watched) && 'not watched = nobody screens it', rows.some((w) => !w.watched && w.freeze_watched) && 'freeze watch only = frozen coins watched, payments not read', !!d.coverage?.retired && 'retired = still paid into'])}</span>
              )}
            </span>
          </header>
          {/* The register's own failure is the Books', and its text a database's: the line says what to do, never the raw error. */}
          {d.books_error && (
            <div className="p-3">
              <StateLine tone="warn" more="The register is read from the Books' database, and it did not answer. The wallets and their watch state are unknown until it does. If Refresh does not help, check that Docker is running on the desk Mac.">
                Register not read — the Books did not answer.
              </StateLine>
            </div>
          )}
          {books && rows.length === 0 && <EmptyState>No wallet registered in the Books.</EmptyState>}
          {rows.length > 0 && <TreasuryRegister rows={rows} onTrace={onTrace} />}
        </section>
      ) : ov.isError && ovLine('Wallets in the Books')}
    </>
  );
}

/** Nothing is on the watchlist: nothing is being read — said as that, in amber, never as a quiet day. */
function NoneWatched() {
  return (
    <StateLine tone="warn" more={<>In the ops group: <span className="font-mono">/watch TRC20 T… treasury</span></>}>
      No wallet watched — add one with <span className="font-mono">/watch</span>.
    </StateLine>
  );
}

// ── Incoming payments (5 Oct 2026) ───────────────────────────────────────────
// The operator: "What i need to keep a close eye on is — Scam/Blocked/Sanction —
// these kind of things on any incoming funds", "I also need to know if it
// appears on santions before depositing them to my CEX", and of this screen:
// "bring this to the KYT screen on books as well". So it shows what the KYT
// group is told: the answer before a deposit to an exchange, one desk wallet to
// a line; then every payment, the sanctions result in a column of its own, and
// its trace a press away. Every level, caveat and answer is the screening
// worker's (onboarding-agent src/kyt/incomingApi.ts) — these are only the words
// and the seats.
export type Hours = 24 | 72 | 168;
export const WINDOWS: Array<{ hours: Hours; chip: string; words: string }> = [
  { hours: 24, chip: '24 h', words: 'the last 24 hours' },
  { hours: 72, chip: '3 days', words: 'the last 3 days' },
  { hours: 168, chip: '7 days', words: 'the last 7 days' },
];
export const windowWords = (hours: number): string => WINDOWS.find((w) => w.hours === hours)?.words ?? `the last ${hours} hours`;
export const windowChip = (hours: number): string => WINDOWS.find((w) => w.hours === hours)?.chip ?? `${hours} h`;
/** The window's words, from the read's own answer: a range of Dubai days ("today", "yesterday", "26 Oct – 28 Oct"), else
 *  the last hours ("last 24 h"). The period picker sends days (28 Oct 2026); the hours stay for an older read. */
export function windowLabel(inc: { hours: number; range?: { from: string; to: string } | null } | undefined): string {
  if (!inc) return '';
  const r = inc.range;
  if (!r) return `last ${windowChip(inc.hours)}`;
  const today = dubaiDate();
  const DAY = 86_400_000;
  const yesterday = dubaiDate(Date.parse(`${today}T00:00:00+04:00`) - DAY);
  if (r.from === r.to) return r.from === today ? 'today' : r.from === yesterday ? 'yesterday' : fmtDay(`${r.from}T00:00:00+04:00`);
  return `${fmtDay(`${r.from}T00:00:00+04:00`)} – ${fmtDay(`${r.to}T00:00:00+04:00`)}${r.to === today ? ' (so far)' : ''}`;
}

export const LEVEL_WORD: Record<KytLevel, { word: string; tone: PillTone }> = {
  alert:      { word: 'Alert',         tone: 'bad' },
  watch:      { word: 'Watch',         tone: 'warn' },
  clean:      { word: 'Clean',         tone: 'good' },
  unscreened: { word: 'Not checked',   tone: 'neutral' },
  pending:    { word: 'Being checked', tone: 'neutral' },
  // Under the minimum, on no list (9 Oct 2026): by the operator's rule, not a failure — and not clean either.
  small:      { word: 'Small',         tone: 'neutral' },
};

/** A clean the screener could not fully vouch for is not a green one, and is
 *  not called clean: its row carries the name of the seat it is counted in —
 *  "Not fully checked", as the tier table, the chip and the header say — and
 *  its reason line says what was found and what could not be read. (It read
 *  "Clean, caveats" while every count around it said otherwise: review, 5 Oct 2026.) */
function LevelPill({ p }: { p: KytPayment }) {
  if (p.level === 'clean' && p.caveats.length > 0) return <Pill>Not fully checked</Pill>;
  const l = LEVEL_WORD[p.level];
  return <Pill tone={l.tone}>{l.word}</Pill>;
}

/** A dollar figure by the shared fiat rule (lib/format.ts), the unit after and no `$` (HOUSE-STYLE rule 10 v1.5, 30 Oct 2026:
 *  Telegram and screens alike) — no rule of this screen's own. */
export const dollars = (n: number): string => fmtMoney(n, 'USD');
/** A share of the funds as GL gives it, never squeezed to a whole number (the operator, 1 Nov 2026: "No rounding here"):
 *  at most two decimals, trailing zeros dropped — "14.98%", "1.2%", "10%"; a share that is not zero but rounds to nothing
 *  reads "under 0.01%", never 0%. The worker's pctWords is the same rule, so a message and the screen agree. */
export function share(pct: number): string {
  if (!(pct > 0)) return '0%';
  const text = pct.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 0 });
  return text === '0' ? 'under 0.01%' : `${text}%`;
}

/** How many sources a list of them stands for: a payment keeps its twenty largest and one line for the rest. */
const sourceCount = (list: KytExposure[]): number => list.reduce((a, e) => a + (e.n ?? 1), 0);

/** "scam 12% ≈ 5,400 USD (FakeShop, direct 1,350 USD)" — one source, with THIS payment's dollars. */
export function exposureWords(e: KytExposure): string {
  const where = e.directPct <= 0
    ? 'indirect'
    : e.directPct >= e.pct * 0.999
      ? 'direct'
      : `direct ${e.usd != null && e.pct > 0 ? dollars((e.usd * e.directPct) / e.pct) : share(e.directPct)}`;
  return `${(e.n ?? 1) > 1 ? `${whole(e.n ?? 1)} more sources` : e.label} ${share(e.pct)}${e.usd != null ? ` ≈ ${dollars(e.usd)}` : ''} (${e.owner ? `${e.owner}, ` : ''}${where})`;
}

/** "None found" never says more than was read: the caveat beside it — GL not asked under the minimum, its sources not read,
 *  or read in part (second review, 5 Oct 2026). Empty when the sources were all read. */
export const sanctionsCaveat = (p: KytPayment): string =>
  p.small ? `GL not asked, under ${dollars(p.small)}` : !p.sourcesRead ? "GL's sources not read" : p.partial ? "GL's sources read in part" : '';

/** The sanctions result of one payment in words: `head` is the pill's word and tone, `rest` what follows it — the words
 *  SanctionsResult draws in full, the trace's sanctions line and the v2 assessment's (one source for all three; the v2
 *  reference build of 27 Oct 2026 had copied them). A pending payment: not checked yet. */
export function sanctionsWords(p: KytPayment): { head: string; tone: PillTone; rest: string } {
  const s = p.sanctions;
  if (p.level === 'pending') return { head: 'Not checked yet', tone: 'neutral', rest: '' };
  if (s.state === 'listed') return { head: 'On a sanctions list', tone: 'bad', rest: s.listed ?? '' };
  if (s.state === 'listed_before') return { head: 'Listed before', tone: 'bad', rest: `at an earlier screening${s.listedBefore ? ` (${s.listedBefore})` : ''}` };
  if (s.state === 'trace') {
    // Sanctioned-party funds are the stricter kind; GL's "sanctioned exchange" is shown here and lined as a criminal source.
    // A sanctioned source GL listed with no share is a trace too, with no figure (fifth review, 6 Oct 2026).
    const blind = (p.unmeasured ?? []).filter((u) => u.sanctions);
    const party = s.exposures.some((e) => e.tier === 'sanctions') || blind.some((u) => u.tier === 'sanctions');
    const blindWords = blind.map((u) => `${u.label}${u.owner ? ` (${u.owner})` : ''} — not measured`);
    return { head: party ? 'Sanctioned-party funds' : 'Sanctions trace', tone: party ? 'bad' : 'warn', rest: [...s.exposures.map(exposureWords), ...blindWords].join(' · ') };
  }
  if (s.state === 'unread') return { head: 'Lists not read', tone: 'warn', rest: '' };
  return { head: 'None found', tone: 'neutral', rest: joined([sanctionsCaveat(p), s.oracleSilent && 'oracle silent']) };
}

/** The sanctions result of one payment: the first thing read on its row.
 *  `labelled` is for a phone card, which has no column header to say what
 *  "None found" was looked for (review, 5 Oct 2026). `bare`: the gap in GL's
 *  sources is said over this row — in the header of the group it stands in,
 *  counted — so the cell does not say it again (6 Oct 2026: on a real day it
 *  was printed on nearly every row). A row alone says it, as before. */
function SanctionsResult({ p, full = false, labelled = false, bare = false }: { p: KytPayment; full?: boolean; labelled?: boolean; bare?: boolean }) {
  const s = p.sanctions;
  if (p.level === 'pending') return <span className="text-tg-hint">—</span>;
  const w = sanctionsWords(p);
  if (s.state === 'listed') {
    return <><Pill tone={w.tone}>{w.head}</Pill>{full && w.rest && <span className="ml-1.5">{w.rest}</span>}</>;
  }
  if (s.state === 'listed_before') {
    return <><Pill tone={w.tone}>{w.head}</Pill>{full && <span className="ml-1.5">{w.rest}</span>}</>;
  }
  if (s.state === 'trace') {
    const blind = (p.unmeasured ?? []).filter((u) => u.sanctions);
    const first = s.exposures[0];
    if (full) return <><Pill tone={w.tone}>{w.head}</Pill><span className="ml-1.5">{w.rest}</span></>;
    if (!first) {
      return (
        <span className="inline-flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
          <Pill tone={w.tone}>{w.head}</Pill>
          <span>{blind[0]?.owner ?? blind[0]?.label} — not measured</span>
          {blind.length > 1 && <span className="text-tg-hint whitespace-nowrap">and {whole(blind.length - 1)} more</span>}
        </span>
      );
    }
    // In a row: the pill and the figures never break; the source's name wraps
    // under them rather than be cut ("Orbit Excha…" named nobody — review, 5 Oct 2026).
    return (
      <span className="inline-flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
        <Pill tone={w.tone}>{w.head}</Pill>
        {first && <span className="whitespace-nowrap">{share(first.pct)}{first.usd != null ? ` ≈ ${dollars(first.usd)}` : ''}</span>}
        {first?.owner && <span>{first.owner}</span>}
        {s.exposures.length > 1 && <span className="text-tg-hint whitespace-nowrap">and {whole(sourceCount(s.exposures.slice(1)))} more</span>}
      </span>
    );
  }
  if (s.state === 'unread') return <Pill tone={w.tone}>{w.head}</Pill>;
  const caveat = sanctionsCaveat(p);
  return (
    <span className="text-tg-hint">
      {labelled ? 'Sanctions: none found' : 'None found'}
      {/* "None found" never says more than was read: no sources, or a part of them (second review, 5 Oct 2026). */}
      {!bare && caveat && <> · {caveat}</>}
      {full && s.oracleSilent && <> · oracle silent</>}
    </span>
  );
}

export const ACTION_WORDS: Record<NonNullable<KytPayment['action']>, string> = {
  do_not_move: 'Do not move these funds, and do not deposit them to an exchange. Speak to your compliance officer today.',
  keep_apart:  'Keep these funds apart. Do not pay them out or deposit them to an exchange until you decide.',
  weigh_trace: 'No hold. Weigh the sanctions trace before you deposit these funds to an exchange.',
  recheck:     'Checked again later — the result follows in the KYT group.',
  by_hand:     'No automatic re-check — check this sender by hand.',
};

/** What to do, in the worker's words — with the re-check's time when it has one, and "this payment" (not its sender) for a
 *  payment checked on its own transaction (7 Oct 2026). Null when nothing is asked. */
export function actionWords(p: KytPayment): string | null {
  if (!p.action) return null;
  if (p.action === 'recheck' && p.recheckAt) return `Checked again after ${fmtStamp(p.recheckAt)} — the result follows in the KYT group.`;
  if (p.action === 'by_hand' && p.basis === 'tx') return 'No automatic re-check — check this payment by hand.';
  return ACTION_WORDS[p.action];
}

export const ALSO_WORDS: Record<NonNullable<KytPayment['also']>['standing'], string> = {
  blocked:  'a chat the bot blocked',
  declined: 'an applicant you declined',
  verified: 'a verified customer',
  pending:  'an applicant awaiting approval',
};
/** …and on a row, beside the payer's name. */
const ALSO_SHORT: Record<NonNullable<KytPayment['also']>['standing'], string> = {
  blocked:  'a blocked chat',
  declined: 'a declined applicant',
  verified: 'a verified customer',
  pending:  'an applicant awaiting approval',
};
export const alsoAdverse = (a: NonNullable<KytPayment['also']>): boolean => a.standing === 'blocked' || a.standing === 'declined';

export const STANDING_WORDS: Record<NonNullable<KytPayment['standing']>, string> = {
  verified:   'verified at onboarding',
  blocked:    'a chat the bot blocked for abuse or a scam attempt',
  declined:   'a declined applicant, refused at onboarding',
  pending:    'application awaiting approval',
  unfinished: 'onboarding not finished',
};

/** The reason on a payment's row, in one line: why it is a Watch or an Alert,
 *  what a Clean could not vouch for, or why there is no result. */
export function reasonLine(p: KytPayment): string {
  if (p.why) return p.why;
  if (p.level === 'clean' && p.caveats.length) return `Nothing found — but not vouched for: ${p.caveats.join(' · ')}`;
  if (p.level === 'unscreened') return p.notes.join(' · ') || 'No result';
  if (p.level === 'pending') return 'Read from the chain — its check runs within five minutes';
  if (p.level === 'small') return `Under your ${dollars(p.small ?? 0)} line: checked on the sanctions lists only, and on none of them. GL is not asked, so there is no deep check either.`;
  return '';
}

/** The reason on a row of the LIST. "Your alert line for this is $1,000." closed every Watch — the same line on row
 *  after row (the live page, 7 Oct 2026). The lines are under System; a row keeps what it found. The Trace panel and
 *  the KYT group's message keep the whole reason. */
export const rowReason = (p: KytPayment): string => reasonLine(p).replace(/\s*Your alert line for this is .+\.$/, '');

/** A Watch or an Alert keeps its level whatever its check did not cover — and says so, in the
 *  same words everywhere: the header and the wallet count these as "not fully checked either". */
export function notFullWords(p: KytPayment): string {
  return (p.kind === 'alert' || p.kind === 'watch') && p.unread.length > 0 ? `Not fully checked: ${p.unread.join(' · ')}` : '';
}

/** Who paid, beside the address: the onboarded customer — and a declined
 *  applicant in a WORD, never by colour alone (§7 rule 10). */
function PayerName({ p }: { p: KytPayment }) {
  // The party the Books booked it to leads (the operator, 28 Oct 2026); the chat's claim follows only when nothing is booked.
  if (p.party) return <span className="min-w-0 break-words text-meta text-tg-hint">{p.party.name}</span>;
  if (!p.customer) return null;
  // The chat of the other kind that named the wallet too — beside, never instead (anyone can name any wallet).
  const also = p.also && <span className={alsoAdverse(p.also) ? 'text-warn' : 'text-tg-hint'}> · also given by {ALSO_SHORT[p.also.standing]}</span>;
  if (p.standing === 'blocked') return <span className="min-w-0 break-words text-meta text-warn">Blocked chat · {p.customer}{also}</span>;
  if (p.standing === 'declined') return <span className="min-w-0 break-words text-meta text-warn">Declined applicant · {p.customer}{also}</span>;
  return <span className="min-w-0 break-words text-meta text-tg-hint">{p.customer}{also}</span>;
}

/** GL's name for a sender. GL's report on a payment's own transaction puts a share on the sender's address itself
 *  (its "Direct", depth 1), so an exchange's withdrawal wallet reads as that exchange — GL's own page says "Transfer …
 *  from Bitget" (the operator, 8 Oct 2026, beside a payment this table called "Unknown sender"). Named only when one
 *  owner holds the whole payment directly. A sender's own report (every payment rated before 7 Oct 2026) lists the
 *  wallets that paid the SENDER: a direct share there is one of those, never the sender. */
export type SenderName = { name: string; label: string };
export function glSenderOf(p: KytPayment): SenderName | null {
  if (p.basis !== 'tx' || !p.trace) return null;
  const [one, ...others] = p.trace.sources.filter((s) => s.owner && s.directPct >= 99.5);
  return one?.owner && others.length === 0 ? { name: one.owner, label: one.label } : null;
}
const senderKey = (p: KytPayment) => `${p.chain}:${p.from.startsWith('0x') ? p.from.toLowerCase() : p.from}`;
/** An address is one wallet on every payment: GL's name for it, from any payment's own report, names its other
 *  payments too — those rated on the sender's report included. */
export function glSenderNames(list: KytPayment[]): (p: KytPayment) => SenderName | null {
  const names = new Map<string, SenderName>();
  for (const p of list) {
    const n = glSenderOf(p);
    if (n && !names.has(senderKey(p))) names.set(senderKey(p), n);
  }
  return (p) => names.get(senderKey(p)) ?? null;
}
/** The sender GL names, with GL's type for it on hover. */
function GlSender({ n }: { n: SenderName }) {
  return <span className="text-tg-text" title={`GL names this address: ${n.name} (${n.label})`}>{n.name}</span>;
}

/** The places a payment is counted. "Not fully checked": no result, not judged yet — or judged clean with something
 *  the check could not cover. A clean that could not be vouched for is never in the green figure (the worker counts it;
 *  this is only its word and seat). "Small": under the minimum, checked on the sanctions lists only (9 Oct 2026). */
export const KINDS: Array<{ key: KytKind; word: string; text: string; bar: string }> = [
  { key: 'alert',     word: 'Alert',             text: 'text-neg',  bar: 'bg-neg' },
  { key: 'watch',     word: 'Watch',             text: 'text-warn', bar: 'bg-warn' },
  { key: 'clean',     word: 'Clean',             text: 'text-pos',  bar: 'bg-pos' },
  { key: 'unchecked', word: 'Not fully checked', text: '',          bar: 'bg-tg-hint' },
  { key: 'small',     word: 'Small, lists only', text: 'text-tg-hint', bar: 'bg-brand-muted' },
];

/** Whether "Small" is said on the KYT page: the minimum is set, or a payment was kept apart under it (9 Oct 2026). */
export function smallSaid(inc: KytIncoming): boolean {
  return (inc.rules.glMinUsd ?? 0) > 0 || inc.payments.some((p) => p.kind === 'small') || inc.wallets.some((w) => (w.month.by.small?.n ?? 0) + (w.window.by.small?.n ?? 0) > 0);
}

/** One level's share of a period: its dollars and how many payments. Nothing is
 *  a dash, never a coloured zero. */
function TierFigure({ t, k, plain = false }: { t: KytTally; k: (typeof KINDS)[number]; /** no colour of its own: what was read clean on a wallet that is not being read is no green figure */ plain?: boolean }) {
  const b = t.by[k.key];
  // An older worker counts no small payments: nothing, said as nothing.
  if (!b || b.n === 0) return <span className="text-tg-hint">—</span>;
  return (
    <span className={`whitespace-nowrap ${plain ? '' : k.text}`}>
      <span className="amt">{dollars(b.usd)}</span> <span className="text-tg-hint">({whole(b.n)})</span>
    </span>
  );
}

export const DEPOSIT_WORD: Record<KytDepositAnswer, { word: string; tone: PillTone }> = {
  listed:      { word: 'Do not deposit',          tone: 'bad' },
  party:       { word: 'Do not deposit',          tone: 'bad' },
  trace_alert: { word: 'Keep apart',              tone: 'bad' },
  other_alert: { word: 'Keep apart',              tone: 'bad' },
  trace:       { word: 'Weigh the trace',         tone: 'warn' },
  unchecked:   { word: 'Not all checked',         tone: 'neutral' },
  none:        { word: 'No sanctions appearance', tone: 'good' },
  no_payments: { word: 'No payments',             tone: 'neutral' },
  not_read:    { word: 'Not read',                tone: 'neutral' },
  not_reading: { word: 'Not known',               tone: 'warn' },
};

export const count = (n: number, one: string, many: string): string => `${whole(n)} ${n === 1 ? one : many}`;

/** What stands behind the answer, in words. `lead`: the strongest reason — ONE line, ten words at most. `shortfalls`: every count
 *  of what was not fully checked, said under one label on the row ("Not fully checked: 5 payments · 2 watches or alerts") — a
 *  shortfall count is never behind a tap. Behind Details: `explain`, what the second count means, and `rest`, the other
 *  counts (Astra, 6 and 22 Oct 2026). */
export function depositNote(w: KytIncomingWallet, answer: KytDepositAnswer = w.deposit.answer): { lead: string | null; shortfalls: string[]; explain: string | null; rest: string[] } {
  const d = w.deposit;
  const lead: Record<KytDepositAnswer, string | null> = {
    listed:      `${count(d.listed, 'sender is', 'senders are')} on a sanctions list`,
    party:       'sanctioned-party funds are in these payments — your compliance officer first',
    trace_alert: 'a payment with a sanctions trace is an alert',
    other_alert: `${count(d.alerts, 'payment is an alert', 'payments are alerts')} for another reason`,
    trace:       `${count(d.traces, 'payment carries', 'payments carry')} a sanctions trace, under your alert line`,
    // Small payments the only ones not sure: said as what they are — under the minimum, not traced (9 Oct 2026).
    unchecked:   d.unchecked === 0 && w.window.incomplete === 0 && (d.small ?? 0) > 0
      ? (d.small ?? 0) >= w.window.payments ? 'no sender listed — small payments are not traced on GL' : 'no sanctions appearance in the payments traced on GL'
      : d.unchecked >= w.window.payments ? 'none of these payments is fully checked yet' : 'no sanctions appearance in the payments that were fully checked',
    none:        null,
    // The quiet answer's days are said on the wallet's own line (AnswerRow `period`), never in a lead.
    no_payments: null,
    not_read:    'payments not read on this network — nothing here is checked',
    // The wallet's own state says it (readerTrouble), not a lead.
    not_reading: null,
  };
  const some = (xs: Array<string | null | false | undefined>): string[] => xs.filter((x): x is string => Boolean(x));
  const shortfalls = some([
    d.unchecked > 0 && count(d.unchecked, 'payment', 'payments'),
    // Beside the split, under its own words: a watch or an alert whose check did not cover everything stays where it is.
    w.window.incomplete > 0 && count(w.window.incomplete, 'watch or alert', 'watches or alerts'),
  ]);
  return {
    lead: lead[answer],
    shortfalls,
    explain: w.window.incomplete > 0
      ? 'A watch or an alert whose check did not cover everything keeps its level; it is counted beside the payments not fully checked.'
      : null,
    rest: some([
      d.answer !== 'trace' && d.traces > 0 && `${count(d.traces, 'payment', 'payments')} with a sanctions trace`,
      d.answer !== 'other_alert' && d.alerts > 0 && count(d.alerts, 'other alert', 'other alerts'),
      d.watch > 0 && `${whole(d.watch)} on watch for other reasons`,
      // In the split, never "not sure": the lists are the whole check the operator set for them (9 Oct 2026).
      (d.small ?? 0) > 0 && `${count(d.small ?? 0, 'small payment', 'small payments')} on the sanctions lists only`,
    ]),
  };
}

export const GL_BAND_TONE: Record<'low' | 'medium' | 'high', PillTone> = { low: 'good', medium: 'neutral', high: 'warn' };

/** Payments into this wallet are not being read in full: its network is not read at all, its reader is failing, stopped or
 *  has never read, or it stepped over a transfer. Said on the Wallets tab, in the header, and on the wallet's own line. */
export const paymentsUnread = (w: KytIncomingWallet): boolean =>
  !w.read || (!!w.reader && (w.reader.state !== 'ok' || !!w.reader.skipped));

/** The answer as it may be DRAWN. A stored "No payments", and the green "none", are claims over every payment, so neither is made
 *  for a wallet whose payments are not being read in full — a transfer stepped over, a reader never read, failing or stopped, a
 *  network not read: the answer is then not known ("Not known", with the reader's own state; "Not read" when the network is not
 *  read at all). Every other answer — above all a stronger, adverse one — is the worker's, drawn as it is (Astra, 22 Oct 2026). */
export function drawnAnswer(w: KytIncomingWallet): KytDepositAnswer {
  const a = w.deposit.answer;
  if ((a !== 'no_payments' && a !== 'none') || !paymentsUnread(w)) return a;
  return w.read ? 'not_reading' : 'not_read';
}

/** The payments the worker COUNTED — this month's or this window's, across the desk's wallets. The rows may be fewer (a list
 *  that was not read), never more. */
export function countedPayments(inc: KytIncoming): number {
  return inc.wallets.reduce((a, w) => a + Math.max(w.month.payments, w.window.payments), 0);
}

/** The alerts the worker COUNTED that no listed row accounts for — reconciled wallet by wallet and period by period: for each desk
 *  wallet, its tallied alerts of the month (and of the window) less the listed alerts OF THAT WALLET in THAT period, floor 0 — an
 *  alert of another wallet cannot account for it. A wallet's deficit is the larger of its two periods' (they overlap), the desk's
 *  the sum. `usd`: the tallied dollars, said only where every deficient wallet has none of its alerts listed; `some`: a deficient
 *  wallet has alert rows listed (the line then says "more"). A row with no time, or a period with no start, counts in: the
 *  benefit of the doubt goes to the list (Astra, 23 Oct 2026). */
export function unlistedAlerts(inc: KytIncoming): { n: number; usd: number | null; some: boolean } {
  const monthFrom = inc.monthStart ? Date.parse(inc.monthStart) : NaN;
  const windowFrom = inc.at && Number.isFinite(inc.hours) ? Date.parse(inc.at) - inc.hours * 3_600_000 : NaN;
  const inPeriod = (p: KytPayment, from: number, strict: boolean): boolean => {
    if (!Number.isFinite(from) || p.at == null) return true;
    const t = Date.parse(p.at);
    return strict ? t > from : t >= from;
  };
  let n = 0;
  let usd = 0;
  let partial = false;
  let some = false;
  for (const w of inc.wallets) {
    const mine = inc.payments.filter((p) => p.kind === 'alert' && sameWallet({ chain: p.chain, address: p.wallet }, w));
    const missing = Math.max(
      Math.max(0, w.month.by.alert.n - mine.filter((p) => inPeriod(p, monthFrom, false)).length),
      Math.max(0, w.window.by.alert.n - mine.filter((p) => inPeriod(p, windowFrom, true)).length),
    );
    if (missing === 0) continue;
    n += missing;
    if (mine.length > 0) { some = true; partial = true; } else usd += (w.month.by.alert.n >= w.window.by.alert.n ? w.month.by.alert : w.window.by.alert).usd;
  }
  return { n, usd: partial ? null : usd, some };
}

/** What an empty list of payments says. An empty month is decided from the tallies, not from the rows: payments that are
 *  counted and not listed are a list that was not read — never "No payments yet this month" beside a ring that counts them.
 *  And "No payments", or "none at this level", over a wallet that is not being read in full would be a quiet day it cannot
 *  vouch for: it says what was read (22 Oct 2026, two rounds). */
export function emptyPaymentsWords(inc: KytIncoming): string {
  const unread = inc.wallets.some(paymentsUnread);
  if (inc.payments.length === 0) {
    const counted = countedPayments(inc);
    if (counted > 0) return `${count(counted, 'payment', 'payments')} counted — list not read.`;
    return unread ? 'No payments read yet.' : 'No payments yet this month.';
  }
  return unread ? 'None read at this level.' : 'None at this level.';
}

/** What is wrong with a wallet's reader, one concise state each — red when it failed or stopped (a payment since is not on the
 *  list), amber when it has not read yet or stepped over a transfer — and, behind Details, the sentences the state stands on. */
export function readerTrouble(w: KytIncomingWallet): Array<{ tone: 'bad' | 'warn'; state: string; raw: string[] }> {
  const r = w.reader;
  if (!r) return [];
  // (No-break spaces: inside "42m ago" and before each dash, so when a state wraps a time is never split and a dash never opens a line.)
  const nb = (t: string): string => t.replace(/ /g, '\u00A0');
  const since = r.since ? nb(fmtRelative(r.since)) : null;
  const gone = 'A payment made since is not on this list yet.';
  const out: Array<{ tone: 'bad' | 'warn'; state: string; raw: string[] }> = [];
  if (r.state === 'never') out.push({ tone: 'warn', state: 'Not read yet\u00A0— what it received is unknown', raw: [] });
  if (r.state === 'failing') out.push({ tone: 'bad', state: `Reader failing${since ? ` since ${since}` : ''}\u00A0— newer payments not known`, raw: [r.failDetail, gone].filter((x): x is string => !!x) });
  if (r.state === 'stale') out.push({ tone: 'bad', state: `Last read ${since ?? 'long ago'}\u00A0— newer payments not known`, raw: [gone] });
  if (r.skipped) out.push({ tone: 'warn', state: `Transfers stepped over${r.skippedAt ? ` ${nb(fmtRelative(r.skippedAt))}` : ''}\u00A0— some payments not known`, raw: [r.skipped] });
  return out;
}

/** A wallet whose payments are not being read says so under its card, one line each, with Details for the sentence behind it
 *  (the Wallets view; the Payments view says it on the wallet's own answer row). */
function ReaderLines({ w }: { w: KytIncomingWallet }) {
  const r = w.reader;
  if (!r) return null;
  const since = r.since ? fmtRelative(r.since) : null;
  const gone = 'A payment made since is not on this list yet.';
  return (
    <>
      {r.state === 'never' && <StateLine tone="warn">Not read yet — what it received is unknown.</StateLine>}
      {r.state === 'failing' && <StateLine tone="bad" more={gone}>Not read{since ? ` since ${since}` : ''}{r.failDetail ? ` — ${r.failDetail}` : ''}</StateLine>}
      {r.state === 'stale' && <StateLine tone="bad" more={gone}>Last read {since ?? 'long ago'}</StateLine>}
      {r.skipped && <StateLine tone="warn">Transfers stepped over{r.skippedAt ? ` ${fmtRelative(r.skippedAt)}` : ''} — {r.skipped}</StateLine>}
    </>
  );
}

/** One desk wallet on the Wallets view: itself — its own score and what an
 *  exchange sees behind a deposit from it — and what it received, by level.
 *  The sources by name, and its transfers, are in its trace. */
function DeskWallet({ w, hours, small, onTrace }: { w: KytIncomingWallet; hours: number; small: boolean; onTrace: () => void }) {
  // "Small" is a seat only where it can be (review, 9 Oct 2026: a column of dashes on every wallet with no minimum set).
  const kinds = KINDS.filter((k) => k.key !== 'small' || small);
  const windowLabel = WINDOWS.find((x) => x.hours === hours)?.chip ?? `${hours} h`;
  const periods: Array<{ label: string; t: KytTally }> = [
    { label: windowLabel, t: w.window },
    { label: 'This month', t: w.month },
  ];
  // A wallet whose payments are not being read has no clean bill: what it did read as clean is not drawn green.
  const notWhole = paymentsUnread(w);
  // The date is on the claim itself — and so is "a part", when the sources read were one.
  const asRead = w.gl?.sourcesHeld && w.gl.sourcesAt ? ` as read ${fmtStamp(w.gl.sourcesAt)}` : '';
  const inPart = w.gl?.partial ? (asRead ? ' — a part of its sources' : ', in the sources held') : '';
  return (
    <div className="px-3 py-2.5 space-y-2 min-w-0">
      {/* The name and chain, Trace at the line's end; the address under the name on a phone (beside it, the name was cut).
          The name wraps rather than being cut: the month it ends in is what tells one wallet from the next. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
        <span className="flex items-baseline gap-2 min-w-0 flex-1">
          <span className="min-w-0 break-words text-body font-medium">{w.label ?? `${w.chain} wallet`}</span>
          <span className={`${CHAIN} shrink-0`}>{w.chain}</span>
        </span>
        <span className="shrink-0 max-lg:order-last max-lg:basis-full"><Address address={w.address} chain={w.chain} /></span>
        <span className="shrink-0"><TraceOpen onClick={onTrace} what={w.label ?? `the ${w.chain} wallet`} /></span>
      </div>

      {/* Two columns at lg+ (6 Oct 2026: the cards used half their width and ran three screens deep): the wallet
          itself on the left — what an exchange sees behind a deposit from it — and what it received on the right. */}
      <div className="grid gap-y-2 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)] lg:gap-x-8">
      <div className="text-meta space-y-1 min-w-0">
        {w.gl ? (
          <>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              {/* A low score GL gave with little data on the wallet is not a green pill. */}
              <Pill tone={w.gl.thin && w.gl.band === 'low' ? 'neutral' : GL_BAND_TONE[w.gl.band]}>{`GL ${Math.round(w.gl.score)} · ${w.gl.band}`}</Pill>
              {w.gl.at && <span className="text-tg-hint" title={fmtStamp(w.gl.at)}>scored {fmtRelative(w.gl.at)}</span>}
            </div>
            {/* What its funds are made of, at the table's own width; the sources by name are in its trace. */}
            {w.gl.trace && (
              <div className="space-y-1">
                <SourceBar t={w.gl.trace} />
                <SourceLegend t={w.gl.trace} />
              </div>
            )}
            {!w.gl.sourcesHeld
              ? <div className="text-tg-hint">GL&apos;s sources are not held for this wallet</div>
              : w.gl.bad.length > 0
                ? <div className="text-warn">Sanctioned or criminal sources in its funds{asRead}: {w.gl.bad.slice(0, 3).map((b) => `${b.label} ${share(b.pct)}${b.owner ? ` (${b.owner})` : ''}`).join(' · ')}{w.gl.bad.length > 3 ? ` · and ${w.gl.bad.length - 3} more` : ''}</div>
                : <div className="text-tg-hint">No sanctioned or criminal sources in its funds{asRead}{inPart}</div>}
            {/* A criminal source GL listed without a share is in no figure above. */}
            {(w.gl.unmeasured ?? []).length > 0 && <div className="text-warn">GL lists {namesWords(w.gl.unmeasured ?? [], 2)} among its sources with no share — not measured</div>}
            {w.gl.thin && <div className="text-warn">GL holds little data — its score is not a clean bill.</div>}
            {/* The score is the newest; its sources may be an older answer's. The date is on the claim itself (above); the reason is here, a tap away. */}
            {w.gl.sourcesHeld && w.gl.sourcesAt && (
              <Disclosure label="Why the sources are older">
                <p>{w.gl.newestUnreadable ? "The newest answer's list of sources could not be read" : 'The newest score came without its sources'}</p>
              </Disclosure>
            )}
          </>
        ) : (
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1"><Pill>Not scored</Pill><span className="text-tg-hint">GL has no score for this wallet yet</span></div>
        )}
      </div>

      <div className="min-w-0 space-y-2">
      {w.read ? (
        <>
          {/* What it received, by level: the levels that hold a payment, each with its dollars and count — a level with
              nothing in it is not a column of dashes. A level not fully checked or an alert is never left out. */}
          <div className="space-y-1.5 text-meta">
            {periods.map(({ label, t }) => {
              const levels = kinds.filter((k) => (t.by[k.key]?.n ?? 0) > 0);
              return (
                <div key={label}>
                  <div className="flex items-baseline gap-2">
                    <span className="w-20 shrink-0 text-tg-hint">{label}</span>
                    {/* "0 payments" is a count of what the reader read: over a wallet it did not read in full it is not known. */}
                    {t.payments === 0 && notWhole
                      ? <span className="text-warn">Not known</span>
                      : <span>{count(t.payments, 'payment', 'payments')}{t.usd > 0 && <> · <span className="amt">{dollars(t.usd)}</span></>}</span>}
                  </div>
                  {t.payments > 0 && levels.length > 0 && (
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 pl-[5.5rem]">
                      {levels.map((k) => (
                        <span key={k.key} className="whitespace-nowrap"><span className="text-tg-hint">{k.word}</span> <TierFigure t={t} k={k} plain={notWhole && k.key === 'clean'} /></span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {w.window.incomplete > 0 && (
            <p className="text-meta text-tg-hint">
              {count(w.window.incomplete, 'watch or alert', 'watches or alerts')} in the last {windowLabel} not fully checked{w.window.by.unchecked.n > 0 ? ' either' : ''}.
            </p>
          )}
          {w.window.coins.length + w.month.coins.length > 0 && (
            <p className="text-meta text-tg-hint">
              {(w.month.coins.length ? w.month.coins : w.window.coins).map((c) => `${fmtAmount(c.amount, c.asset)} ${c.asset} has no dollar price — counted by payment`).join(' · ')}
            </p>
          )}
          {w.window.dust > 0 && (
            <p className="text-meta text-tg-hint">
              {count(w.window.dust, 'dust transfer', 'dust transfers')} ignored — address poisoning. Never copy an address from here.
            </p>
          )}
        </>
      ) : (
        <p className="text-meta text-tg-hint">Payments not read on this network — nothing is checked.</p>
      )}
      </div>
      </div>
      <ReaderLines w={w} />
    </div>
  );
}

/** How much of what came in has been checked — counted, never claimed. The
 *  header said "every payment checked" over rows reading Not checked and a
 *  network that is not read at all (review, 5 Oct 2026) — and "all 2 payments are fully checked" over a wallet whose
 *  reader was failing (render review, 21 Oct 2026): a wallet not being read is said first, and nothing is "fully checked" over it. */
/** A list's figures: how many, their dollars, the alerts and the watches among them. */
export function dayTotals(rows: readonly KytPayment[]): { n: number; usd: number; alerts: number; watch: number } {
  return rows.reduce((t, p) => ({ n: t.n + 1, usd: t.usd + (p.usd ?? 0), alerts: t.alerts + (p.kind === 'alert' ? 1 : 0), watch: t.watch + (p.kind === 'watch' ? 1 : 0) }), { n: 0, usd: 0, alerts: 0, watch: 0 });
}

/** The window's figures over every desk wallet, from the worker's own tallies (the same sums the morning report and
 *  /cex make): the alerts and their dollars, the watches, the payments not fully checked, the wallets not read. */
export function windowCounts(inc: KytIncoming, pick: (w: KytIncomingWallet) => boolean = () => true): { payments: number; usd: number; alerts: { n: number; usd: number }; watch: { n: number; usd: number }; unchecked: number; incomplete: number; unread: number } {
  const chosen = inc.wallets.filter(pick);
  const sum = (f: (w: KytIncomingWallet) => number): number => chosen.reduce((a, w) => a + f(w), 0);
  return {
    payments: sum((w) => w.window.payments),
    usd: sum((w) => w.window.usd),
    alerts: { n: sum((w) => w.window.by.alert.n), usd: sum((w) => w.window.by.alert.usd) },
    watch: { n: sum((w) => w.window.by.watch.n), usd: sum((w) => w.window.by.watch.usd) },
    unchecked: sum((w) => w.window.by.unchecked.n),
    incomplete: sum((w) => w.window.incomplete),
    unread: chosen.filter(paymentsUnread).length,
  };
}

/** What the desk's payments add up to, from the worker's tallies: over the longer of the two periods (both end now; early in
 *  a month the window reaches back past the 1st) — the payments, those not fully checked, the alerts and watches whose check
 *  did not cover everything (they keep their level, and "all fully checked" is not said over them), the small ones (under
 *  the minimum, on the sanctions lists only: never "fully checked", never a warning either, 9 Oct 2026) — and the wallets
 *  not being read (the network not read, or a reader that failed, stopped, never read or stepped over a transfer). */
export function coverageCounts(inc: KytIncoming): { payments: number; unchecked: number; incomplete: number; small: number; unread: number } {
  const longer = inc.wallets.map((w) => (w.window.payments > w.month.payments ? w.window : w.month));
  return {
    payments: longer.reduce((a, t) => a + t.payments, 0),
    unchecked: longer.reduce((a, t) => a + t.by.unchecked.n, 0),
    incomplete: longer.reduce((a, t) => a + t.incomplete, 0),
    small: longer.reduce((a, t) => a + (t.by.small?.n ?? 0), 0),
    unread: inc.wallets.filter(paymentsUnread).length,
  };
}

export function Coverage({ inc, tone = 'warn' }: { inc: KytIncoming; tone?: 'warn' | 'hint' }) {
  const { payments, unchecked, incomplete, small, unread } = coverageCounts(inc);
  const short = joined([
    unread > 0 && `${count(unread, 'wallet', 'wallets')} not read`,
    unchecked > 0 && `${count(unchecked, 'payment', 'payments')} not fully checked`,
    incomplete > 0 && `${count(incomplete, 'watch or alert', 'watches or alerts')} not fully checked${unchecked > 0 ? ' either' : ''}`,
  ]);
  if (short) return <span className={`text-meta ${tone === 'warn' ? 'text-warn' : 'text-tg-hint'}`}>{short}</span>;
  // Nothing watched is not a quiet day: nothing is being read.
  if (inc.wallets.length === 0) return <span className="text-meta text-warn">nothing watched</span>;
  if (payments === 0) return <span className="text-meta text-tg-hint">no payments yet</span>;
  if (small === 0) return <span className="text-meta text-tg-hint">{payments === 1 ? 'the 1 payment received is fully checked' : `all ${whole(payments)} payments received are fully checked`}</span>;
  // Never "checked on GL": a listed sender's small payment is told without GL, and is fully checked all the same (review, 9 Oct 2026).
  const others = payments - small;
  return (
    <span className="text-meta text-tg-hint">
      {count(small, 'small payment', 'small payments')} on the sanctions lists only{others > 0 && ` · ${others === 1 ? 'the other is' : `the other ${whole(others)} are`} fully checked`}
    </span>
  );
}

type PayFilter = 'all' | 'alert' | 'watch' | 'unchecked' | 'clean' | 'small';
/** The chips are the tier table's four seats: a payment is under the chip the worker counts it in —
 *  a "Clean, caveats" row is under Not fully checked, never under Clean. */
const PAY_FILTERS: Array<{ key: PayFilter; label: string; has: (p: KytPayment) => boolean }> = [
  { key: 'all',       label: 'All',               has: () => true },
  { key: 'alert',     label: 'Alert',             has: (p) => p.kind === 'alert' },
  { key: 'watch',     label: 'Watch',             has: (p) => p.kind === 'watch' },
  { key: 'unchecked', label: 'Not fully checked', has: (p) => p.kind === 'unchecked' },
  { key: 'clean',     label: 'Clean',             has: (p) => p.kind === 'clean' },
  { key: 'small',     label: 'Small',             has: (p) => p.kind === 'small' },
];

/** A line set at 0 alerts on any amount. */
const lineWords = (usd: number): string => (usd > 0 ? `from ${dollars(usd)}` : 'any amount');
/** The sanctions line since N-011 (20 Oct 2026): dollars AND a share of the payment, both — a figure at 0 is no line. */
const sanctionsLineWords = (usd: number, pct: number): string =>
  usd > 0 && pct > 0 ? `from ${dollars(usd)} and ${whole(pct)}% of the payment, both` : usd > 0 ? `from ${dollars(usd)}` : pct > 0 ? `${whole(pct)}% of the payment` : 'any amount';

/** The rules in force — on the System view, one line until opened (205 words on every render buried the tiles: render
 *  review, 21 Oct 2026). They are changed in the KYT group, not here. A native <details>: the rules stay in the page. */
function RulesInForce({ inc }: { inc: KytIncoming }) {
  return (
    <section className="card p-0 overflow-hidden">
      <details className="group">
        <summary className="flex cursor-pointer list-none flex-wrap items-baseline justify-between gap-x-3 px-3 py-2 hover:bg-panel-2/40 [&::-webkit-details-marker]:hidden">
          <span className="flex items-baseline gap-2">
            <span aria-hidden="true" className="text-meta text-tg-hint group-open:hidden">▸</span>
            <span aria-hidden="true" className="hidden text-meta text-tg-hint group-open:inline">▾</span>
            <h2 className="font-semibold text-body">The rules in force</h2>
          </span>
          <span className="text-meta text-tg-hint">set with <span className="font-mono">/kytrules</span></span>
        </summary>
        {/* One line to a level (6 Oct 2026): it was one paragraph of three rules run together. */}
        <div className="border-t border-stroke-soft px-3 py-2"><RulesLines inc={inc} /></div>
      </details>
    </section>
  );
}

/** The rules in force, one line to a level, as DATA — the level, how it joins, its words — drawn inside a fold by both
 *  screens (today's RulesInForce, the v2 System view). The words live here, in one place, and never on a screen by default. */
export function rulesLines(inc: KytIncoming): Array<{ level: string; sep: string; words: string }> {
  const r = inc.rules;
  // The worker before N-011 sends sanctionsIndirectUsd alone and alerts on any direct amount: its words stay its own.
  // Only a worker that sends both new figures is read the new way (Bookkeeping's review, 20 Oct 2026).
  const sanctions =
    r.sanctionsUsd != null && r.sanctionsPct != null
      ? `funds from a party on a government sanctions list (a sanctioned exchange too): ${sanctionsLineWords(r.sanctionsUsd, r.sanctionsPct)}`
      : `sanctioned-party funds: any direct amount, ${lineWords(r.sanctionsIndirectUsd)} indirect`;
  const out = [
    { level: 'Alert', sep: ' — ', words: `the sender on a sanctions list · ${sanctions} · criminal sources: ${lineWords(r.criminalDirectUsd)} direct, ${lineWords(r.criminalIndirectUsd)} indirect.` },
    { level: 'Watch', sep: ' — ', words: `any smaller trace · a declined applicant · a wallet given in a chat the bot blocked · more than ${whole(r.riskyPct)}% from risky sources · GL ${whole(r.glHigh)} or more.` },
    { level: 'Not fully checked', sep: ' — ', words: "nothing to alert on or watch was found, but the check was not whole (a list or GL's sources not read, or read in part) or has not run yet: never counted as clean. An Alert or a Watch found on less than the whole keeps its level, says so, and is counted beside these." },
  ];
  if ((r.glMinUsd ?? 0) > 0) out.push({ level: 'Small', sep: ' — ', words: `under ${dollars(r.glMinUsd ?? 0)}: checked on the sanctions lists only, with no GL check and no deep check. A sender on a list is still an Alert; a wallet from a blocked or declined chat, a Watch.` });
  if ((r.deepPct ?? 0) > 0) out.push({ level: 'Deep check', sep: ' — ', words: `${whole(r.deepPct ?? 0)}% or more of a payment's funds${(r.deepUsd ?? 0) > 0 ? ` and ${dollars(r.deepUsd ?? 0)} or more` : ''} from sanctions or criminal sources, or a sender on a list: the sender seen whole, attribution only.` });
  // The GL settings live here since the phone's /kytrules stopped listing them (house style rule 21, 27 Oct 2026).
  out.push({ level: 'GL', sep: ' — ', words: `${r.glBasis ? `asked about ${r.glBasis === 'tx' ? "each payment's own transaction" : "the sender's address"} · ` : ''}at most ${whole(r.glPerDay)} a day · a payment under ${dollars(r.dustUsd)} is counted, never checked.` });
  if (r.venues.length > 0) out.push({ level: 'Venues set', sep: ': ', words: `${r.venues.map((v) => `${v.name} → ${v.level}`).join(' · ')}.` });
  return out;
}

/** The rules' lines as drawn, one <p> a level — inside a fold, never on a screen by default. */
export function RulesLines({ inc }: { inc: KytIncoming }) {
  return (
    <div className="text-meta leading-snug text-tg-hint space-y-1.5">
      {rulesLines(inc).map((l) => <p key={l.level}><span className="text-tg-text">{l.level}</span>{l.sep}{l.words}</p>)}
    </div>
  );
}

/** The Payments view: what came in, and is anything wrong with it. The answer
 *  before a deposit first — one wallet to a line — then every payment, newest
 *  first; a payment's Trace opens beside the list. */
// ── Payments, laid out as GL Vision lays out a report (7 Oct 2026) ───────────
// The operator, 7 Oct 2026, on the live page: "Visually I see so much cluttered information on page", "so many
// numbers/formatting issues/not aligned information - Not good", "refer GL's own format their dashboard", and
// "additional information can be squeezed into a dropdown or more if the operator wants to have a look". So the
// view is GL's report in the Books' colours: the month at a glance (one ring: the dollars by level, and what needs
// you), where the money came from (one bar to a kind of source), and ONE table — a payment to a line, each column
// aligned, its particulars in a row that opens under it. Trace still opens the whole picture beside the list.

/** A row's level in one word — the screen's own words: a clean the check could not vouch for is "Not fully
 *  checked", never a green one. */
export function seatOf(p: KytPayment): { word: string; tone: PillTone } {
  if (p.level === 'clean' && p.caveats.length > 0) return { word: 'Not fully checked', tone: 'neutral' };
  return LEVEL_WORD[p.level];
}

/** A caveat the worker words in a sentence, in the few words a closed row has room for. */
const caveatLabel = (c: string): string =>
  /little data/i.test(c) ? 'GL little data'
    : /sources.*not read/i.test(c) ? 'GL sources not read'
      : /read in part/i.test(c) ? 'GL sources in part'
        : /lists? not read/i.test(c) ? 'Lists not read'
          : /oracle/i.test(c) ? 'Oracle silent'
            : capital(c);

type RiskTone = 'bad' | 'warn' | 'good' | 'hint';
const RISK_TEXT: Record<RiskTone, string> = { bad: 'text-neg', warn: 'text-warn', good: 'text-pos', hint: 'text-tg-hint' };

/** A row's risk: the label, its line under it, its tone — and, when an exposure decided it, the share, its dollars of this
 *  payment and who, as fields: a table puts the share in a column of its own (28 Oct 2026). */
/** `score`: the GL score a row was rated on ("GL rates it high"), as a field — the rebuilt table gives it a cell of its own, the
 *  sentence form (`riskSub`) keeps it in the line (1 Nov 2026, his "one fact per cell"). */
export type Risk = { label: string; sub: string | null; tone: RiskTone; pct?: number | null; usd?: number | null; who?: string | null; score?: number | null };
/** The line under a risk label as a SENTENCE: the score first, when the row was rated on one — today's screen's form. */
export const riskSub = (r: Risk): string | null => joined([r.score != null && `${Math.round(r.score)} of 100`, r.sub]) || null;

/** What a row says of its risk — GL's "risk label" — and, under it, its share and who. One label: the one the payment
 *  was RATED on — its own reason says which (the live page, 8 Oct 2026: an alert for a 3.3% scam trace was labelled by
 *  a 0.2% sanctions trace under the line, and watches GL scores high by a risky share under the line). A sanctions
 *  trace that is not the reason is still marked on the row: it is what an exchange looks for. */
export function mainRisk(p: KytPayment): Risk {
  const s = p.sanctions;
  // The pill beside it already says "Being checked", and the details say when it runs: the risk line says what there is
  // so far, not the same two words twice (22 Oct 2026).
  if (p.level === 'pending') return { label: 'No result yet', sub: null, tone: 'hint' };
  if (p.level === 'small') return { label: 'Sanctions lists only', sub: p.small ? `under ${dollars(p.small)}` : null, tone: 'hint' };
  if (s.state === 'listed') return { label: 'Sender on a sanctions list', sub: s.listed, tone: 'bad' };
  if (s.state === 'listed_before') return { label: 'Sender listed before', sub: s.listedBefore, tone: 'bad' };
  // Red is an alert's colour: a trace under the line is a Watch, and amber (the old screen's rule).
  const tone: RiskTone = p.kind === 'alert' ? 'bad' : 'warn';
  const why = p.why ?? '';
  const said = (label: string, e: KytExposure, all: KytExposure[]): Risk => {
    const rest = sourceCount(all) - (e.n ?? 1);
    // Who, when GL names an owner — the label already says what kind of source it is.
    const who = (e.n ?? 1) > 1 ? `${whole(e.n ?? 1)} sources` : e.owner;
    const more = rest > 0 ? `and ${whole(rest)} more` : null;
    return { label, sub: joined([`${share(e.pct)}${e.usd != null ? ` ≈ ${dollars(e.usd)}` : ''}`, who && more ? `${who} ${more}` : (who ?? more)]), tone, pct: e.pct, usd: e.usd ?? null, who: who ?? null };
  };
  // The worker's order: the sanctioned first, a folded line last — its first is the one to name.
  const blind = (p.unmeasured ?? [])[0];
  const traced = s.state === 'trace';
  const sanctionsRisk = (): Risk | null => {
    const e = s.exposures[0];
    if (e) return said(s.exposures.some((x) => x.tier === 'sanctions') ? 'Sanctioned-party funds' : 'Sanctions trace', e, s.exposures);
    if (traced && blind) return { label: blind.tier === 'sanctions' ? 'Sanctioned-party funds' : 'Sanctions trace', sub: `${blind.owner ?? capital(blind.label)} — not measured`, tone };
    return null;
  };
  const crimeRisk = (): Risk | null => {
    const e = p.criminal[0];
    if (e) return said((e.n ?? 1) > 1 ? 'Criminal sources' : capital(e.label), e, p.criminal);
    if (blind && !traced) return { label: capital(blind.label), sub: `${blind.owner ?? capital(blind.label)} — not measured`, tone };
    return null;
  };
  /** A criminal reason on a payment that ALSO carries a sanctions trace: the trace is marked on the row. */
  const marked = (r: Risk) => (traced ? { ...r, sub: joined([r.sub, 'sanctions trace']) } : r);
  if (s.state === 'unread') return { label: 'Sanctions lists not read', sub: null, tone: 'warn' };
  if (p.level === 'watch' || p.level === 'alert') {
    if (/^GL rates/.test(why)) return marked({ label: 'GL rates it high', sub: 'no criminal source named', tone: 'warn', score: p.score });
    if (/lists as risky/.test(why)) {
      const risky = p.risky.slice().sort((x, y) => y.pct - x.pct)[0];
      if (risky) return marked({ label: capital(risky.label), sub: share(risky.pct), tone: 'warn', pct: risky.pct });
    }
    // The name is the sender's line: said once.
    if (p.standing === 'blocked' && /chat the bot blocked/.test(why)) return marked({ label: 'Blocked chat', sub: null, tone: 'warn' });
    if (p.standing === 'declined' && /declined/.test(why)) return marked({ label: 'Declined applicant', sub: null, tone: 'warn' });
    // A trace: the sanctions one when the reason speaks of sanctions (or the action is "do not move"), else the criminal one.
    const sanctionsFirst = /sanction/i.test(why) || p.action === 'do_not_move';
    if (sanctionsFirst) {
      const r = sanctionsRisk() ?? crimeRisk();
      if (r) return r;
    } else {
      const c = crimeRisk();
      if (c) return marked(c);
      const r = sanctionsRisk();
      if (r) return r;
    }
    if (p.standing === 'blocked') return { label: 'Blocked chat', sub: null, tone: 'warn' };
    if (p.standing === 'declined') return { label: 'Declined applicant', sub: null, tone: 'warn' };
    // A watch or an alert the fields above do not name says its own reason — never the clean words below.
    const own = rowReason(p).replace(/\.$/, '');
    return { label: own ? capital(own) : LEVEL_WORD[p.level].word, sub: null, tone };
  }
  // Not rated on a trace (not checked, or a clean): a sanctions trace — or a source GL listed with no share, which
  // leaves a payment not checked — is said all the same, whatever the level.
  const unrated = sanctionsRisk() ?? crimeRisk();
  if (unrated) return unrated;
  if (p.level === 'unscreened') return { label: 'No result', sub: p.notes[0] ?? null, tone: 'hint' };
  // What the check did not cover: the pill says "not fully checked", this says what — its first reason, amber, in a few words
  // (it read "Nothing found" in plain words beside a low score). The rest is in the row's dropdown.
  if (p.level === 'clean' && p.caveats.length > 0) return { label: caveatLabel(p.caveats[0] ?? ''), sub: null, tone: 'warn' };
  // Clean: where most of it came from, as GL would name it.
  const known = p.trace?.sources.filter((x) => x.group === 'known').sort((a, b) => b.pct - a.pct)[0];
  if (known) return { label: known.owner ?? capital(known.label), sub: share(known.pct), tone: 'good' };
  return { label: 'Nothing found', sub: null, tone: 'good' };
}

/** The places a payment is counted, in the ring's order — the worker's own tallies, summed over the desk wallets. */
const RING: Array<{ key: KytKind; word: string; stroke: string; dot: string }> = [
  { key: 'alert',     word: 'Alert',             stroke: 'stroke-neg',     dot: 'bg-neg' },
  { key: 'watch',     word: 'Watch',             stroke: 'stroke-warn',    dot: 'bg-warn' },
  { key: 'unchecked', word: 'Not fully checked', stroke: 'stroke-tg-hint', dot: 'bg-tg-hint' },
  { key: 'clean',     word: 'Clean',             stroke: 'stroke-pos',     dot: 'bg-pos' },
  { key: 'small',     word: 'Small, lists only', stroke: 'stroke-brand-muted', dot: 'bg-brand-muted' },
];

/** The month's total is "0 USD · 0 payments" only where every wallet was read in full: over one that was not, nothing read is
 *  not nothing received — the ring and its phone line say "Not known" (22 Oct 2026). */
const monthUnknown = (inc: KytIncoming, t: { n: number }): boolean => t.n === 0 && inc.wallets.some(paymentsUnread);

function monthTally(inc: KytIncoming): { usd: number; n: number; by: Record<KytKind, { n: number; usd: number }> } {
  const by: Record<KytKind, { n: number; usd: number }> = { alert: { n: 0, usd: 0 }, watch: { n: 0, usd: 0 }, unchecked: { n: 0, usd: 0 }, clean: { n: 0, usd: 0 }, small: { n: 0, usd: 0 } };
  for (const w of inc.wallets) {
    for (const k of RING) {
      // An older worker counts no small payments.
      by[k.key].n += w.month.by[k.key]?.n ?? 0;
      by[k.key].usd += w.month.by[k.key]?.usd ?? 0;
    }
  }
  return { usd: RING.reduce((a, k) => a + by[k.key].usd, 0), n: RING.reduce((a, k) => a + by[k.key].n, 0), by };
}

/** The month as one ring, as GL draws a score: the dollars by level around it, the total inside. On a phone the ring is
 *  small and the total stands beside it: at 200 px it was half a phone screen before the first thing that needs him. */
function MonthRing({ t, unknown = false }: { t: ReturnType<typeof monthTally>; unknown?: boolean }) {
  const R = 84;
  const C = 2 * Math.PI * R;
  let at = 0;
  return (
    <div className="relative h-24 w-24 shrink-0 lg:h-[200px] lg:w-[200px]" role="img"
      aria-label={unknown ? 'This month: payments not known' : `This month: ${RING.map((k) => `${k.word} ${dollars(t.by[k.key].usd)}`).join(', ')}`}>
      <svg viewBox="0 0 200 200" className="h-full w-full -rotate-90">
        <circle cx="100" cy="100" r={R} fill="none" strokeWidth="14" className="stroke-panel-2" />
        {t.usd > 0 && RING.map((k) => {
          const len = (t.by[k.key].usd / t.usd) * C;
          if (len <= 0) return null;
          // A thin slice is never too thin to see: an alert of 1% is still an alert.
          const seen = Math.max(len, k.key === 'alert' || k.key === 'watch' ? 6 : 0);
          const seg = <circle key={k.key} cx="100" cy="100" r={R} fill="none" strokeWidth="14" className={k.stroke}
            strokeDasharray={`${seen} ${C - seen}`} strokeDashoffset={-at} />;
          at += len;
          return seg;
        })}
      </svg>
      <div className="absolute inset-0 hidden flex-col items-center justify-center lg:flex">
        {/* A figure of more than fourteen characters (316,274,043 USD touched the ring) sets in the title role. */}
        <div className={`amt font-semibold leading-tight ${unknown ? 'text-figure text-tg-hint' : dollars(t.usd).length > 14 ? 'text-title' : 'text-figure'}`}>{unknown ? '—' : dollars(t.usd)}</div>
        <div className="my-1.5 h-px w-20 bg-stroke" />
        {unknown
          ? <div className="text-meta uppercase tracking-wide text-warn">Not known</div>
          : <div className="text-meta uppercase tracking-wide text-tg-hint">{count(t.n, 'payment', 'payments')}</div>}
      </div>
    </div>
  );
}

/** The levels that hold a payment this month, a dot and a word and the dollars — a level with nothing in it is no row (a
 *  legend of dashes said "no alert" five times over a wallet that was not being read). On a phone the total leads. */
function Legend({ month, smallOn, unknown = false }: { month: ReturnType<typeof monthTally>; smallOn: boolean; unknown?: boolean }) {
  const levels = RING.filter((k) => (k.key !== 'small' || smallOn) && month.by[k.key].n > 0);
  return (
    <div className="space-y-1.5">
      <div className="lg:hidden">
        {unknown
          ? <><span className="amt text-figure font-semibold text-tg-hint">—</span> <span className="whitespace-nowrap text-meta text-warn">Not known</span></>
          : <><span className="amt text-figure font-semibold">{dollars(month.usd)}</span> <span className="whitespace-nowrap text-meta text-tg-hint">{count(month.n, 'payment', 'payments')}</span></>}
      </div>
      {/* A legend, not a verdict: a dot and a word — a pill here would be a green "Clean" on a page with no clean payment. */}
      {levels.map((k) => (
        <div key={k.key} className="flex items-center gap-3 lg:justify-end">
          <span className="flex items-center gap-2 text-body lg:w-[9rem] lg:justify-end">
            <span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${k.dot}`} aria-hidden="true" />{k.word}
          </span>
          <span className="amt text-body lg:w-28 lg:text-right">{dollars(month.by[k.key].usd)}</span>
        </div>
      ))}
    </div>
  );
}

/** One wallet's answer before a deposit, in blocks that each fit a line: the wallet on its own, the strongest reason, the
 *  reader's state when it is not read in full (one concise line, red or amber), and EVERY count of what was not fully checked
 *  as one labelled summary — whatever the answer is, a wallet not being read included — before any tap. Behind Details: the
 *  sentences the reader's state stands on, what the second count means, and the other counts (a 54-word sentence on a calm
 *  screen, then 22 words on a line, then a reader's failure in 20: render review 21 Oct, Astra 22 and 23 Oct 2026).
 *  `readFor`: the days the response is of, said on the wallet's line — for "No payments", for every answer of days while
 *  another window is being read (`settled` false), and for the counts beside a reader that is not read. */
function AnswerRow({ w, answer = w.deposit.answer, readFor = null, settled = true }: { w: KytIncomingWallet; answer?: KytDepositAnswer; readFor?: string | null; settled?: boolean }) {
  const a = DEPOSIT_WORD[answer];
  const { lead, shortfalls, explain, rest } = depositNote(w, answer);
  const trouble = readerTrouble(w);
  const ofTheWindow = answer !== 'not_read' && answer !== 'not_reading';
  const period = readFor && (answer === 'no_payments' || (!settled && ofTheWindow) || (answer === 'not_reading' && shortfalls.length > 0)) ? readFor : null;
  const behind = [...trouble.flatMap((t) => t.raw), explain, rest.length > 0 ? rest.join(' · ') : null].filter((x): x is string => x !== null);
  const body = !!lead || trouble.length > 0 || shortfalls.length > 0 || behind.length > 0;
  return (
    // The pill and the wallet share the first line; what the row says stands under them at the row's full width on a phone (a
    // reader's warning beside the pill left one word alone on its line), and under the wallet from lg.
    <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-2.5 gap-y-0.5">
      <Pill tone={a.tone}>{a.word}</Pill>
      <div data-answer="who" className="min-w-0 text-balance text-meta leading-snug"><span className="text-tg-text">{w.label ?? `${w.chain} wallet`}</span>{period && <span className="text-tg-hint"> · {period}</span>}</div>
      {body && (
        <div className="col-span-2 min-w-0 space-y-0.5 text-balance text-meta leading-snug lg:col-span-1 lg:col-start-2">
          {lead && <div data-answer="lead">{lead}</div>}
          {trouble.map((t) => <div key={t.state} data-answer="state" role={t.tone === 'bad' ? 'alert' : undefined} className={t.tone === 'bad' ? 'text-neg' : 'text-warn'}>{t.state}</div>)}
          {/* Each count stays whole when the line wraps: "2 watches or alerts" is never split across two lines. */}
          {shortfalls.length > 0 && (
            <div data-answer="short" className="text-tg-hint">
              Not fully checked: {shortfalls.map((t, i) => <Fragment key={t}>{i > 0 && ' · '}<span className="whitespace-nowrap">{t}</span></Fragment>)}
            </div>
          )}
          {behind.length > 0 && <Disclosure label="Details" className="mt-0.5">{behind.map((t) => <p key={t}>{t}</p>)}</Disclosure>}
        </div>
      )}
    </div>
  );
}

/** "No alerts." / "Nothing needs you." is a claim over every payment: made only with a wallet watched, every wallet read in
 *  full, nothing counted as not fully checked, and no alert counted (the worker's month and window tallies, not the rows —
 *  with the list not read, an alert that is counted is not an alert that is not there; Astra, 23 Oct 2026) or listed. */
export function calmDay(inc: KytIncoming): boolean {
  const allRead = !inc.wallets.some(paymentsUnread);
  const unchecked = inc.wallets.reduce((a, w) => a + Math.max(w.window.by.unchecked.n, w.month.by.unchecked.n), 0);
  const countedAlerts = inc.wallets.reduce((acc, w) => acc + Math.max(w.month.by.alert.n, w.window.by.alert.n), 0);
  return inc.wallets.length > 0 && allRead && unchecked === 0 && countedAlerts === 0 && !inc.payments.some((p) => p.kind === 'alert');
}

/** What needs the operator: the month's alerts the red line has not named, what is not being read, then the answer before
 *  an exchange deposit — one line a wallet, only where it is not the plain "no sanctions appearance". `named` are the alerts
 *  the red line above already names: each fact once. */
function NeedsYou({ inc, hours, onHours, onTrace, onRefresh, named }: { inc: KytIncoming; hours: Hours; onHours: (h: Hours) => void; onTrace: (id: string) => void; onRefresh: () => void; named: Set<string> }) {
  const monthAlerts = inc.payments.filter((p) => p.kind === 'alert');
  const alerts = monthAlerts.filter((p) => !named.has(p.id));
  // The green answer is said only when EVERY wallet's answer is the green one: no payments, a network not read or a
  // reader that is down is never "no sanctions appearance" (the old screen's rule, kept).
  const loud = inc.wallets.filter((w) => w.deposit.answer !== 'none');
  // "No alerts this month." and the green answer are claims over every payment: said only when every wallet is being read in
  // full — whatever the worker's answer for a wallet says, the screen draws no green over a reader that is down.
  const allRead = !inc.wallets.some(paymentsUnread);
  // …and over payments that were not all fully checked it is not said either: what needs him is then the row below. An alert
  // that is counted and has no row of ITS wallet is said as counted (unlistedAlerts), with the way to read the list again.
  const missing = unlistedAlerts(inc);
  const calm = calmDay(inc);
  // The answers are for the window the RESPONSE is of. The query keeps the last response on screen while another window is
  // read, and the chip follows the click at once: a calm conclusion of the old days beside the new chip ("No payments", the
  // green answer) is a claim about days that were not read — so, until the answer arrives, the screen says it is reading in
  // its place. What guards money is never made to wait: an adverse answer and every not-fully-checked count stay drawn, each
  // labelled with the days it was read for ("last 24 h"); so do a reader or a network not read, which hold for any days
  // (Astra, 22 Oct 2026, two rounds).
  const settled = typeof inc.hours !== 'number' || inc.hours === hours;
  const readFor = Number.isFinite(inc.hours) ? `last ${windowChip(inc.hours)}` : null;
  return (
    <div className="min-w-0 space-y-3">
      {/* The label stands over something: the alerts the red line has not named, or the calm. */}
      {(alerts.length > 0 || missing.n > 0 || calm) && <Label>Needs you</Label>}
      {calm && <div className="text-body text-tg-hint">No alerts.</div>}
      {alerts.slice(0, 3).map((p) => (
        <button key={p.id} type="button" onClick={() => onTrace(p.id)} className="flex w-full items-start gap-2.5 text-left hover:opacity-90">
          <Pill tone="bad">Alert</Pill>
          <span className="min-w-0">
            <span className="block text-body font-medium"><span className="amt">{fmtAmount(p.amount, p.asset)}</span> {p.asset} · {p.action === 'do_not_move' ? 'do not move' : 'keep apart'}</span>
            <span className="block text-meta text-tg-hint">{mainRisk(p).label}{riskSub(mainRisk(p)) ? ` ${riskSub(mainRisk(p))}` : ''} · {p.at ? `${fmtDay(p.at)} ${fmtTime(p.at)}` : '—'}</span>
          </span>
        </button>
      ))}
      {alerts.length > 3 && <div className="text-meta text-tg-hint">and {count(alerts.length - 3, 'more alert', 'more alerts')} in the table</div>}
      {missing.n > 0 && (
        <div className="flex items-start gap-2.5" data-alerts="counted">
          <Pill tone="bad">Alert</Pill>
          <StateLine tone="bad" className="min-w-0 text-balance" action={inc.more > 0 ? undefined : <Refresh onClick={onRefresh} />}>
            {count(missing.n, missing.some ? 'more alert' : 'alert', missing.some ? 'more alerts' : 'alerts')} counted{missing.usd != null && missing.usd > 0 && <> · <span className="amt">{dollars(missing.usd)}</span></>} — {inc.more > 0 ? 'older rows not shown' : 'list not read'}.
          </StateLine>
        </div>
      )}
      <div className="pt-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Label>Before a deposit</Label>
          <span className="flex gap-1">
            {WINDOWS.map((w) => (
              <button key={w.hours} type="button" className={`chip !px-2 !py-0.5 text-meta ${w.hours === hours ? 'chip-on' : ''}`} aria-pressed={w.hours === hours} onClick={() => onHours(w.hours)}>{w.chip}</button>
            ))}
          </span>
        </div>
        {!settled && <div className="mt-1.5 text-meta text-tg-hint">Reading {windowWords(hours)}…</div>}
        {settled && loud.length === 0 && allRead && (
          <div className="mt-1.5 flex items-center gap-2 text-body"><Pill tone="good">No sanctions appearance</Pill><span className="text-tg-hint text-meta">in {windowWords(inc.hours)}</span></div>
        )}
        <div className="mt-1.5 space-y-1.5">
          {inc.wallets.map((w) => {
            // A stored "No payments" or green "none" is not drawn over a wallet that is not being read in full: it says "Not known".
            const answer = drawnAnswer(w);
            // The green answer is said once, above; the calm "No payments" of the old days waits while another window loads.
            if (answer === 'none' || (!settled && answer === 'no_payments')) return null;
            // ONE row a wallet, whatever the answer: the wallet, why, the reader's state, and every count — never one without the other.
            return <AnswerRow key={`${w.chain}:${w.address}`} w={w} answer={answer} readFor={readFor} settled={settled} />;
          })}
        </div>
      </div>
    </div>
  );
}

/** Where the month's money came from, as GL's "Risk exposure": each kind of source, its dollars of the payments it
 *  is in, a bar for its share, and the largest names in it. Summed from each payment's own trace — the picture it
 *  was rated on — so it is an estimate, and what was not read is said as such. One line of shares until opened: the
 *  flagged kinds first, then what was not read. */
function FundsCard({ inc }: { inc: KytIncoming }) {
  const ORDER: KytTraceGroup[] = ['sanctions', 'criminal', 'risky', 'unknown', 'known'];
  const usd = new Map<KytTraceGroup, number>();
  const names = new Map<KytTraceGroup, Map<string, number>>();
  let total = 0;
  let unread = 0;
  let small = 0;
  for (const p of inc.payments) {
    if (p.usd == null || p.usd <= 0) continue;
    total += p.usd;
    const t = p.trace;
    // Under the minimum GL is not asked (9 Oct 2026): its dollars are said as that, not as "not read".
    if (!t && p.small) { small += p.usd; continue; }
    if (!t) { unread += p.usd; continue; }
    for (const g of t.groups) usd.set(g.group, (usd.get(g.group) ?? 0) + (g.usd ?? (g.pct / 100) * p.usd));
    unread += (t.unread / 100) * p.usd;
    for (const src of t.sources) {
      const who = src.owner ?? capital(src.label);
      const m = names.get(src.group) ?? new Map<string, number>();
      m.set(who, (m.get(who) ?? 0) + (src.usd ?? (src.pct / 100) * p.usd));
      names.set(src.group, m);
    }
  }
  if (total <= 0) return null;
  const top = (g: KytTraceGroup): string => [...(names.get(g) ?? new Map<string, number>()).entries()].sort((a, b) => b[1] - a[1]).slice(0, 3).map(([n]) => n).join(', ');
  // The kinds in the order of what matters: the flagged, then what was not read (amber: nobody knows), then the rest.
  const lines: Array<{ word: string; value: number; bar: string; text: string; who: string }> = [];
  for (const g of ORDER.slice(0, 3)) if ((usd.get(g) ?? 0) > 0) lines.push({ word: SOURCE_GROUPS[g].word, value: usd.get(g) ?? 0, bar: SOURCE_GROUPS[g].bar, text: SOURCE_GROUPS[g].text, who: top(g) });
  if (unread > 0.5) lines.push({ word: 'Not read', value: unread, bar: 'bg-warn/40', text: 'text-warn', who: '' });
  for (const g of ORDER.slice(3)) if ((usd.get(g) ?? 0) > 0) lines.push({ word: SOURCE_GROUPS[g].word, value: usd.get(g) ?? 0, bar: SOURCE_GROUPS[g].bar, text: SOURCE_GROUPS[g].text, who: top(g) });
  if (small > 0.5) lines.push({ word: 'Small, lists only', value: small, bar: 'bg-brand-muted', text: 'text-tg-hint', who: 'GL not asked' });
  // Every dollar Known: "Known 100%" says what the ring's green already says.
  if (lines.every((l) => l.word === 'Known')) return null;
  const line = (l: (typeof lines)[number]) => (
    <div key={l.word} className="grid grid-cols-[minmax(0,1fr)_3.5rem] items-center gap-x-3 gap-y-1.5 border-t border-stroke-soft py-2.5 first:border-t-0 lg:grid-cols-[18rem_minmax(0,1fr)_3.5rem] lg:gap-x-6">
      <div className="col-span-2 min-w-0 lg:col-span-1">
        <div className={`text-body ${l.text}`}>{l.word}</div>
        <div className="break-words text-meta text-tg-hint"><span className="amt">{dollars(l.value)}</span>{l.who ? ` · ${l.who}` : ''}</div>
      </div>
      <div className="h-3 overflow-hidden rounded bg-panel-2">
        <div className={`h-full ${l.bar}`} style={{ width: `${(l.value / total) * 100}%`, minWidth: l.value > 0 ? 3 : 0 }} />
      </div>
      {/* The share has its own column: printed on the bar, its digits were split between the fill and the track. */}
      <span className="text-right text-meta font-medium amt">{share((l.value / total) * 100)}</span>
    </div>
  );
  const summary = lines.slice(0, 4);
  return (
    <section className="card p-0 overflow-hidden">
      <details className="group">
        <summary className="flex cursor-pointer list-none flex-wrap items-baseline gap-x-3 gap-y-1 px-3 py-2 hover:bg-panel-2/40 [&::-webkit-details-marker]:hidden">
          <span className="flex items-baseline gap-2">
            <span aria-hidden="true" className="text-meta text-tg-hint group-open:hidden">▸</span>
            <span aria-hidden="true" className="hidden text-meta text-tg-hint group-open:inline">▾</span>
            <h2 className="font-semibold text-body">Where the money came from</h2>
          </span>
          <span className="flex flex-wrap gap-x-3 text-meta text-tg-hint">
            {summary.map((l) => <span key={l.word} className="whitespace-nowrap"><span className={l.text || undefined}>{l.word}</span> <span className="amt">{share((l.value / total) * 100)}</span></span>)}
            {lines.length > summary.length && <span>+{lines.length - summary.length} more</span>}
          </span>
        </summary>
        <div className="border-t border-stroke-soft px-3 py-1 lg:px-4">
          {lines.map(line)}
          <p className="pb-2 pt-1 text-meta text-tg-hint">This month, from each payment&apos;s check — an estimate.</p>
        </div>
      </details>
    </section>
  );
}

type PaySort = 'risk' | 'newest' | 'amount';
/** Newest first unless the operator picks another (8 Oct 2026: "need lastet first and descending order"). */
const SORTS: Array<{ key: PaySort; label: string }> = [
  { key: 'newest', label: 'Newest first' },
  { key: 'risk', label: 'Highest risk first' },
  { key: 'amount', label: 'Largest first' },
];
// Small before Clean: its funds were never traced (review, 9 Oct 2026).
const KIND_ORDER: Record<KytKind, number> = { alert: 0, watch: 1, unchecked: 2, small: 3, clean: 4 };
/** The row's risk as a figure a column can sort by, highest first: the same order the risk sort has kept since 8 Oct 2026. */
export const riskRank = (p: KytPayment): number => 4 - KIND_ORDER[p.kind];
const atMs = (p: KytPayment): number => (p.at ? Date.parse(p.at) : 0);
export function sortPayments(rows: KytPayment[], by: PaySort): KytPayment[] {
  const out = rows.slice();
  if (by === 'newest') return out.sort((a, b) => atMs(b) - atMs(a) || (a.id < b.id ? 1 : -1));
  if (by === 'amount') return out.sort((a, b) => (b.usd ?? 0) - (a.usd ?? 0) || atMs(b) - atMs(a));
  return out.sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || atMs(b) - atMs(a));
}

/** How many rows stand before "Show more". */
const ROWS_SHOWN = 25;

/** The columns at lg+, in order; Main risk has no width and takes the rest. */
const TABLE_COLS = {
  level: 'w-[9.5rem]', amount: 'w-[10rem]', sender: 'w-[13rem]',
  // The table was 60.5 rem wide whatever the window: at 1024 px Main risk had no width at all and Date was cut off, at 1280 it had 166 px.
  // The hash (linked in each row's details and in its trace) stands from 1400 px, the score (in the trace) from xl; the risk always has room.
  tx: 'w-[11rem] hidden min-[1400px]:table-cell', score: 'w-[6.5rem] hidden xl:table-cell',
  when: 'w-[7.5rem]', open: 'w-[3rem]',
} as const;

/** What opens under a row: why, the sanctions result, what to do, the funds as one bar — and the way to the whole trace. */
function PaymentDetail({ p, gl, onTrace }: { p: KytPayment; gl: SenderName | null; onTrace: () => void }) {
  const why = rowReason(p);
  const notFull = notFullWords(p);
  const ex = explorer(p.chain);
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] lg:gap-8">
      <div className="min-w-0 space-y-1.5 text-body leading-snug">
        {why && <p><span className="text-tg-hint">Why: </span>{why}</p>}
        <p>{p.sanctions.state === 'none' ? <SanctionsResult p={p} full labelled /> : <><span className="text-tg-hint">Sanctions: </span><SanctionsResult p={p} full /></>}</p>
        {p.action && <p><span className="text-tg-hint">Action: </span>{actionWords(p)}</p>}
        {notFull && <p className="text-tg-hint">{notFull}</p>}
        {(p.party || p.customer) && <p><span className="text-tg-hint">{p.party ? 'Booked to: ' : 'Who paid: '}</span><PayerName p={p} /></p>}
        {p.poisoning && <p className="text-neg">Address poisoning — look-alike of {p.poisoning.of}. Never a payee; never copy.</p>}
        {gl && <p><span className="text-tg-hint">Sender: </span>{gl.name}<span className="text-tg-hint"> — GL names this address ({gl.label})</span></p>}
        <p className="text-tg-hint">Into {p.walletLabel ?? `the ${p.chain} desk wallet`} · <span className="amt">{fmtAmount(p.amount, p.asset)}</span> {p.asset}</p>
        <div className="flex flex-wrap items-center gap-2 pt-1.5">
          <TraceOpen onClick={onTrace} what={`this payment of ${fmtAmount(p.amount, p.asset)} ${p.asset}`} />
          {ex && <a href={ex.tx(p.txid)} target="_blank" rel="noreferrer" className="btn-secondary text-meta py-1 px-2 inline-flex items-center gap-1">Transaction <ExternalLink size={11} /></a>}
          <Address address={p.from} chain={p.chain} poisoning={p.poisoning?.of ?? null} />
        </div>
      </div>
      <div className="min-w-0">
        {p.trace ? <SourceSplit t={p.trace} /> : <p className="text-body text-tg-hint">{p.small ? `GL was not asked — under ${dollars(p.small)}, the sanctions lists only.` : 'No trace was kept for this payment — its funds were not read.'}</p>}
      </div>
      {p.deep && <div className="lg:col-span-2"><DeepBlock d={p.deep} /></div>}
    </div>
  );
}

/** A worker's UTC sql time ("2026-10-08 07:24:33") as the instant it is. */
const utcOf = (sql: string): string => (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(sql) ? `${sql.replace(' ', 'T')}Z` : sql);

/** A payment's deep check — its sender seen whole: Arkham's name, GL's score of its whole wallet and where its funds
 *  came from, its sanctioned or criminal sources, its payments here (the operator, 8 Oct 2026: "I just need proper
 *  attribution, decision will be manual"). Attribution only: no verdict is said. */
function DeepBlock({ d }: { d: KytDeep }) {
  const s = d.sender;
  const a = d.arkham;
  const read = s ? s.read !== false : false;
  const groups = s && read ? s.groups.filter((g) => g.pct > 0).map((g) => `${SOURCE_GROUPS[g.g].word.toLowerCase()} ${share(g.pct)}`).join(' · ') : '';
  const more = s ? Math.max(0, s.flagged.length - 6) + (s.flaggedMore ?? 0) : 0;
  const rows: Array<{ k: string; v: React.ReactNode }> = [];
  rows.push({ k: 'Arkham', v: <>
    {a?.entity ? <span className="font-medium">{a.entity}</span> : <span className="text-tg-hint">{a?.note ?? 'no name for this address'}</span>}
    {a && a.labels.length > 0 && <span className="text-tg-hint"> ({a.labels.slice(0, 3).join(', ')})</span>}
  </> });
  if (s) {
    if (s.score != null) {
      rows.push({ k: 'GL, whole wallet', v: <><span className="amt">{Math.round(s.score)}</span> of 100{s.band ? ` (${s.band})` : ''}{groups && <span className="text-tg-hint"> — {groups}</span>}</> });
      // A stored answer, said with its date: not one GL gave at the deep check.
      const answer = joined([s.asOf && `as GL answered on ${fmtDay(utcOf(s.asOf))}`, s.thin && 'GL holds little data']);
      if (answer) rows.push({ k: 'GL answer', v: <span className="text-tg-hint">{answer}</span> });
    } else {
      rows.push({ k: 'GL, whole wallet', v: s.note ?? 'no score' });
    }
    // Never "none" of what was not read.
    if (s.score != null && !read) rows.push({ k: 'Sources not read', v: s.note ?? 'GL gave its score without them' });
    if (s.score != null && read) {
      rows.push({
        k: s.partial && s.flagged.length > 0 ? 'Sanctioned or criminal, part read' : 'Sanctioned or criminal',
        v: <>
          {s.flagged.length === 0
            ? `none${s.partial ? ' in the part read' : ''}`
            : s.flagged.slice(0, 6).map((f, i) => (
              <span key={`${f.label}:${f.owner ?? ''}:${i}`} className={SOURCE_GROUPS[f.g].text || undefined}>
                {i > 0 ? ' · ' : ''}{f.owner ?? capital(f.label)}{f.owner ? ` (${f.label})` : ''} {f.pct == null ? 'no share' : share(f.pct)}
              </span>
            ))}
          {s.flagged.length > 0 && more > 0 && <span className="text-tg-hint"> · and {whole(more)} more</span>}
        </>,
      });
    }
    if (read && s.top.length > 0) rows.push({ k: 'Largest others', v: s.top.slice(0, 4).map((t) => `${t.owner ?? capital(t.label)} ${share(t.pct)}`).join(' · ') });
  }
  if (d.paid) rows.push({ k: 'Paid to desk, month', v: <><span className="amt">{whole(d.paid.n)}</span>{d.paid.usd > 0 && <> · <span className="amt">{dollars(d.paid.usd)}</span></>}</> });
  return (
    <div className="rounded-lg border border-stroke-soft bg-panel-2/40 px-3 py-2.5 text-body leading-snug space-y-1.5">
      <div className="font-medium">Deep check · the sender seen whole</div>
      <dl className="grid grid-cols-1 gap-x-3 gap-y-0.5 sm:grid-cols-[10rem_minmax(0,1fr)]">
        {rows.map((r) => (
          <Fragment key={r.k}>
            <dt className="text-meta text-tg-hint sm:pt-0.5">{r.k}</dt>
            <dd className="mb-1.5 min-w-0 break-words sm:mb-0">{r.v}</dd>
          </Fragment>
        ))}
      </dl>
    </div>
  );
}

/** The transaction the row is — the operator, 8 Oct 2026: "I also need TX hash in each line for the checked tx". Its
 *  explorer page, and a copy of the whole hash; neither opens the row. */
function TxHash({ p }: { p: KytPayment }) {
  const [copied, setCopied] = useState(false);
  const ex = explorer(p.chain);
  const short = <span className="font-mono tabular text-meta">{mask(p.txid)}</span>;
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
      {ex ? <a href={ex.tx(p.txid)} target="_blank" rel="noreferrer" className="text-info hover:underline" title={p.txid}>{short}</a> : <span title={p.txid}>{short}</span>}
      <button type="button" className={`${DOOR} text-tg-hint hover:text-tg-text`} title="Copy the transaction hash" aria-label="Copy the transaction hash"
        onClick={() => { void navigator.clipboard?.writeText(p.txid).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1200); }); }}>
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </button>
    </span>
  );
}

function RiskCell({ p }: { p: KytPayment }) {
  const r = mainRisk(p);
  // A payment looked at again from the sender's side says so on its line; what was found is in its details.
  const sub = joined([riskSub(r), p.deep && 'deep checked']);
  return (
    <div className="min-w-0" title={joined([r.label, sub]) || undefined}>
      <div className={`line-clamp-2 break-words text-body ${r.tone === 'hint' ? 'text-tg-text' : RISK_TEXT[r.tone]}`}>{r.label}</div>
      {/* Three lines: the sub line carries the owner of an adverse trace, and a 70-character name was cut at 1024 px. */}
      {sub && <div className="line-clamp-3 break-words text-meta text-tg-hint">{sub}</div>}
    </div>
  );
}

function ScoreCell({ p }: { p: KytPayment }) {
  if (p.score == null) return <span className="text-tg-hint">—</span>;
  return (
    <div>
      <div className="amt text-body">{Math.round(p.score)}</div>
      {/* GL holding little data on the sender makes a low score no clean bill: "low" would read as one. */}
      {p.thin ? <div className="text-meta text-warn">little data</div> : p.band && <div className="text-meta text-tg-hint">{p.band}</div>}
    </div>
  );
}

function WhenCell({ p }: { p: KytPayment }) {
  if (!p.at) return <span className="text-tg-hint">—</span>;
  return (
    <div title={fmtRelative(p.at)}>
      <div className="text-body">{fmtDay(p.at)}</div>
      <div className="amt text-meta text-tg-hint">{fmtTime(p.at)}</div>
    </div>
  );
}

/** One payment at lg+: one line in the table, and a second that opens under it on a click. */
function PaymentLine({ p, gl, open, onToggle, onTrace }: { p: KytPayment; gl: SenderName | null; open: boolean; onToggle: () => void; onTrace: () => void }) {
  const seat = seatOf(p);
  return (
    <tbody className="group">
      <tr className={`border-t border-stroke-soft cursor-pointer hover:bg-panel-2/40 ${open ? 'bg-panel-2/30' : ''}`} onClick={onToggle}>
        <td className={`pl-4 pr-3 py-2.5 ${TABLE_COLS.level}`}><Pill tone={seat.tone}>{seat.word}</Pill></td>
        <td className={`px-3 py-2.5 text-right whitespace-nowrap ${TABLE_COLS.amount}`}><span className="amt text-body">{fmtAmount(p.amount, p.asset)}</span> <span className="text-meta text-tg-hint">{p.asset}</span></td>
        <td className={`px-3 py-2.5 ${TABLE_COLS.sender}`}>
          <div className={`font-mono tabular text-meta${p.poisoning ? ' text-neg' : ''}`} title={p.poisoning ? `Address poisoning — look-alike of ${p.poisoning.of}. Never a payee; never copy.` : p.from}>{mask(p.from)}</div>
          {p.poisoning && <div className="text-meta text-neg">Look-alike — address poisoning</div>}
          {(p.party || p.customer || gl) && <div className="break-words text-meta text-tg-hint">{p.party || p.customer ? <PayerName p={p} /> : gl && <GlSender n={gl} />}</div>}
        </td>
        <td className={`px-3 py-2.5 ${TABLE_COLS.tx}`}><TxHash p={p} /></td>
        <td className="px-3 py-2.5 overflow-hidden"><RiskCell p={p} /></td>
        <td className={`px-3 py-2.5 text-right ${TABLE_COLS.score}`}><ScoreCell p={p} /></td>
        <td className={`px-3 py-2.5 text-right whitespace-nowrap ${TABLE_COLS.when}`}><WhenCell p={p} /></td>
        <td className={`pr-4 py-2.5 text-right ${TABLE_COLS.open}`}>
          <button type="button" aria-expanded={open} aria-label={open ? 'Close the details' : 'Open the details'} className="text-tg-hint hover:text-tg-text"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}>{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>
        </td>
      </tr>
      <tr className="bg-panel-2/30" hidden={!open}>
        <td colSpan={8} className="px-4 pb-4 pt-1"><PaymentDetail p={p} gl={gl} onTrace={onTrace} /></td>
      </tr>
    </tbody>
  );
}

/** One payment on a phone: the level and the amount, then the day and its risk — the rest opens under it. */
function PaymentTile({ p, gl, open, onToggle, onTrace }: { p: KytPayment; gl: SenderName | null; open: boolean; onToggle: () => void; onTrace: () => void }) {
  const seat = seatOf(p);
  const r = mainRisk(p);
  return (
    <div className="border-t border-stroke-soft px-3 py-2.5">
      <button type="button" aria-expanded={open} className="flex w-full items-center gap-2.5 text-left" onClick={onToggle}>
        <Pill tone={seat.tone}>{seat.word}</Pill>
        <span className="min-w-0 flex-1 truncate"><span className="amt">{fmtAmount(p.amount, p.asset)}</span> <span className="text-meta text-tg-hint">{p.asset}</span></span>
        <span className="text-tg-hint">{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
      </button>
      <div className="mt-1 flex items-baseline gap-2 text-meta">
        <span className="amt text-tg-hint whitespace-nowrap">{p.at ? `${fmtDay(p.at)} ${fmtTime(p.at)}` : '—'}</span>
        <TxHash p={p} />
      </div>
      <div className={`mt-0.5 line-clamp-3 break-words text-meta ${r.tone === 'hint' ? 'text-tg-hint' : RISK_TEXT[r.tone]}`}>{r.label}{riskSub(r) ? ` · ${riskSub(r)}` : ''}{p.deep ? ' · deep checked' : ''}</div>
      {/* Who paid, when the desk knows them — a blocked or declined payer in words, as on the table; else the sender GL names. */}
      {p.poisoning && <div className="mt-0.5 text-meta text-neg">Look-alike — address poisoning</div>}
      {p.party || p.customer ? <div className="mt-0.5 leading-snug"><PayerName p={p} /></div> : gl && <div className="mt-0.5 text-meta text-tg-hint">From <GlSender n={gl} /></div>}
      <div className="mt-3" hidden={!open}><PaymentDetail p={p} gl={gl} onTrace={onTrace} /></div>
    </div>
  );
}

function PaymentsView({ q, hours, onHours, onTrace }: {
  q: { data?: KytIncoming; isLoading: boolean; isError: boolean; error: unknown; refetch: () => unknown };
  hours: Hours;
  onHours: (h: Hours) => void;
  onTrace: (id: string) => void;
}) {
  const [filter, setFilter] = useState<PayFilter>('all');
  const [sort, setSort] = useState<PaySort>('newest');
  const [opened, setOpened] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const inc = q.data;
  const nameOf = glSenderNames(inc?.payments ?? []);
  const shown = inc ? sortPayments(inc.payments.filter(PAY_FILTERS.find((f) => f.key === filter)!.has), sort) : [];
  const visible = all ? shown : shown.slice(0, ROWS_SHOWN);
  const toggle = (id: string) => setOpened((o) => (o === id ? null : id));
  // A failed read is said in red, with what to do — never the look of an empty list.
  if (q.isError) {
    const failed = readFailure(q.error);
    return (
      <Card tone="bad">
        <StateLine tone="bad" more={failed.more} action={<Retry onClick={() => void q.refetch()} />}>Payments not read — {failed.why}.</StateLine>
      </Card>
    );
  }
  if (q.isLoading || !inc) return <section className="card p-3"><Pending inline what="Reading the incoming payments…" /></section>;
  const month = monthTally(inc);
  const smallOn = smallSaid(inc);
  // The red line above names one or two alerts, with their Trace: Needs you does not say them again.
  const named = new Set(namedAlerts(inc, paymentAlerts(inc)).map((p) => p.id));
  // The chips are the levels that hold a payment (a chip with a 0 on it said nothing); with one level there is nothing to filter.
  const levels = PAY_FILTERS.filter((f) => f.key !== 'all' && (f.key !== 'small' || smallOn) && inc.payments.some(f.has));
  // A level that is pressed stays on the row — however few levels the list now holds — so the way back is never taken away.
  const chips = levels.length > 1 || filter !== 'all' ? PAY_FILTERS.filter((f) => f.key === 'all' || f.key === filter || levels.includes(f)) : [];
  return (
    <div className="space-y-4">
      {!inc.group && (
        <Slab tone="warn" more="Until then only alerts are told, in the ops group.">No KYT group — send <span className="font-mono">/kytgroup</span> in it.</Slab>
      )}
      {inc.wallets.length === 0 ? (
        // Nothing watched is not a quiet month — nothing is being read: said in amber, with no chips and no list under it.
        <Card tone="warn"><NoneWatched /></Card>
      ) : (
        <>
          <section className="card p-0 overflow-hidden">
            <header className={SECTION_HEAD}>
              <h2 className="font-semibold text-body">{inc.monthStart ? fmtMonth(inc.monthStart) : 'This month'}</h2>
            </header>
            {/* Phone: what needs him first, then the small ring beside the levels. From lg: levels · ring · what needs him. */}
            <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-4 px-3 py-4 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:gap-10 lg:px-4 lg:py-5">
              <div className="order-1 col-span-2 min-w-0 lg:order-3 lg:col-span-1"><NeedsYou inc={inc} hours={hours} onHours={onHours} onTrace={onTrace} onRefresh={() => void q.refetch()} named={named} /></div>
              <div className="order-2 lg:justify-self-center"><MonthRing t={month} unknown={monthUnknown(inc, month)} /></div>
              <div className="order-3 min-w-0 lg:order-1 lg:justify-self-end"><Legend month={month} smallOn={smallOn} unknown={monthUnknown(inc, month)} /></div>
            </div>
          </section>

          <FundsCard inc={inc} />

          <section className="card p-0 overflow-hidden">
            <header className={SECTION_HEAD}>
              <h2 className="font-semibold text-body">Incoming payments</h2>
              {/* Four rows need no order menu. */}
              {inc.payments.length > 5 && (
                <select aria-label="Order" className="rounded-lg border border-stroke bg-panel-2 px-2 py-1 text-field lg:text-meta text-tg-text" value={sort} onChange={(e) => setSort(e.target.value as PaySort)}>
                  {SORTS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              )}
            </header>
            {(chips.length > 0 || inc.more > 0) && (
              <div className="flex flex-wrap items-center gap-1.5 px-3 py-2.5 lg:px-4">
                {chips.map((f) => {
                  const n = inc.payments.filter(f.has).length;
                  return (
                    <button key={f.key} type="button" className={`chip ${filter === f.key ? 'chip-on' : ''}`} aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
                      {f.label} <span className="amt">{whole(n)}</span>
                    </button>
                  );
                })}
                {inc.more > 0 && <span className="ml-auto text-meta text-tg-hint">{whole(inc.more)} older not shown</span>}
              </div>
            )}
            {shown.length === 0 ? (
              <div className="border-t border-stroke-soft">
                {inc.payments.length === 0 && countedPayments(inc) > 0
                  // Counted, and the list did not come: said as that, with the way to read it again.
                  ? <div className="p-3"><StateLine tone="warn" action={<Refresh onClick={() => void q.refetch()} />}>{emptyPaymentsWords(inc)}</StateLine></div>
                  : <EmptyState>{emptyPaymentsWords(inc)}</EmptyState>}
              </div>
            ) : (
              <>
                <div className="hidden lg:block" data-layout="desk">
                  <table className="w-full table-fixed text-body">
                    <thead className="text-meta text-tg-hint">
                      <tr className="border-t border-stroke-soft">
                        <th className={`pl-4 pr-3 py-2 text-left font-medium ${TABLE_COLS.level}`}>Level</th>
                        <th className={`px-3 py-2 text-right font-medium ${TABLE_COLS.amount}`}>Amount</th>
                        <th className={`px-3 py-2 text-left font-medium ${TABLE_COLS.sender}`}>Sender</th>
                        <th className={`px-3 py-2 text-left font-medium ${TABLE_COLS.tx}`}>Tx hash</th>
                        <th className="px-3 py-2 text-left font-medium">Main risk</th>
                        <th className={`px-3 py-2 text-right font-medium ${TABLE_COLS.score}`}>GL score</th>
                        <th className={`px-3 py-2 text-right font-medium ${TABLE_COLS.when}`}>Date &amp; time</th>
                        <th aria-label="Details" className={`pr-4 py-2 ${TABLE_COLS.open}`} />
                      </tr>
                    </thead>
                    {visible.map((p) => <PaymentLine key={p.id} p={p} gl={nameOf(p)} open={opened === p.id} onToggle={() => toggle(p.id)} onTrace={() => onTrace(p.id)} />)}
                  </table>
                </div>
                <div className="lg:hidden" data-layout="phone">
                  {visible.map((p) => <PaymentTile key={p.id} p={p} gl={nameOf(p)} open={opened === p.id} onToggle={() => toggle(p.id)} onTrace={() => onTrace(p.id)} />)}
                </div>
                {shown.length > visible.length && (
                  <button type="button" className="w-full border-t border-stroke-soft py-3 text-center text-body text-brand-gold hover:bg-panel-2/40" onClick={() => setAll(true)}>
                    Show {whole(shown.length - visible.length)} more
                  </button>
                )}
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/** The alerts the red line may NAME — only when the names are the count. The line counts the chosen WINDOW's alerts by the
 *  worker's tallies (`paymentAlerts`); the list is the whole period, from the earlier of the month's start and the
 *  window's start. Naming "the alerts on the list" named last week's under "1 alert in the last 24 hours", and past two
 *  named none, losing today's (6 Oct 2026: two reviews found it, one in each gate). So: an alert is named when it is inside
 *  the window — the worker's own `block_time > windowStart` — on a wallet the count sums; and only when that makes exactly
 *  the number the line says, and it is one or two. The list is capped: a count the names cannot account for names none. */
export function namedAlerts(inc: KytIncoming | undefined, alerts: { n: number } | null): KytPayment[] {
  if (!inc || !alerts || alerts.n < 1 || alerts.n > 2 || !inc.at) return [];
  const named = windowAlerts(inc);
  return named.length === alerts.n ? named : [];
}

/** The alerts the worker's window count stands for: inside the window (strictly after "now minus the window", the
 *  worker's own `block_time > windowStart`) and on a wallet the count sums. */
function windowAlerts(inc: KytIncoming): KytPayment[] {
  if (!inc.at) return [];
  const from = Date.parse(inc.at) - inc.hours * 3_600_000;
  return inc.payments.filter((p) => p.kind === 'alert' && p.at != null && Date.parse(p.at) > from
    && inc.wallets.some((w) => sameWallet({ chain: p.chain, address: p.wallet }, w)));
}

/** The payment alerts of the window, across the desk's wallets — null when there are none. `stop` is what the line tells
 *  him to do, and it follows the STRONGEST action it stands for: a listed sender, sanctioned-party funds, or any alert the
 *  worker says "do not move" — never the softer words over a harder answer (render review, 21 Oct 2026). */
export function paymentAlerts(inc: KytIncoming | undefined): { n: number; usd: number; listed: number; hours: number; stop: string } | null {
  if (!inc) return null;
  const n = inc.wallets.reduce((a, w) => a + w.window.by.alert.n, 0);
  if (n === 0) return null;
  const listed = inc.wallets.reduce((a, w) => a + w.deposit.listed, 0);
  const hold = listed > 0 || inc.wallets.some((w) => w.deposit.answer === 'party') || windowAlerts(inc).some((p) => p.action === 'do_not_move');
  return {
    n,
    usd: inc.wallets.reduce((a, w) => a + w.window.by.alert.usd, 0),
    listed,
    hours: inc.hours,
    stop: hold ? 'do not move those funds' : 'keep those funds apart',
  };
}

/** The watched wallet as the overview has it — the Wallets view's fallback when
 *  the payments read is not there: risk · label (the entity muted after it) ·
 *  the address at the right; then checked · chain · Trace. */
function WatchedCard({ w, onTrace }: { w: KytWatchedWallet; onTrace: () => void }) {
  return (
    <div className={CARD}>
      <div className={LEDGER.cardLine1}>
        <BandPill band={w.band} looked={looked(w)} />
        <span className="flex-1 min-w-0 break-words">
          {w.label ?? <span className="text-tg-hint">—</span>}
          {w.entity && <span className="text-tg-hint"> · {w.entity}</span>}
        </span>
      </div>
      <div className={CARD_LINE2}>
        <span className={LEDGER.whenText} title={w.lastChecked ? fmtStamp(w.lastChecked) : undefined}>
          {w.lastChecked ? fmtRelative(w.lastChecked) : 'never'}
        </span>
        <span className={CHAIN}>{w.chain}</span>
        <Address address={w.address} chain={w.chain} />
        <span className="ml-auto"><TraceOpen onClick={onTrace} what={w.label ?? `the ${w.chain} wallet`} /></span>
      </div>
    </div>
  );
}

/** A desk address that is no longer handed out. It stays on this list — and in
 *  the count above — because it can still be paid into and the Books still read
 *  it (bff: registerWhere). It used to drop off both. This is the TABLE's
 *  marker, in a cell that is never cut short; the phone card has its own line. */
function RetiredNote({ w }: { w: KytBooksWallet }) {
  if (w.status !== 'archived') return null;
  return <span className="text-tg-hint whitespace-nowrap"> · retired{w.retired_at ? ` ${fmtDay(w.retired_at, true)}` : ''}</span>;
}

/** The party's kind in the operator's words (§6 "Counterparty type": every
 *  counterparty is a Merchant on screen). */
export const KIND_WORDS: Record<KytBooksWallet['party_type'], string> = { own_account: 'Treasury', customer: 'Customer', counterparty: 'Merchant' };

/** `kind`: the Kind column stands only when every class is listed together. */
function BooksRow({ w, kind, labels = true, watchCol = true, riskCol = true, onTrace }: { w: KytBooksWallet; kind: boolean; labels?: boolean; watchCol?: boolean; riskCol?: boolean; onTrace: () => void }) {
  const treasury = w.party_type === 'own_account';
  const name = partyName({ display_name: w.display_name, slug: w.slug });
  return (
    <tr className="border-t border-stroke-soft">
      <td className="px-3 py-1.5">
        {/* The treasury account's internal code used to sit beside the
            name in mono; the label column already says which wallet. */}
        <span className={treasury ? 'font-medium' : ''}>{name}</span>
        <RetiredNote w={w} />
      </td>
      {kind && <td className="px-3 py-1.5"><Pill tone={treasury ? 'info' : 'neutral'}>{KIND_WORDS[w.party_type]}</Pill></td>}
      <td className="px-3 py-1.5 font-mono tabular text-meta">{w.chain}</td>
      <td className="px-3 py-1.5"><Address address={w.address} chain={w.chain} /></td>
      {/* A label that only repeats the wallet's name is not printed twice. */}
      {labels && <td className="px-3 py-1.5">{w.label && w.label !== name ? w.label : <span className="text-tg-hint">—</span>}</td>}
      {watchCol && (
        <td className="px-3 py-1.5">
          {w.watched
            ? <Pill tone="good">watched</Pill>
            : treasury
              ? <WatchMark w={w} />
              : <span className="text-meta text-tg-hint">—</span>}
        </td>
      )}
      {riskCol && (
        <td className="px-3 py-1.5">
          <BandPill band={w.band} looked={looked(w)} />
          {w.risk_score != null && <span className="ml-1.5 font-mono tabular text-meta text-tg-hint">{w.risk_score}</span>}
        </td>
      )}
      <td className="px-3 py-1.5 text-right"><TraceOpen onClick={onTrace} what={name} quiet /></td>
    </tr>
  );
}

/** The Books wallet on a phone: [kind ·] party name (the label muted after
 *  it, unless it only repeats the name) · watched state [· risk] at the
 *  right; then chain · address · Trace. Two lines: with the state on the
 *  second, the Trace button wrapped to a third line of its own with a
 *  blank band beside it (19 Sept 2026).
 *  6 Oct 2026: the name is what tells one wallet from the next, and the real names end in the month
 *  ("… · Oct 2026") — it was cut to "Main TRC20 · O…" by a Kind pill that says what the list already says
 *  (`kind`: only where classes are listed together) and a "no score" pill that says nothing at all. */
function BooksCard({ w, kind = true, onTrace }: { w: KytBooksWallet; kind?: boolean; onTrace: () => void }) {
  const treasury = w.party_type === 'own_account';
  const name = partyName({ display_name: w.display_name, slug: w.slug });
  return (
    <div className={CARD}>
      <div className={LEDGER.cardLine1}>
        {kind && <Pill tone={treasury ? 'info' : 'neutral'} className="shrink-0 mt-px">{KIND_WORDS[w.party_type]}</Pill>}
        <span className="flex-1 min-w-0 break-words">
          <span className={treasury ? 'font-medium' : 'who'}>{name}</span>
          {w.label && w.label !== name && <span className="text-tg-hint"> · {w.label}</span>}
        </span>
        <span className="inline-flex items-baseline gap-1.5 shrink-0">
          {/* Only the exception is marked: "watched" on every card under "3 of 3 watched" was clutter (7 Oct 2026). */}
          {!w.watched && treasury && <WatchMark w={w} />}
          {(w.band || w.risk_score != null) && <BandPill band={w.band} looked={looked(w)} />}
          {w.risk_score != null && <span className="amt text-meta text-tg-hint">{w.risk_score}</span>}
        </span>
      </div>
      {/* A line of its own — never inside the name's span above: that span is
          cut short on a narrow phone, and a retired address that loses its
          marker looks like a current one (review, 1 Oct 2026). */}
      {w.status === 'archived' && (
        <div className="text-meta text-tg-hint">Retired{w.retired_at ? ` ${fmtDay(w.retired_at, true)}` : ''}</div>
      )}
      <div className={CARD_LINE2}>
        <span className={CHAIN}>{w.chain}</span>
        <Address address={w.address} chain={w.chain} />
        <span className="ml-auto"><TraceOpen onClick={onTrace} what={name} /></span>
      </div>
    </div>
  );
}

// ── Trace: where did this money come from (5 Oct 2026) ───────────────────────
// The operator: "build a proper screens for effective KYT Tracing, not too
// complicated, not overwhelming". A trace opens BESIDE the list — never inside
// a table row — and answers one question at a time, top to bottom: the verdict
// · does it appear on sanctions · where the funds came from · who paid · into
// which wallet · what was actually checked. It is free: it shows what the
// check stored with the payment (the worker's `trace`), so it is the picture
// the payment was RATED on, not the sender's wallet a week later.

/** The side panel a trace opens in: the right edge at sm+, the whole screen on a phone. */
function Panel({ label, title, sub, onClose, children }: { label: string; title: React.ReactNode; sub?: React.ReactNode; onClose: () => void; children: React.ReactNode }) {
  const panel = useRef<HTMLElement>(null);
  const closer = useRef<HTMLButtonElement>(null);
  // The latest onClose, without re-running the effect: run again on every render, it handed focus
  // back and took it again each time.
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    // A dialog takes the keyboard: focus moves into it, Tab stays inside it, and on closing it goes
    // back to the button that opened it (Astra, 6 Oct 2026: focus stayed on the row behind).
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closer.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close.current();
        return;
      }
      const box = panel.current;
      if (e.key !== 'Tab' || !box) return;
      const stops = [...box.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')].filter((el) => el.offsetParent !== null);
      const first = stops[0];
      const last = stops[stops.length - 1];
      if (!first || !last) return;
      const inside = box.contains(document.activeElement);
      if (e.shiftKey && (!inside || document.activeElement === first)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!inside || document.activeElement === last)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    const was = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = was;
      if (opener?.isConnected) opener.focus();
    };
  }, []);
  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} aria-hidden="true" />
      <aside ref={panel} role="dialog" aria-modal="true" aria-label={label}
        className="fixed inset-y-0 right-0 z-50 w-full sm:w-[480px] sm:max-w-[94vw] bg-tg-section-bg border-l border-stroke shadow-2xl flex flex-col">
        <header className="flex items-start justify-between gap-2 px-4 py-3 border-b border-stroke-soft shrink-0">
          <div className="min-w-0 space-y-0.5">{title}{sub}</div>
          <button ref={closer} type="button" onClick={onClose} aria-label="Close" title="Close (Esc)"
            className="tap-44 p-1.5 rounded-md text-tg-hint hover:bg-tg-secondary hover:text-tg-text transition-colors shrink-0">
            <X size={16} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 text-body leading-snug">{children}</div>
      </aside>
    </>
  );
}

/** One block of a trace: a small heading, and what stands under it. */
function TraceBlock({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h3 className="text-label uppercase tracking-wide text-tg-hint">
        {title}{note && <span className="normal-case tracking-normal"> · {note}</span>}
      </h3>
      {children}
    </section>
  );
}

/** The kinds of source, as the worker groups them. The WORD says the kind; the tone only underlines it (§7 rule 10). */
const SOURCE_GROUPS: Record<KytTraceGroup, { word: string; bar: string; text: string }> = {
  sanctions: { word: 'Sanctions', bar: 'bg-neg',     text: 'text-neg' },
  criminal:  { word: 'Criminal',  bar: 'bg-neg/60',  text: 'text-neg' },
  risky:     { word: 'Risky',     bar: 'bg-warn',    text: 'text-warn' },
  unknown:   { word: 'Unknown',   bar: 'bg-tg-hint/70', text: '' },
  known:     { word: 'Known',     bar: 'bg-info/60', text: '' },
};
const flaggedGroup = (g: KytTraceGroup): boolean => g === 'sanctions' || g === 'criminal';

/** The funds by kind of source, as one bar. What was not read stays the bare
 *  track — so "unknown" is a grey of its own, never the track's colour. */
export function SourceBar({ t }: { t: KytTrace }) {
  // A list GL gives over the whole (never seen) is drawn to the bar's width, not cut at its end.
  const whole100 = Math.max(100, t.groups.reduce((a, g) => a + g.pct, 0) + t.unread);
  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-panel-2" role="img"
      aria-label={`By kind of source: ${t.groups.map((g) => `${SOURCE_GROUPS[g.group].word} ${share(g.pct)}`).join(', ')}${t.unread > 0 ? `, not read ${share(t.unread)}` : ''}`}>
      {/* A sanctioned or criminal sliver is never too thin to see. */}
      {t.groups.map((g) => (
        <span key={g.group} className={`${SOURCE_GROUPS[g.group].bar} shrink-0`}
          style={{ width: `${(g.pct / whole100) * 100}%`, minWidth: flaggedGroup(g.group) && g.pct > 0 ? 4 : undefined }} />
      ))}
    </div>
  );
}

/** The bar's legend, on one line: each kind and its share. */
export function SourceLegend({ t }: { t: KytTrace }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-meta text-tg-hint">
      {t.groups.map((g) => {
        const k = SOURCE_GROUPS[g.group];
        return (
          <span key={g.group} className="whitespace-nowrap">
            <span className={`inline-block h-2 w-2 rounded-full align-middle mr-1 ${k.bar}`} aria-hidden="true" />
            <span className={k.text || undefined}>{k.word}</span> <span className="amt">{share(g.pct)}</span>
          </span>
        );
      })}
      {t.unread > 0 && <span className="whitespace-nowrap text-warn">Not read <span className="amt">{share(t.unread)}</span></span>}
    </div>
  );
}

/** The same split in words and figures: each kind, how many sources, its share — and its dollars of this payment. */
function SourceSplit({ t }: { t: KytTrace }) {
  // A wallet's own funds are in percents alone: no seat is kept for dollars that are not there.
  const priced = t.groups.some((g) => g.usd != null);
  return (
    <div className="space-y-1.5">
      <SourceBar t={t} />
      <table className="w-full text-meta">
        <tbody>
          {t.groups.map((g) => {
            const k = SOURCE_GROUPS[g.group];
            return (
              <tr key={g.group}>
                <td className="py-0.5 pr-2">
                  <span className={`inline-block h-2 w-2 rounded-full align-middle mr-1.5 ${k.bar}`} aria-hidden="true" />
                  <span className={k.text || undefined}>{k.word}</span>
                  <span className="text-tg-hint"> · {count(g.n, 'source', 'sources')}</span>
                </td>
                <td className={`py-0.5 ${priced ? 'px-2' : 'pl-2'} text-right whitespace-nowrap amt ${k.text}`}>{share(g.pct)}</td>
                {priced && <td className="py-0.5 pl-2 text-right whitespace-nowrap amt text-tg-hint w-24">{g.usd != null ? `≈ ${dollars(g.usd)}` : ''}</td>}
              </tr>
            );
          })}
          {t.unread > 0 && (
            <tr>
              <td className="py-0.5 pr-2">
                <span className="inline-block h-2 w-2 rounded-full align-middle mr-1.5 border border-stroke" aria-hidden="true" />
                <span className="text-warn">Not read</span>
              </td>
              <td className={`py-0.5 ${priced ? 'px-2' : 'pl-2'} text-right whitespace-nowrap amt text-warn`}>{share(t.unread)}</td>
              {priced && <td className="py-0.5 pl-2 w-24" />}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/** How a source reached the wallet: straight from it, or through other wallets first. */
function distance(s: { pct: number; directPct: number; usd: number | null }): string {
  if (s.directPct <= 0) return 'through other wallets';
  if (s.directPct >= s.pct * 0.999) return 'direct';
  return `${s.usd != null && s.pct > 0 ? dollars((s.usd * s.directPct) / s.pct) : share(s.directPct)} of it direct`;
}

export const capital = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** How many sources stand before "Show all": who the funds came from, at a glance. */
const SOURCES_SHOWN = 6;

/** The largest sources by name. The sanctioned and criminal ones come first — the worker's order — and are never folded away. */
function SourceList({ t }: { t: KytTrace }) {
  const [all, setAll] = useState(false);
  const first = Math.max(SOURCES_SHOWN, t.sources.filter((s) => flaggedGroup(s.group)).length);
  const shown = all ? t.sources : t.sources.slice(0, first);
  return (
    <div>
      <ul className="divide-y divide-stroke-soft">
        {shown.map((s, i) => {
          const k = SOURCE_GROUPS[s.group];
          const marked = flaggedGroup(s.group) || s.group === 'risky';
          return (
            <li key={`${s.group}:${s.owner ?? ''}:${s.label}:${i}`} className="py-1.5 flex items-baseline gap-2">
              <span className={`inline-block h-2 w-2 rounded-full shrink-0 ${k.bar}`} aria-hidden="true" />
              <span className="flex-1 min-w-0">
                <span className="who break-words">{s.owner ?? capital(s.label)}</span>
                {s.owner && <span className="text-tg-hint"> · {s.label}</span>}
                {(s.n ?? 1) > 1 && <span className="text-tg-hint"> · {whole(s.n ?? 1)} sources</span>}
                <span className="block text-meta text-tg-hint">
                  {marked && <span className={k.text}>{k.word} · </span>}
                  {distance(s)}
                </span>
              </span>
              <span className="shrink-0 text-right">
                <span className={`amt ${marked ? k.text : ''}`}>{share(s.pct)}</span>
                {s.usd != null && <span className="block text-meta text-tg-hint amt">≈ {dollars(s.usd)}</span>}
              </span>
            </li>
          );
        })}
      </ul>
      {(t.sources.length > first || t.more > 0) && (
        <div className="pt-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-meta text-tg-hint">
          {t.sources.length > first && (
            <button type="button" className="btn-link" onClick={() => setAll(!all)}>{all ? 'Show fewer' : `Show all ${whole(t.sources.length)}`}</button>
          )}
          {t.more > 0 && <span>and {count(t.more, 'smaller source', 'smaller sources')} not listed by name</span>}
        </div>
      )}
    </div>
  );
}

/** "scam (FakeShop) and sanctions (Listed Co)" — every one, or the first `max` and how many more. */
export function namesWords(names: string[], max = Infinity): string {
  const shown = names.slice(0, max);
  const rest = names.length - shown.length;
  return `${shown.join(' and ')}${rest > 0 ? ` and ${count(rest, 'more', 'more')}` : ''}`;
}

/** What a trace did not see: the part of the funds its list does not account for, a list read in part,
 *  and the criminal or sanctioned sources GL listed with no share — none of them is in the split above. */
function TracePart({ t, whose, names }: { t: KytTrace; whose: string; names?: string[] }) {
  // The trace's own list; a worker that sent the names beside the trace only (cecb12b) is read from those.
  const blind = t.unmeasured?.length ? t.unmeasured.map((u) => `${u.label}${u.owner ? ` (${u.owner})` : ''}`) : (names ?? []);
  return (
    <>
      {t.unread > 0
        ? <p className="text-meta text-warn">GL read only part of {whose} funds — the rest is not known.</p>
        : t.partial && <p className="text-meta text-warn">GL read part of {whose} source list — rows were not measured.</p>}
      {blind.length > 0 && <p className="text-meta text-warn">GL lists {namesWords(blind)} among the sources with no share — not measured.</p>}
    </>
  );
}

/** No trace to show — said for what it is, never as an empty split that reads "nothing found". */
function NoTrace({ p }: { p: KytPayment }) {
  if (p.level === 'pending') return <p className="text-tg-hint">Not known yet — its check has not run.</p>;
  // Under the minimum GL is not asked — the operator's rule (9 Oct 2026), not a failure: no warning colour.
  if (p.small) return <p className="text-tg-hint">Not known — GL was not asked.</p>;
  if (!p.sourcesRead) {
    // With no result, or a clean that could not be vouched for, the reason is the verdict above: not said twice.
    const said = p.level === 'unscreened' || (p.level === 'clean' && p.caveats.length > 0);
    const why = said ? '' : p.notes.length ? `: ${p.notes.join(' · ')}` : p.thin ? `: GL holds little data on ${p.basis === 'tx' ? 'this transaction' : 'the sender'}` : '';
    return <p className="text-warn">Not known — GL&apos;s sources were not read{why}.</p>;
  }
  // Rated before the trace was kept with the payment: what was stored then.
  const none = p.partial ? 'none in the part read' : 'none';
  return (
    <div className="space-y-1">
      {p.funds && <p><span className="text-tg-hint">Older check — summary only: </span>{p.funds}</p>}
      <p className="text-tg-hint">Criminal sources: {p.criminal.length ? p.criminal.map(exposureWords).join(' · ') : none}</p>
      <p className="text-tg-hint">Risky sources: {p.risky.length ? p.risky.map((r) => `${r.label} ${share(r.pct)}`).join(' · ') : none}</p>
    </div>
  );
}

/** 1st, 2nd, 3rd, 4th … 11th, 12th, 13th … 21st — as the payment's report says it. */
function ordinal(n: number): string {
  const tens = n % 100;
  return `${whole(n)}${tens >= 11 && tens <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th')}`;
}

/** One payment traced. Top to bottom: the verdict · sanctions · where the funds
 *  came from · who paid · into which wallet · what was checked. */
export function PaymentTrace({ p, gl = null, onClose }: { p: KytPayment; gl?: SenderName | null; onClose: () => void }) {
  const ex = explorer(p.chain);
  const reason = reasonLine(p);
  const notFull = notFullWords(p);
  const hold = p.action === 'do_not_move' || p.action === 'keep_apart';
  const action = actionWords(p);
  const adverse = p.standing === 'blocked' || p.standing === 'declined';
  const listsRead = p.sanctions.state !== 'unread' && p.sanctions.lists.length > 0;
  const lists = p.sanctions.lists.filter((l) => !/oracle/i.test(l));
  return (
    <Panel onClose={onClose} label={`Trace of ${fmtAmount(p.amount, p.asset)} ${p.asset}`}
      title={(
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <LevelPill p={p} />
          <span><span className="amt-key">{fmtAmount(p.amount, p.asset)}</span> <span className="text-tg-hint">{p.asset}</span></span>
        </div>
      )}
      sub={<div className="text-meta text-tg-hint">received {p.at ? fmtStamp(p.at) : '—'} · into {p.walletLabel ?? `the ${p.chain} desk wallet`}</div>}>

      {/* 1 · the verdict: why, and what to do */}
      {(reason || notFull || action) && (
        <div className={`rounded-lg px-3 py-2 space-y-1 ${hold ? 'bg-neg-bg' : p.kind === 'watch' ? 'bg-warn-bg' : 'bg-panel-2'}`}>
          {reason && <p>{capital(reason)}</p>}
          {notFull && <p className="text-tg-hint">{notFull}.</p>}
          {action && <p className={hold ? 'font-medium text-neg' : undefined}>{action}</p>}
        </div>
      )}

      {/* 2 · sanctions — the first question of every check */}
      <TraceBlock title="Sanctions">
        {/* Not judged yet: nothing was read, which is not "none found". */}
        <div>{p.level === 'pending' ? <span className="text-tg-hint">Not checked yet</span> : <SanctionsResult p={p} full />}</div>
      </TraceBlock>

      {/* 3 · the trace itself */}
      {/* Whose funds: the payment's own, from GL's report on its transaction — or, rated before per-transaction
          checks were switched on (7 Oct 2026), its sender's as the payment was rated. */}
      <TraceBlock title="Where the funds came from" note={p.trace ? (p.basis === 'tx' ? "this payment's own funds" : "the sender's funds, as rated") : undefined}>
        {p.trace ? (
          <>
            <SourceSplit t={p.trace} />
            <TracePart t={p.trace} whose={p.basis === 'tx' ? "this payment's" : "the sender's"} />
            <SourceList t={p.trace} />
          </>
        ) : <NoTrace p={p} />}
      </TraceBlock>

      {/* 4 · who paid */}
      <TraceBlock title="Who paid">
        <div><Address address={p.from} chain={p.chain} poisoning={p.poisoning?.of ?? null} /></div>
        {p.poisoning && <p className="text-neg">Address poisoning — look-alike of {p.poisoning.of}. Never a payee; never copy.</p>}
        {/* The customer is looked up when the payment is judged: before that, "not matched" would be a claim. */}
        {p.level !== 'pending' && (
          <div>
            {p.customer
              ? <><span className="who">{p.customer}</span><span className={adverse ? 'text-warn' : 'text-tg-hint'}> — {p.standing ? STANDING_WORDS[p.standing] : 'screened at onboarding'}</span></>
              : <span className="text-tg-hint">Not matched to an onboarded customer</span>}
          </div>
        )}
        {gl && <div>GL names this address <span className="who">{gl.name}</span><span className="text-tg-hint"> — {gl.label}</span></div>}
        {/* A wallet a customer's chat only named is a claim: it is not the one the desk approved them with. */}
        {p.customer && p.standing === 'verified' && p.own === false && (
          <div className="text-tg-hint">Named in their chat only — not on an approved application.</div>
        )}
        {/* Beside the one named above, never instead: anyone can name any wallet in a chat. */}
        {p.customer && p.also && (
          <div>
            <span className="text-tg-hint">Also given by </span>
            <span className={alsoAdverse(p.also) ? 'text-warn' : undefined}>{ALSO_WORDS[p.also.standing]}</span>: <span className="who">{p.also.name}</span>
            <span className="text-tg-hint" title="Anyone can name any wallet in a chat."> — changes no level</span>
          </div>
        )}
        {p.level !== 'pending' && (
          <div className="text-tg-hint">
            {p.score != null ? <>GL score <span className="amt text-tg-text">{Math.round(p.score)}</span> of 100 · {p.band}</> : 'No GL score'}
          </div>
        )}
        {p.level !== 'pending' && p.thin && <div className="text-warn">GL holds little data on this address.</div>}
        {p.nth != null && (
          <div className="text-tg-hint">
            {p.nth === 1
              ? 'First payment from this sender into this wallet this month'
              : <>{ordinal(p.nth)} from this sender into this wallet this month{p.senderUsd != null && p.senderUsd > 0 && <> · <span className="amt">{dollars(p.senderUsd)}</span> so far</>}</>}
          </div>
        )}
      </TraceBlock>

      {/* 5 · into which wallet */}
      <TraceBlock title="Paid into">
        <div>{p.walletLabel ?? 'Desk wallet'} <span className={CHAIN}>{p.chain}</span></div>
        {p.txid && (
          <div>
            <span className="text-tg-hint">Transaction </span>
            {ex
              ? <a href={ex.tx(p.txid)} target="_blank" rel="noreferrer" className="tap-44 font-mono text-meta text-info hover:underline" title={p.txid}>{p.txid.slice(0, 10)}… <ExternalLink size={11} className="inline" /></a>
              : <span className="font-mono text-meta" title={p.txid}>{p.txid.slice(0, 10)}…</span>}
          </div>
        )}
      </TraceBlock>

      {/* 6 · what the check covered — "not fully checked", in particulars */}
      {p.level !== 'pending' && (
        <TraceBlock title="What was checked">
          <KeyValue>
            <KeyValueRow label="Sanctions lists" value={listsRead ? (lists.join(', ') || 'Read') : <span className="text-warn">Not read</span>} />
            {p.from.startsWith('0x') && (
              <KeyValueRow label="Sanctions oracle" value={p.sanctions.oracleSilent || !listsRead ? <span className="text-warn">Did not answer</span> : 'Answered'} />
            )}
            <KeyValueRow label="GL's sources" value={p.small
              ? `Not asked — under ${dollars(p.small)}, the sanctions lists only`
              : !p.sourcesRead
              ? <span className="text-warn">{p.thin ? 'None to read — GL holds little data' : 'Not read'}</span>
              : p.partial ? <span className="text-warn">Read in part</span> : 'Read in full'} />
            {/* The worker's notes — a source GL named without a share, the day's GL limit. Without sources they
                are said under "Where the funds came from"; with no result they are the verdict itself. */}
            {p.level !== 'unscreened' && p.sourcesRead && p.notes.length > 0 && (
              <KeyValueRow label="Note" value={<span className="text-warn">{p.notes.join(' · ')}</span>} />
            )}
            <KeyValueRow label="KYT group" value={p.told ? 'Its report was delivered' : <span className="text-tg-hint">Its report has not been delivered yet</span>} />
          </KeyValue>
        </TraceBlock>
      )}
    </Panel>
  );
}

/** One wallet traced: where its own funds came from — what an exchange sees
 *  behind a deposit from it — how it is known, and, on a press, its transfers
 *  of the last 7 days: the one paid read on this screen, behind its own button. */
export function WalletTrace({ chain, address, desk, watched, books, trace, onClose }: {
  chain: string;
  address: string;
  desk?: KytIncomingWallet;
  watched?: KytWatchedWallet;
  books?: KytBooksWallet;
  trace: TraceInfo;
  onClose: () => void;
}) {
  // The paid read. Never fetched by itself — not on opening this trace, not on Refresh, not when the
  // window comes back into focus: only by its priced button. Opening the free Trace re-bought it from a
  // cache over ten minutes old (Astra, 6 Oct 2026). A read already made is shown, with when it was made.
  const transfers = useQuery({
    queryKey: ['kyt', 'transfers', address],
    queryFn: () => api.kytTransfers(address),
    enabled: false,
    staleTime: Infinity,
    gcTime: 10 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const read = transfers.data;
  const party = books ? partyName({ display_name: books.display_name, slug: books.slug }) : null;
  const name = desk?.label ?? watched?.label ?? party ?? `${chain} wallet`;
  const gl = desk?.gl ?? null;
  return (
    <Panel onClose={onClose} label={`Trace of ${name}`}
      title={<div className="flex flex-wrap items-baseline gap-x-2"><span className="font-semibold text-body">{name}</span><span className={CHAIN}>{chain}</span></div>}
      sub={(
        <div className="space-y-1.5">
          <div><Address address={address} chain={chain} /></div>
          {gl && (
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-meta">
              {/* A low score GL gave with little data on the wallet is not a green pill. */}
              <Pill tone={gl.thin && gl.band === 'low' ? 'neutral' : GL_BAND_TONE[gl.band]}>{`GL ${Math.round(gl.score)} · ${gl.band}`}</Pill>
              {gl.at && <span className="text-tg-hint" title={fmtStamp(gl.at)}>the wallet&apos;s own score · {fmtRelative(gl.at)}</span>}
            </div>
          )}
        </div>
      )}>

      <TraceBlock title="Where its funds came from" note="what an exchange sees behind a deposit from it">
        {gl ? (
          <>
            {gl.thin && <p className="text-warn">GL holds little data — its score is not a clean bill.</p>}
            {gl.trace ? (
              <>
                {gl.sourcesAt && <p className="text-tg-hint">Its sources as read {fmtStamp(gl.sourcesAt)} — {gl.newestUnreadable ? 'the newest list could not be read' : 'the newest score came without them'}.</p>}
                <SourceSplit t={gl.trace} />
                <TracePart t={gl.trace} whose="its" names={gl.unmeasured} />
                {/* The card names these from the same answer; the list of sources may not carry them — said here, never only there. */}
                {(() => {
                  const named = new Set(gl.trace.sources.map((x) => (x.owner ?? x.label).toLowerCase()));
                  const missing = gl.bad.filter((b) => !named.has((b.owner ?? b.label).toLowerCase()));
                  return missing.length > 0
                    ? <p className="text-warn">Sanctioned or criminal sources in its funds: {missing.map((b) => `${b.label} ${share(b.pct)}${b.owner ? ` (${b.owner})` : ''}`).join(' · ')}</p>
                    : null;
                })()}
                <SourceList t={gl.trace} />
              </>
            ) : gl.sourcesHeld ? (
              // A worker from before the trace sends the sources as one line and the bad ones by name:
              // they are held, and said — "not held" was false (Astra, 6 Oct 2026).
              <>
                {gl.sourcesAt && <p className="text-tg-hint">Its sources as read {fmtStamp(gl.sourcesAt)} — the newest score came without them.</p>}
                {gl.funds && <p>{gl.funds}</p>}
                {gl.bad.length > 0
                  ? <p className="text-warn">Sanctioned or criminal sources: {gl.bad.map((b) => `${b.label} ${share(b.pct)}${b.owner ? ` (${b.owner})` : ''}`).join(' · ')}</p>
                  : <p className="text-tg-hint">No sanctioned or criminal sources{gl.partial ? ' in the part of its sources read' : ''}.</p>}
                {(gl.unmeasured ?? []).length > 0 && <p className="text-meta text-warn">GL lists {namesWords(gl.unmeasured ?? [])} among its sources with no share — not measured.</p>}
              </>
            ) : <p className="text-tg-hint">GL&apos;s sources are not held for this wallet.</p>}
          </>
        ) : (
          <p className="text-tg-hint">
            {desk ? 'GL has no score for this wallet yet.' : "Not one of this month's desk wallets — no GL score."}
          </p>
        )}
      </TraceBlock>

      {(watched || books) && (
        <TraceBlock title="What is known of it">
          <KeyValue>
            {books && (
              <KeyValueRow label="In the Books" value={<>
                <span className="who">{party}</span> · {KIND_WORDS[books.party_type]}
                {books.status === 'archived' && <span className="text-tg-hint"> · retired{books.retired_at ? ` ${fmtDay(books.retired_at, true)}` : ''}, still paid into</span>}
              </>} />
            )}
            {books && (
              <KeyValueRow label="Watched" value={books.watched
                ? 'Yes — looked at each night'
                // 3 Nov 2026: the freeze watch holds it, the desk list does not (Hot · Base) — frozen coins watched, nothing else.
                : books.freeze_watched ? <span className="text-warn" title={`Frozen coins watched${books.swept_at ? `, last ${fmtRelative(books.swept_at)}` : ''}; payments on ${networkWord(books.chain)} are not read here and GL gives no ${networkWord(books.chain)} score`}>Freeze watch only — payments not read on {networkWord(books.chain)}</span>
                : books.party_type === 'own_account' ? <span className="text-warn">Not watched — nobody screens it</span> : 'No'} />
            )}
            {watched && (
              <KeyValueRow label="Arkham" value={<>{watched.entity ?? 'Not named'}<span className="text-tg-hint"> · {watched.lastChecked ? `looked at ${fmtRelative(watched.lastChecked)}` : 'never looked at'}</span></>} />
            )}
            {books && (books.band || books.risk_score != null) && (
              <KeyValueRow label="Risk on record" value={<><BandPill band={books.band} looked={looked(books)} />{books.risk_score != null && <span className="ml-1.5 amt">{books.risk_score}</span>}</>} />
            )}
          </KeyValue>
        </TraceBlock>
      )}

      <TraceBlock title="Transfers in and out" note="the last 7 days, with who the other side is">
        {(read || transfers.isFetching || transfers.isError) && <TransfersRead q={transfers} />}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <button type="button" className="btn-secondary text-meta py-1 px-2 whitespace-nowrap disabled:opacity-50" disabled={trace.off || transfers.isFetching} title={trace.title}
            onClick={() => void transfers.refetch()}>
            {read?.status === 'ok' ? trace.again : trace.label}
          </button>
          {(trace.off || !read) && <span className="text-meta text-tg-hint">{trace.off ? trace.title : 'Paid in Arkham credits — nothing is read until you press.'}</span>}
        </div>
      </TraceBlock>
    </Panel>
  );
}

/** Opens a trace beside the list. It costs nothing: the one paid read is inside a wallet's trace, behind its own button.
 *  `quiet`: on a list of thirty rows a bordered button on every one is a column of boxes the eye must skip — the
 *  house allows "a secondary button or a link" for an action, so a row's Trace is a link until it is pointed at. */
function TraceOpen({ onClick, what, quiet = false }: { onClick: () => void; what: string; quiet?: boolean }) {
  return (
    <button type="button" onClick={onClick} aria-label={`Trace ${what}`}
      className={quiet
        ? 'rounded-lg border border-transparent px-2 py-1 text-meta whitespace-nowrap text-info hover:border-stroke hover:bg-panel-2 focus-visible:border-stroke'
        : 'btn-secondary text-meta py-1 px-2 whitespace-nowrap'}>
      Trace
    </button>
  );
}

/** What the paid read returned: a wallet's transfers of the last 7 days, from Arkham — the query is
 *  WalletTrace's, run only by its button. Always the card form: it stands in the trace panel, which is
 *  a phone's width. */
function TransfersRead({ q }: { q: UseQueryResult<KytTransfers> }) {
  const rows: KytTransferRow[] = q.data?.rows ?? [];
  const inflow = rows.filter((r) => r.dir === 'in').reduce((s, r) => s + r.usd, 0);
  const outflow = rows.filter((r) => r.dir === 'out').reduce((s, r) => s + r.usd, 0);
  return (
    <>
      {q.isFetching && <Pending inline what="Reading the last 7 days…" />}
      {/* A failed read, in one line and the cause the BFF gave — not "the books did not answer, the desk Mac may be asleep". */}
      {q.isError && (() => {
        const failed = readFailure(q.error);
        return <StateLine tone="bad" more={failed.more}>Read failed — {failed.why}.</StateLine>;
      })()}
      {q.data && q.data.status !== 'ok' && (
        <StateLine tone="warn">
          {q.data.status === 'not_enabled' ? 'Arkham is not enabled.' : q.data.status === 'budget_exhausted' ? 'Arkham budget spent — try after 04:00 Dubai.' : 'Arkham did not answer — try again.'}
        </StateLine>
      )}
      {q.data && q.data.status === 'ok' && (
        <div className="space-y-1.5">
          {/* An empty read is one line, not three (the count, the two dollar figures and "Nothing in the last 7 days"). */}
          {rows.length > 0 && (
            <div className="flex flex-wrap gap-x-3 text-meta text-tg-hint">
              <span>7 days · {rows.length} transfer{rows.length === 1 ? '' : 's'}</span>
              <span className="text-pos">in ≈ {fmtAmount(inflow, 'USD')} USD</span>
              <span className="text-neg">out ≈ {fmtAmount(outflow, 'USD')} USD</span>
              {q.data.fetched_at && <span>{q.data.cached ? 'cached' : 'live'} · read {fmtRelative(q.data.fetched_at)}</span>}
            </div>
          )}
          {rows.length === 0 ? (
            <p className="text-body text-tg-hint">Nothing in the last 7 days{q.data.fetched_at ? ` · read ${fmtRelative(q.data.fetched_at)}` : ''}.</p>
          ) : (
            // In / Out · the amount, the time at the right; then ≈ USD · the
            // chain · the counterparty · its address · the tx. The chain
            // stands here — an EVM address is the same on ETH, BSC and Base.
            <div>
              {rows.map((r) => {
                const ex = explorer(r.chain);
                const inbound = r.dir === 'in';
                return (
                  <div key={`${r.hash}:${r.dir}:${r.counterparty.address}`} className={`${LEDGER.card} last:border-b-0`}>
                    <div className={LEDGER.cardLine1}>
                      <span className={`font-medium shrink-0 ${inbound ? 'text-pos' : 'text-neg'}`}>{inbound ? 'In' : 'Out'}</span>
                      <span className="amt whitespace-nowrap">{inbound ? '+' : '−'}{fmtAmount(r.amount, r.token)} {r.token}</span>
                      <span className={`${LEDGER.whenText} ml-auto shrink-0`} title={r.ts ? fmtStamp(r.ts) : undefined}>{r.ts ? fmtRelative(r.ts) : '—'}</span>
                    </div>
                    <div className={CARD_LINE2}>
                      <span className="amt text-tg-hint whitespace-nowrap">≈ {fmtAmount(r.usd, 'USD')} USD</span>
                      <span className={CHAIN}>{r.chain}</span>
                      <span className="min-w-0 break-words">
                        {r.counterparty.entity || r.counterparty.label
                          ? <span className="font-medium">{r.counterparty.entity ?? r.counterparty.label}</span>
                          : <span className="text-tg-hint">unattributed</span>}
                      </span>
                      {r.counterparty.address && <Address address={r.counterparty.address} chain={r.chain} />}
                      <span className={`${CHAIN} ml-auto`}>
                        {r.hash
                          ? ex
                            ? <a href={ex.tx(r.hash)} target="_blank" rel="noreferrer" className="text-info hover:underline" title={r.hash}>{r.hash.slice(0, 10)}…</a>
                            : <span title={r.hash}>{r.hash.slice(0, 10)}…</span>
                          : '—'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </>
  );
}
